import React, { useState, useEffect, createContext, useContext, useRef, useCallback, useMemo, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Link, useParams, useSearchParams } from "react-router-dom";
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { format, addDays, differenceInDays } from "date-fns";
import { Toaster, toast } from "sonner";
import { useTranslation } from 'react-i18next';import './i18n';
import { languages } from './i18n';
import { HelmetProvider } from 'react-helmet-async';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

// Helper function to parse date strings correctly without timezone issues
const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  if (dateStr instanceof Date) return dateStr;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed in JS Date
};

// Drag and Drop
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Stripe
import { loadStripe } from "@stripe/stripe-js";
import EmbeddedCheckout from "@/components/EmbeddedCheckout";

// Extracted Pages (Refactored from monolith)
// ─── ROUTE-LEVEL CODE-SPLITTING (Optie D, Fase 1) ──────────────────────────
// Each page becomes its own JS chunk, only downloaded when the user navigates
// to that route. Cuts the initial bundle by ~10-15% for visitors who only
// hit the homepage.
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const WhoWeArePage = lazy(() => import("@/pages/WhoWeArePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ReferFriendPage = lazy(() => import("@/pages/ReferFriendPage"));
const SurveyPage = lazy(() => import("@/pages/SurveyPage"));
const HowItWorksPage = lazy(() => import("@/pages/HowItWorksPage"));
const FreestaysMovementPage = lazy(() => import("@/pages/FreestaysMovementPage"));
// Pages with named exports must be wrapped to expose `default` for React.lazy.
const NewsListPage = lazy(() => import("@/pages/NewsPage").then(m => ({ default: m.NewsListPage })));
const NewsArticlePage = lazy(() => import("@/pages/NewsPage").then(m => ({ default: m.NewsArticlePage })));
const BlogListPage = lazy(() => import("@/pages/BlogPage").then(m => ({ default: m.BlogListPage })));
const BlogArticlePage = lazy(() => import("@/pages/BlogPage").then(m => ({ default: m.BlogArticlePage })));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));
const LastMinutePage = lazy(() => import("@/pages/LastMinutePage"));
const ToursPage = lazy(() => import("@/pages/ToursPage"));
const CarRentalPage = lazy(() => import("@/pages/CarRentalPage"));
const FlightsPage = lazy(() => import("@/pages/FlightsPage"));
const FlightBookingPage = lazy(() => import("@/pages/FlightBookingPage"));
const PassPage = lazy(() => import("@/pages/PassPage"));
const VoucherPage = lazy(() => import("@/pages/VoucherPage"));
const ComparisonPage = lazy(() => import("@/pages/ComparisonPage"));
const DestinationPage = lazy(() => import("@/pages/DestinationPage"));
const ProgrammaticLandingPage = lazy(() => import("@/pages/ProgrammaticLandingPage"));
const ProgrammaticGuideIndex = lazy(() =>
  import("@/pages/ProgrammaticLandingPage").then(m => ({ default: m.ProgrammaticGuideIndex }))
);
const UnsubscribePage = lazy(() => import("@/pages/UnsubscribePage"));
const NewsletterConfirmPage = lazy(() => import("@/pages/NewsletterConfirmPage"));
const SearchMapPage = lazy(() => import("@/pages/SearchMapPage"));
import { PopularDestinationsGrid, FooterDestinationLinks, PopularDestinationsCarousel } from "@/components/PopularDestinations";
import { FeaturedHotelsCarousel } from "@/components/FeaturedHotelsCarousel";
import CookieConsent from "@/components/CookieConsent";
// Lazy-loaded so the popup's JS chunk is excluded from the initial bundle.
// This protects LCP: hero image is painted before popup JS even arrives.
const WelcomePopup = lazy(() => import("@/components/WelcomePopup"));
import ShareCard from "@/components/ShareCard";
import HotelMap from "@/components/HotelMap";

// New UI Components (Freestays Blue Theme)
import { CompactSearchBar } from "@/components/CompactSearchBar";
import { DateRangeSheet } from "@/components/DateRangeSheet";
import { CountrySelector, getCustomerCountry } from "@/components/CountrySelector";
import { getTCKimlikStatus, getNationalityGateConfig, TCKimlikDialog as TCKimlikDialogComp } from "@/components/TCKimlikDialog";
import { FadeInSection } from "@/components/FadeInSection";
import { ManifestoBanner } from "@/components/ManifestoBanner";
import { PromoBanner } from "@/components/PromoBanner";
import { DynamicPageBanner } from "@/components/DynamicPageBanner";
import AnimatedBackground from "@/components/AnimatedBackground";
import VideoBackground, { GradientBackground } from "@/components/VideoBackground";
import { loadGoogleMaps } from "@/utils/loadGoogleMaps";
// HotelCard extracted to @/components/HotelCard.jsx - available for future use
// import { HotelCard as ExtractedHotelCard, calculatePricing as extractedCalculatePricing } from "@/components/HotelCard";

// ─── Admin Panel (Optie D, Fase 2) ──────────────────────────────────────
// 18.717-line file used by <1% of visitors. Lazy-loaded → shaves ~30-35%
// off the initial bundle for the public site.
const AdminDashboardPage = lazy(() =>
  import("@/admin").then(m => ({ default: m.AdminDashboardPage }))
);

// ─── CMS Components (Optie D, Fase 2) ──────────────────────────────────
// CMSProvider + useCMS are eager (used by routing logic in <CMSApp>).
// All page components are lazy — they only run for staff users.
import { CMSProvider, useCMS } from "@/cms";
const CMSLogin = lazy(() => import("@/cms").then(m => ({ default: m.CMSLogin })));
const CMSLayout = lazy(() => import("@/cms").then(m => ({ default: m.CMSLayout })));
const CMSDashboard = lazy(() => import("@/cms").then(m => ({ default: m.CMSDashboard })));
const CMSUsers = lazy(() => import("@/cms").then(m => ({ default: m.CMSUsers })));
const CMSUserDetail = lazy(() => import("@/cms").then(m => ({ default: m.CMSUserDetail })));
const CMSPasses = lazy(() => import("@/cms").then(m => ({ default: m.CMSPasses })));
const CMSPayments = lazy(() => import("@/cms").then(m => ({ default: m.CMSPayments })));
const CMSReports = lazy(() => import("@/cms").then(m => ({ default: m.CMSReports })));
const CMSAuditLogs = lazy(() => import("@/cms").then(m => ({ default: m.CMSAuditLogs })));
const CMSAdmins = lazy(() => import("@/cms").then(m => ({ default: m.CMSAdmins })));
const CMSFraudRules = lazy(() => import("@/cms").then(m => ({ default: m.CMSFraudRules })));
const CMSAlerts = lazy(() => import("@/cms").then(m => ({ default: m.CMSAlerts })));
const CMSAnalytics = lazy(() => import("@/cms").then(m => ({ default: m.CMSAnalytics })));
const CMSSecurity = lazy(() => import("@/cms").then(m => ({ default: m.CMSSecurity })));
const CMSTCKimlikAudit = lazy(() => import("@/cms").then(m => ({ default: m.CMSTCKimlikAudit })));
const CMSSystemHealth = lazy(() => import("@/cms").then(m => ({ default: m.CMSSystemHealth })));
const CMSB2B = lazy(() => import("@/cms").then(m => ({ default: m.CMSB2B })));
const CMSCommissions = lazy(() => import("@/cms").then(m => ({ default: m.CMSCommissions })));
const CMSPopupSettings = lazy(() => import("@/cms").then(m => ({ default: m.CMSPopupSettings })));
const MarketingDashboard = lazy(() => import("@/cms").then(m => ({ default: m.MarketingDashboard })));

// UI Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger, PopoverClose } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Icons
import { 
  Search, MapPin, Calendar as CalendarIcon, Users, Star, Wifi, Car, Utensils, 
  Dumbbell, Waves, Coffee, Heart, ChevronDown, Menu, X, Loader2, Check, 
  CreditCard, Shield, Tag, Copy, LogOut, User, Settings, Ticket, Building2,
  ArrowRight, ArrowLeft, Sparkles, Phone, Mail, Globe, ChevronLeft, ChevronRight,
  Bed, Bath, Mountain, TreePine, UtensilsCrossed, PlaneTakeoff, Clock, Zap, Crown,
  Lock, Trash2, Plus, Edit, BarChart3, DollarSign, Gift, Percent, Info, AlertCircle,
  CheckCircle, XCircle, Send, Euro, Briefcase, LogIn, Share2, MessageCircle, Twitter,
  Languages, Wallet, Plane, ClipboardCheck, CalendarX, CalendarCheck, RefreshCw, MoreVertical, Armchair,
  Eye, FileText, Download, Printer, UserPlus, Moon, Sun, Database, Camera, Image as ImageIcon, Play,
  ArrowUpDown, Bell, Smartphone, Activity, History, TrendingUp, TrendingDown, Trophy, Save, ExternalLink,
  MessageSquare, ThumbsUp, Home, CircleSlash, GripVertical, Facebook, Instagram, Linkedin, Youtube,
  AlertTriangle, Upload, RotateCcw, ArrowUpCircle, KeyRound, Baby, ChevronsLeft, ChevronsRight, LayoutDashboard, BookOpen, UserCircle,
  Hotel, Expand, Award, ChevronUp, Navigation, List, Map, ShieldAlert, Megaphone, Music, Leaf, Flame
} from "lucide-react";

// ==================== ERROR BOUNDARY ====================
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    // Detect chunk-load failures (stale service-worker cache after a new deploy).
    // ChromeError message: "Loading chunk N failed."
    // Webpack error name: "ChunkLoadError"
    const msg = String(error?.message || error || "");
    const isChunkError =
      error?.name === "ChunkLoadError" ||
      /Loading chunk [\d]+ failed/i.test(msg) ||
      /Loading CSS chunk [\d]+ failed/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg);
    return { hasError: true, isChunkError };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Page render error:", error, errorInfo);
    // Auto-recover from chunk errors: kill SW caches, then hard reload.
    if (this.state.isChunkError) {
      try {
        if ("caches" in window) {
          caches.keys().then(names => names.forEach(n => caches.delete(n)));
        }
        if (navigator.serviceWorker) {
          navigator.serviceWorker.getRegistrations().then(regs => regs.forEach(r => r.unregister()));
        }
      } catch (e) { /* noop */ }
      // Slight delay so the cache-busting can finish, then hard reload once.
      // Guard with sessionStorage to prevent infinite reload loops.
      if (!sessionStorage.getItem("__chunkReloaded")) {
        sessionStorage.setItem("__chunkReloaded", "1");
        setTimeout(() => window.location.reload(), 200);
      }
    }
  }

  render() {
    if (this.state.hasError) {
      const isChunk = this.state.isChunkError;
      return (
        <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-8">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold mb-4">
              {isChunk ? "Updating to the latest version…" : "Something went wrong"}
            </h2>
            <p className="text-muted-foreground mb-6">
              {isChunk ? "We're loading the newest build for you." : "Please try refreshing the page."}
            </p>
            <button
              onClick={() => { sessionStorage.removeItem("__chunkReloaded"); this.setState({ hasError: false }); window.location.reload(); }}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors"
              data-testid="error-boundary-refresh"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Lightweight fallback shown while a route chunk is being downloaded.
// Avoids a blank page during the brief lazy-load window (typically <300ms on 4G).
const RouteSuspenseFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-background text-foreground" role="status" aria-live="polite">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      <p className="text-sm text-muted-foreground">Loading…</p>
    </div>
  </div>
);

// ==================== BREADCRUMBS COMPONENT ====================
// Extracted to /app/frontend/src/components/Breadcrumbs.jsx (Fase 3a)
import { Breadcrumbs } from './components/Breadcrumbs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Image proxy helper ─────────────────────────────────────────────────
// Routes external hotel images (sunhotels, cupid, hotelbeds, etc.) through
// our /api/proxy/image endpoint which resizes + converts to WebP + caches.
// Pass-through for non-allowlist URLs and our own /api/* paths.
const proxyImage = (url, width = 800, quality = 80) => {
  if (!url || typeof url !== 'string') return url;
  // Already a proxied / first-party URL — leave alone
  if (url.includes('/api/proxy/image') || url.startsWith('/') || url.includes('/api/media/')) return url;
  const allowlist = [
    'hotelimages.sunhotels.net',
    'static.cupid.travel',
    'media.cupid.travel',
    'exp.cdn-hotels.com',
    'images.trvl-media.com',
    'photos.hotelbeds.com'
  ];
  try {
    const u = new URL(url);
    if (!allowlist.some(h => u.hostname.endsWith(h))) return url;
    return `${BACKEND_URL}/api/proxy/image?url=${encodeURIComponent(url)}&w=${width}&q=${quality}`;
  } catch { return url; }
};
const API = `${BACKEND_URL}/api`;

const queryClient = new QueryClient();

// ==================== THEME CONTEXT ====================
const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('dark'); // Default to dark mode
  const [darkModeEnabled, setDarkModeEnabled] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Fetch theme settings from backend
  useEffect(() => {
    const initializeTheme = async () => {
      try {
        const response = await axios.get(`${API}/settings/ui`);
        const enabled = response.data?.darkMode_enabled !== false;
        const defaultTheme = response.data?.defaultTheme || 'dark'; // Default to dark
        
        setDarkModeEnabled(enabled);
        
        // Check localStorage first for user preference
        const storedTheme = localStorage.getItem('theme');
        if (storedTheme) {
          // User has a saved preference
          if (!enabled && storedTheme === 'dark') {
            // Dark mode disabled by admin, force light
            setTheme('light');
          } else {
            setTheme(storedTheme);
          }
        } else {
          // No user preference - use system preference or admin default
          if (window.matchMedia('(prefers-color-scheme: light)').matches) {
            setTheme('light');
          } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
            setTheme('dark');
          } else {
            // No system preference - use admin default
            setTheme(defaultTheme);
          }
        }
        
        setSettingsLoaded(true);
      } catch (error) {
        // Default to light if fetch fails
        const storedTheme = localStorage.getItem('theme');
        setTheme(storedTheme || 'light');
        setDarkModeEnabled(true);
        setSettingsLoaded(true);
      }
    };
    initializeTheme();
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    if (darkModeEnabled) {
      setTheme(prev => prev === 'light' ? 'dark' : 'light');
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, darkModeEnabled, settingsLoaded }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

// Theme Toggle Button Component
const ThemeToggle = ({ variant = "ghost", size = "sm", showLabel = false }) => {
  const { theme, toggleTheme, darkModeEnabled } = useTheme();
  const { t } = useTranslation();
  
  // Don't render if dark mode is disabled by admin
  if (!darkModeEnabled) {
    return null;
  }
  
  return (
    <Button
      variant={variant}
      size={size}
      onClick={toggleTheme}
      className="gap-2"
      data-testid="theme-toggle"
      title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
    >
      {theme === 'light' ? (
        <Moon className="w-4 h-4" />
      ) : (
        <Sun className="w-4 h-4" />
      )}
      {showLabel && (
        <span className="hidden sm:inline text-sm">
          {theme === 'light' ? t('darkMode', 'Dark') : t('lightMode', 'Light')}
        </span>
      )}
    </Button>
  );
};

// ==================== PWA INSTALL PROMPT ====================
// PWA Install Context for sharing install functionality
const PWAInstallContext = createContext();

export const usePWAInstall = () => useContext(PWAInstallContext);

const PWAInstallProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [installId, setInstallId] = useState(() => localStorage.getItem('pwa-install-id'));

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    try {
      // Check if service worker and push are supported
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('Push notifications not supported');
        return false;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return false;
      }

      // Get the service worker registration
      const registration = await navigator.serviceWorker.ready;
      
      // Get the VAPID public key from backend
      const vapidResponse = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidResponse.data.publicKey;
      
      if (!vapidPublicKey) {
        console.log('VAPID public key not configured');
        return false;
      }

      // Convert VAPID key to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON(),
        install_id: localStorage.getItem('pwa-install-id')
      }, { headers });

      console.log('Push notification subscription successful');
      return true;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      return false;
    }
  };

  // Track PWA install to backend
  const trackInstall = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      // Detect platform and browser
      const ua = navigator.userAgent;
      let platform = 'unknown';
      let browser = 'unknown';
      
      if (/Android/i.test(ua)) platform = 'Android';
      else if (/iPhone|iPad|iPod/i.test(ua)) platform = 'iOS';
      else if (/Windows/i.test(ua)) platform = 'Windows';
      else if (/Mac/i.test(ua)) platform = 'macOS';
      else if (/Linux/i.test(ua)) platform = 'Linux';
      
      if (/Chrome/i.test(ua) && !/Edge|Edg/i.test(ua)) browser = 'Chrome';
      else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
      else if (/Firefox/i.test(ua)) browser = 'Firefox';
      else if (/Edge|Edg/i.test(ua)) browser = 'Edge';
      
      const response = await axios.post(`${API}/pwa/track-install`, {
        platform,
        browser,
        device_info: {
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          standalone: window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone
        },
        app_version: '2.0.0'
      }, { headers });
      
      if (response.data.install_id) {
        localStorage.setItem('pwa-install-id', response.data.install_id);
        setInstallId(response.data.install_id);
        
        // Try to subscribe to push notifications after install tracking
        // Small delay to ensure service worker is ready
        setTimeout(() => {
          subscribeToPush();
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to track PWA install:', error);
    }
  };

  // Track activity heartbeat
  const trackActivity = async () => {
    const storedInstallId = localStorage.getItem('pwa-install-id');
    if (!storedInstallId) return;
    
    try {
      await axios.post(`${API}/pwa/track-activity`, { install_id: storedInstallId });
    } catch (error) {
      // Silent fail for activity tracking
    }
  };

  useEffect(() => {
    // Detect iOS
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(iOS);
    
    // Check if already installed (standalone mode) - works for both iOS and Android/Desktop
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    
    if (isStandalone) {
      setIsInstalled(true);
      // Track activity for installed apps
      trackActivity();
      
      // Try to subscribe to push notifications if not already subscribed
      // Check if already subscribed to avoid duplicate subscriptions
      const pushSubscribed = localStorage.getItem('push-subscribed');
      if (!pushSubscribed) {
        setTimeout(() => {
          subscribeToPush().then(success => {
            if (success) {
              localStorage.setItem('push-subscribed', 'true');
            }
          });
        }, 3000);
      }
      
      // Periodic activity tracking every 10 minutes
      const activityInterval = setInterval(trackActivity, 10 * 60 * 1000);
      return () => clearInterval(activityInterval);
    }

    // Check if banner was dismissed recently
    const checkBannerDismissal = () => {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (dismissed) {
        const dismissedTime = parseInt(dismissed);
        // Show again after 3 days
        if (Date.now() - dismissedTime < 3 * 24 * 60 * 60 * 1000) {
          return true; // Still dismissed
        }
      }
      return false; // Can show banner
    };

    // Check if this is a first-time visitor (never seen banner before)
    const isFirstVisit = !localStorage.getItem('pwa-banner-shown');
    
    // Always enable install capability for ALL platforms
    setCanInstall(true);
    
    // Show banner for new visitors regardless of platform (iOS or not)
    if (!checkBannerDismissal()) {
      // For first-time visitors, show immediately after 3 seconds
      // For returning visitors, show after 5 seconds
      const delay = isFirstVisit ? 3000 : 5000;
      setTimeout(() => {
        setShowInstallBanner(true);
        localStorage.setItem('pwa-banner-shown', 'true');
      }, delay);
    }

    // For Android/Desktop: Listen for beforeinstallprompt to enable native install
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Native install prompt is now available
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowInstallBanner(false);
      setDeferredPrompt(null);
      setCanInstall(false);
      // Track the install to backend
      trackInstall();
    });

    // Listen for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log('App updated to version:', event.data.version);
        }
      });

      // Check for updates periodically (every 30 minutes)
      const checkForUpdates = () => {
        navigator.serviceWorker.ready.then(registration => {
          registration.update();
        });
      };
      
      // Initial check after 5 seconds
      setTimeout(checkForUpdates, 5000);
      
      // Periodic check
      const updateInterval = setInterval(checkForUpdates, 30 * 60 * 1000);
      
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
        clearInterval(updateInterval);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    // For iOS, we can't programmatically install - just track the intent
    if (isIOS) {
      trackInstall();
      return true; // Return true to keep showing instructions
    }
    
    if (!deferredPrompt) return false;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstallBanner(false);
      setCanInstall(false);
      // Track install immediately
      trackInstall();
    }
    setDeferredPrompt(null);
    return outcome === 'accepted';
  };

  const dismissBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Force show banner for testing/admin purposes
  const forceShowBanner = () => {
    setShowInstallBanner(true);
  };

  return (
    <PWAInstallContext.Provider value={{ 
      canInstall, 
      isInstalled, 
      showInstallBanner,
      handleInstall, 
      dismissBanner,
      forceShowBanner,
      installId,
      isIOS,
      deferredPrompt
    }}>
      {children}
    </PWAInstallContext.Provider>
  );
};

const InstallAppPrompt = () => {
  const { showInstallBanner, handleInstall, dismissBanner, isInstalled, isIOS, deferredPrompt } = usePWAInstall();
  const { t } = useTranslation();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showDesktopInstructions, setShowDesktopInstructions] = useState(false);
  const [cookieDecided, setCookieDecided] = useState(() => !!localStorage.getItem('freestays_cookie_consent'));

  // Defer the PWA banner until the user has dealt with the cookie banner —
  // otherwise both stack at the bottom-right and clip each other off-screen.
  useEffect(() => {
    if (cookieDecided) return;
    const interval = setInterval(() => {
      if (localStorage.getItem('freestays_cookie_consent')) {
        setCookieDecided(true);
      }
    }, 600);
    return () => clearInterval(interval);
  }, [cookieDecided]);

  if (isInstalled || !showInstallBanner || !cookieDecided) return null;

  // Detect browser for specific instructions
  const ua = navigator.userAgent;
  const isChrome = /Chrome/i.test(ua) && !/Edge|Edg/i.test(ua);
  const isEdge = /Edge|Edg/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/Chrome/i.test(ua);
  const isFirefox = /Firefox/i.test(ua);
  const isSamsung = /SamsungBrowser/i.test(ua);

  // iOS-specific install instructions
  if (isIOS) {
    return (
      <div
        className="fixed left-4 right-4 md:left-auto md:right-4 md:w-96 z-[58] animate-slideInRight max-w-[calc(100vw-2rem)]"
        style={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        data-testid="pwa-banner-ios"
      >
        <Card className="shadow-2xl border-[#00A9E0]/20 bg-white dark:bg-[#081D33]">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <img 
                src="/assets/logo.png" 
                alt="Freestays" 
                className="w-14 h-14 rounded-xl object-contain flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base mb-1">{t('pwa.installTitle', 'Install Freestays App')}</h3>
                {!showIOSInstructions ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-3">
                      {t('pwa.installDescIOS', 'Add Freestays to your home screen for quick access!')}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => {
                          setShowIOSInstructions(true);
                          handleInstall(); // Track the click
                        }} 
                        className="rounded-full gap-2"
                        data-testid="pwa-show-instructions-btn"
                      >
                        <Download className="w-4 h-4" />
                        {t('pwa.showHowTo', 'Show How')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={dismissBanner} className="rounded-full">
                        {t('pwa.notNow', 'Not Now')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-[#00A9E0]">{t('pwa.iosInstructions', 'To install:')}</p>
                    <ol className="text-sm text-muted-foreground space-y-2">
                      <li className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#00A9E0] text-white text-xs font-bold">1</span>
                        <span>{t('pwa.iosStep1', 'Tap the Share button')} <Share2 className="w-4 h-4 inline text-[#00A9E0]" /></span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#00A9E0] text-white text-xs font-bold">2</span>
                        <span>{t('pwa.iosStep2', 'Scroll and tap "Add to Home Screen"')}</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#00A9E0] text-white text-xs font-bold">3</span>
                        <span>{t('pwa.iosStep3', 'Tap "Add" to install')}</span>
                      </li>
                    </ol>
                    <Button size="sm" variant="outline" onClick={dismissBanner} className="rounded-full w-full mt-2">
                      {t('pwa.gotIt', 'Got it!')}
                    </Button>
                  </div>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 flex-shrink-0" 
                onClick={dismissBanner}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Android/Desktop install prompt
  return (
    <div
      className="fixed left-4 right-4 md:left-auto md:right-4 md:w-96 z-[58] animate-slideInRight max-w-[calc(100vw-2rem)]"
      style={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      data-testid="pwa-banner"
    >
      <Card className="shadow-2xl border-[#00A9E0]/20 bg-white dark:bg-[#081D33]">
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <img 
              src="/assets/logo.png" 
              alt="Freestays" 
              className="w-14 h-14 rounded-xl object-contain flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base mb-1">{t('pwa.installTitle', 'Install Freestays App')}</h3>
              {!showDesktopInstructions ? (
                <>
                  <p className="text-sm text-muted-foreground mb-3">
                    {t('pwa.installDesc', 'Get quick access, offline support & push notifications for deals!')}
                  </p>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={async () => {
                        const installed = await handleInstall();
                        // If no native prompt available, show instructions
                        if (!installed && !deferredPrompt) {
                          setShowDesktopInstructions(true);
                        }
                      }} 
                      className="rounded-full gap-2"
                      data-testid="pwa-install-btn"
                    >
                      <Download className="w-4 h-4" />
                      {t('pwa.install', 'Install')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={dismissBanner} className="rounded-full">
                      {t('pwa.notNow', 'Not Now')}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-primary">
                    {isChrome || isSamsung ? 'To install in Chrome:' : 
                     isEdge ? 'To install in Edge:' :
                     isSafari ? 'To install in Safari:' :
                     isFirefox ? 'Firefox doesn\'t support PWA. Use Chrome/Edge.' :
                     'To install:'}
                  </p>
                  {(isChrome || isEdge || isSamsung) && (
                    <ol className="text-sm text-muted-foreground space-y-2">
                      <li className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#00A9E0] text-white text-xs font-bold">1</span>
                        <span>Click the install icon in the address bar</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#00A9E0] text-white text-xs font-bold">2</span>
                        <span>Or click ⋮ menu → "Install Freestays"</span>
                      </li>
                    </ol>
                  )}
                  {isSafari && (
                    <ol className="text-sm text-muted-foreground space-y-2">
                      <li className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#00A9E0] text-white text-xs font-bold">1</span>
                        <span>Click File → Add to Dock</span>
                      </li>
                    </ol>
                  )}
                  <Button size="sm" variant="outline" onClick={dismissBanner} className="rounded-full w-full mt-2">
                    {t('pwa.gotIt', 'Got it!')}
                  </Button>
                </div>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 flex-shrink-0" 
              onClick={dismissBanner}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== LANGUAGE SELECTOR COMPONENT ====================
const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 h-9 px-3">
          <span className="text-lg">{currentLang.flag}</span>
          <span className="hidden sm:inline text-sm">{currentLang.code.toUpperCase()}</span>
          <ChevronDown className="w-3 h-3 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 max-h-80 overflow-y-auto">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => i18n.changeLanguage(lang.code)}
            className={`flex items-center gap-3 cursor-pointer ${i18n.language === lang.code ? 'bg-primary/10 text-primary' : ''}`}
          >
            <span className="text-lg">{lang.flag}</span>
            <span className="flex-1">{lang.name}</span>
            {i18n.language === lang.code && <Check className="w-4 h-4 text-[#00A9E0]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ==================== PRICING CONSTANTS ====================
const MARKUP_RATE = 0.16;
const VAT_RATE = 0.21;
const FREESTAYS_DISCOUNT = 0.15;
const BOOKING_FEE = 0.00; // Eerste jaar gratis - na 1 jaar terugzetten naar 15.00
const PASS_ONE_TIME_PRICE = 35.00;
const PASS_ANNUAL_PRICE = 129.00;

// ==================== HELPER FUNCTIONS ====================
const isPackageBoard = (boardType) => {
  if (!boardType) return false;
  const lower = boardType.toLowerCase();
  return ['half board', 'full board', 'all inclusive', 'halfboard', 'fullboard', 'allinclusive'].some(pkg => lower.includes(pkg));
};

const calculatePricing = (nettPrice, hasValidPass, passPurchaseType = null, referralDiscount = 0, firstBookingFeeWaived = false) => {
  const markup = nettPrice * MARKUP_RATE;
  const vatOnMarkup = markup * VAT_RATE;
  const priceBeforeDiscount = nettPrice + markup + vatOnMarkup;
  
  const applyDiscount = hasValidPass || passPurchaseType;
  let discountAmount = 0;
  let roomTotal = priceBeforeDiscount;
  
  if (applyDiscount) {
    discountAmount = priceBeforeDiscount * FREESTAYS_DISCOUNT;
    roomTotal = priceBeforeDiscount - discountAmount;
  }
  
  // Booking fee is waived if: buying a pass OR has referral discount OR first booking with existing pass
  const hasReferralDiscount = referralDiscount > 0;
  const isFirstBookingWithPass = firstBookingFeeWaived && hasValidPass;
  const bookingFee = (passPurchaseType || hasReferralDiscount || isFirstBookingWithPass) ? 0 : BOOKING_FEE;
  
  let passPrice = 0;
  if (passPurchaseType === 'one_time') passPrice = PASS_ONE_TIME_PRICE;
  if (passPurchaseType === 'annual') passPrice = PASS_ANNUAL_PRICE;
  
  const finalTotal = roomTotal + bookingFee + passPrice;
  
  return {
    nettPrice,
    markup,
    vatOnMarkup,
    priceBeforeDiscount,
    discountApplied: applyDiscount,
    discountAmount,
    roomTotal,
    bookingFee,
    bookingFeeWaivedReason: isFirstBookingWithPass ? 'first_booking' : (passPurchaseType ? 'pass_purchase' : (hasReferralDiscount ? 'referral' : null)),
    passPrice,
    finalTotal,
    potentialSavings: applyDiscount ? 0 : priceBeforeDiscount * FREESTAYS_DISCOUNT,
    referralDiscountApplied: hasReferralDiscount,
    referralDiscountAmount: hasReferralDiscount ? BOOKING_FEE : 0
  };
};

// ==================== AUTH CONTEXT ====================
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("freestays_token"));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const savedToken = localStorage.getItem("freestays_token");
      if (savedToken) {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${savedToken}` },
          withCredentials: true
        });
        setUser(response.data);
        setToken(savedToken);
      }
    } catch (e) {
      localStorage.removeItem("freestays_token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data.user);
    setToken(response.data.token);
    localStorage.setItem("freestays_token", response.data.token);
    return response.data;
  };

  const register = async (email, password, name, referralCode = null, b2bCode = null) => {
    const payload = { email, password, name, referral_code: referralCode };
    if (b2bCode) payload.b2b_code = b2bCode;
    const response = await axios.post(`${API}/auth/register`, payload);
    setUser(response.data.user);
    setToken(response.data.token);
    localStorage.setItem("freestays_token", response.data.token);
    return response.data;
  };

  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Direct Google OAuth flow (parallel to Emergent Managed Google Auth).
  // The redirect_uri MUST match exactly what's configured in Google Cloud Console.
  const loginWithGoogleDirect = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/google/config`);
      const clientId = data.client_id;
      const redirectUri = window.location.origin + '/auth/callback';
      const state = `gdirect_${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem('google_oauth_state', state);
      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid email profile',
        access_type: 'online',
        prompt: 'select_account',
        state,
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    } catch (e) {
      toast.error('Google login is currently unavailable. Please try again.');
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {}
    setUser(null);
    setToken(null);
    localStorage.removeItem("freestays_token");
  };

  const hasValidPass = () => {
    if (!user) return false;
    return user.pass_type === 'one_time' || user.pass_type === 'annual' || user.pass_type === 'b2b';
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, loginWithGoogle, loginWithGoogleDirect, setUser, setToken, checkAuth, hasValidPass }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== DESTINATION SEARCH HOOK ====================
const useDestinationSearch = () => {
  const { i18n } = useTranslation();
  const [query, setQuery] = useState("");
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const debounceRef = useRef(null);

  const searchDestinations = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setDestinations([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/destinations/search`, {
        params: { q: searchQuery, lang: i18n.language || 'en' }
      });
      let results = response.data.destinations || [];
      // === NATIONALITY GATE FILTER ===
      // Soft UX: hide destinations in blocked countries for visitors without TC Kimlik.
      try {
        const gate = await getNationalityGateConfig();
        if (
          gate?.enabled &&
          getCustomerCountry() === gate.trigger_country &&
          getTCKimlikStatus() === 'no'
        ) {
          const blocked = (gate.blocked_destinations || []).map(c => String(c).toUpperCase());
          // ISO-code to full country name map for blocked destinations
          const ISO_TO_NAME = { TR: ['TURKEY', 'TURKIJE', 'TÜRKIYE', 'TURQUIA', 'TURCHIA', 'TÜRKEI'] };
          const blockedNames = new Set();
          blocked.forEach(code => (ISO_TO_NAME[code] || []).forEach(n => blockedNames.add(n)));
          results = results.filter(d => {
            const dcCode = String(d.country_code || '').toUpperCase();
            const dcName = String(d.country || d.name || '').toUpperCase();
            // Block if ISO code matches OR country full name matches
            if (blocked.includes(dcCode)) return false;
            if (blockedNames.has(dcName)) return false;
            // Also block when destination type is a country and its own name matches
            if (d.type === 'country' && blockedNames.has(String(d.name || '').toUpperCase())) return false;
            return true;
          });
        }
      } catch (_) { /* ignore */ }
      setDestinations(results);
    } catch (e) {
      console.error("Destination search error:", e);
      setDestinations([]);
    } finally {
      setLoading(false);
    }
  }, [i18n.language]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      searchDestinations(query);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, searchDestinations]);

  return { query, setQuery, destinations, loading, selectedDestination, setSelectedDestination };
};

// ==================== HEADER ====================
const Header = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { canInstall, isInstalled, handleInstall } = usePWAInstall();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [scrolled, setScrolled] = useState(false);
  const isPartnerPage = location.pathname.startsWith('/offers/');
  const { theme } = useTheme();
  const logoSrc = theme === 'dark' ? '/assets/freestays-eu-logo_wit.png' : '/assets/logo.png';

  // Scroll-aware opacity + shadow intensification (premium floating effect)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-gray-200 dark:border-white/10 transition-all duration-300 ${
        scrolled
          ? 'bg-white dark:bg-[#081D33]'
          : 'bg-white/95 dark:bg-[#081D33]/95'
      }`}
      style={{
        boxShadow: scrolled
          ? '0 8px 24px rgba(0, 0, 0, 0.22), 0 2px 4px rgba(0, 0, 0, 0.12)'
          : '0 4px 12px rgba(0, 0, 0, 0.15), 0 1px 2px rgba(0, 0, 0, 0.08)',
      }}
      data-testid="header"
    >
      <div className="max-w-[1760px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <img 
              src={logoSrc} 
              alt="Freestays" 
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-semibold text-[#081D33] dark:text-white/90 hover:text-[#00A9E0] transition-colors" data-testid="nav-search">
              {t('header.searchHotels', 'Search Hotels')}
            </Link>
            {/* Menu items moved to footer - managed via Admin > Settings > Footer */}
            {user && (
              <Link to="/dashboard" className="text-sm font-semibold text-[#081D33] dark:text-white/90 hover:text-[#00A9E0] transition-colors" data-testid="nav-dashboard">
                {t('dashboard.myBookings')}
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-2 md:gap-4">
            {/* Theme Toggle */}
            <ThemeToggle />
            
            {/* Language Selector - hidden on partner pages */}
            {!isPartnerPage && <LanguageSelector />}
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        {user.name?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:block text-sm font-medium">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                    {user.pass_type && user.pass_type !== 'free' && (
                      <Badge className="mt-1 bg-accent text-accent-foreground text-xs">
                        <Crown className="w-3 h-3 mr-1" />
                        {user.pass_type === 'annual' ? t('pass.annualPass', 'Annual Pass') : t('pass.oneTimePass', 'One-Time Pass')}
                      </Badge>
                    )}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <Ticket className="w-4 h-4 mr-2" /> {t('dashboard.myBookings')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard?tab=passcode')} data-testid="menu-passcode">
                    <Tag className="w-4 h-4 mr-2" /> {t('dashboard.myPass')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/refer-a-friend')} data-testid="menu-refer">
                    <Gift className="w-4 h-4 mr-2" /> {t('header.referFriend', 'Refer a Friend')}
                  </DropdownMenuItem>
                  {(user.role === 'manager' || user.group === 'managers' || user.role === 'admin' || user.is_admin || user.group === 'b2b' || user.pass_type === 'b2b') && (
                    <DropdownMenuItem onClick={() => navigate('/dashboard?tab=b2b')} data-testid="menu-b2b">
                      <Building2 className="w-4 h-4 mr-2" /> B2B Orders
                    </DropdownMenuItem>
                  )}
                  {(user.role === 'manager' || user.group === 'managers' || user.role === 'admin' || user.is_admin) && (
                    <DropdownMenuItem onClick={() => navigate('/dashboard?tab=create-order')} data-testid="menu-create-order">
                      <Plus className="w-4 h-4 mr-2" /> Create Order
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> {t('auth.signIn', 'Log Out').includes('Log') ? 'Log Out' : 'Uitloggen'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" className="hidden md:flex" data-testid="login-btn">{t('header.signIn')}</Button>
                  </DialogTrigger>
                  <AuthDialog />
                </Dialog>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      className="rounded-full px-3 sm:px-6 text-xs sm:text-sm bg-gradient-to-b from-[#FFAF00] to-[#FF7A00] text-white font-bold shadow-[0_4px_0_#CC6200,0_5px_10px_rgba(0,0,0,0.2)] hover:brightness-110 active:shadow-[0_0px_0_#CC6200] active:translate-y-[4px] border-0 whitespace-nowrap"
                      data-testid="signup-btn"
                    >
                      <span className="sm:hidden">{t('hero.getFreeRoomShort', 'Get Pass')}</span>
                      <span className="hidden sm:inline">{t('hero.getFreeRoom')}</span>
                    </Button>
                  </DialogTrigger>
                  <AuthDialog defaultTab="register" />
                </Dialog>
              </div>
            )}

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>
                    <img 
                      src={logoSrc} 
                      alt="Freestays" 
                      className="h-10 w-auto object-contain"
                    />
                  </SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4 mt-8">
                  <Link to="/" className="text-lg font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                    <Search className="w-5 h-5" /> {t('header.searchHotels', 'Search Hotels')}
                  </Link>
                  {/* Menu items moved to footer - managed via Admin > Settings > Footer */}
                  
                  {/* Install App Link */}
                  {canInstall && !isInstalled && (
                    <button 
                      className="text-lg font-medium flex items-center gap-2 text-primary hover:text-[#00A9E0]/80 transition-colors"
                      onClick={async () => {
                        await handleInstall();
                        setMobileMenuOpen(false);
                      }}
                    >
                      <Download className="w-5 h-5" /> {t('pwa.installApp', 'Install App')}
                    </button>
                  )}
                  
                  {/* Login/Sign Up for non-logged-in users */}
                  {!user && (
                    <>
                      <Separator className="my-1" />
                      <button 
                        className="text-lg font-medium flex items-center gap-2 text-[#00A9E0] hover:text-[#00A9E0]/80 transition-colors"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setAuthTab("login");
                          setTimeout(() => setShowAuth(true), 150);
                        }}
                        data-testid="mobile-login-btn"
                      >
                        <LogIn className="w-5 h-5" /> {t('auth.signIn', 'Sign In')}
                      </button>
                      <button 
                        className="text-lg font-medium flex items-center gap-2 hover:text-[#00A9E0] transition-colors"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          setAuthTab("register");
                          setTimeout(() => setShowAuth(true), 150);
                        }}
                        data-testid="mobile-signup-btn"
                      >
                        <UserPlus className="w-5 h-5" /> {t('auth.signUp', 'Sign Up')}
                      </button>
                    </>
                  )}
                  
                  {user && (
                    <>
                      <Separator className="my-1" />
                      <Link to="/dashboard" className="text-lg font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <Ticket className="w-5 h-5" /> {t('dashboard.myBookings')}
                      </Link>
                      <Link to="/dashboard?tab=favorites" className="text-lg font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <Heart className="w-5 h-5" /> {t('dashboard.favorites')}
                      </Link>
                      <Link to="/dashboard?tab=passcode" className="text-lg font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <Crown className="w-5 h-5" /> {t('dashboard.myPass')}
                      </Link>
                      <Link to="/refer-a-friend" className="text-lg font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <Gift className="w-5 h-5" /> {t('header.referFriend', 'Refer a Friend')}
                      </Link>
                      {(user.role === 'manager' || user.group === 'managers' || user.role === 'admin' || user.is_admin || user.group === 'b2b' || user.pass_type === 'b2b') && (
                        <Link to="/dashboard?tab=b2b" className="text-lg font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                          <Building2 className="w-5 h-5" /> B2B Orders
                        </Link>
                      )}
                      {(user.role === 'manager' || user.group === 'managers' || user.role === 'admin' || user.is_admin) && (
                        <Link to="/dashboard?tab=create-order" className="text-lg font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                          <Plus className="w-5 h-5" /> Create Order
                        </Link>
                      )}
                    </>
                  )}
                  
                  {/* Mobile Language Selector - hidden on partner pages */}
                  {!isPartnerPage && (
                  <>
                  <Separator className="my-2" />
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Languages className="w-4 h-4" /> {t('common.language', 'Language')}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {languages.map((lang) => (
                        <Button
                          key={lang.code}
                          variant={i18n.language === lang.code ? "default" : "outline"}
                          size="sm"
                          className="justify-start gap-2"
                          onClick={() => {
                            i18n.changeLanguage(lang.code);
                            setMobileMenuOpen(false);
                          }}
                        >
                          <span>{lang.flag}</span>
                          <span className="truncate">{lang.name}</span>
                        </Button>
                      ))}
                    </div>
                  </div>
                  </>
                  )}
                  
                  {/* Theme Toggle */}
                  <div className="pt-2 border-t border-border/50">
                    <p className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                      <Sun className="w-4 h-4" /> {t('common.theme', 'Theme')}
                    </p>
                    <ThemeToggle variant="outline" size="default" showLabel={true} />
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            {/* Controlled Auth Dialog for mobile menu */}
            <Dialog open={showAuth} onOpenChange={setShowAuth}>
              <AuthDialog defaultTab={authTab} />
            </Dialog>
          </div>
        </div>
      </div>
    </header>
  );
};

// ==================== AUTH DIALOG ====================
// Extracted to /app/frontend/src/components/AuthDialog.jsx (Fase 3b)
// Uses circular import for useAuth/usePWAInstall (named, hook-only — safe).
import { AuthDialog, AuthDialogContent } from './components/AuthDialog';

// ==================== AUTH CALLBACK ====================
const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.slice(1)).get('session_id');
      const searchParams = new URLSearchParams(location.search);
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      
      // Branch 1: Direct Google OAuth flow (code + state in query string)
      if (code && state && state.startsWith('gdirect_')) {
        try {
          const expected = sessionStorage.getItem('google_oauth_state');
          sessionStorage.removeItem('google_oauth_state');
          if (expected && expected !== state) {
            throw new Error('Invalid state');
          }
          const redirectUri = window.location.origin + '/auth/callback';
          const response = await axios.post(`${API}/auth/google/exchange`, {
            code,
            redirect_uri: redirectUri,
          });
          setUser(response.data.user);
          setToken(response.data.token);
          localStorage.setItem('freestays_token', response.data.token);
          toast.success('Welcome to Freestays!');
          navigate('/dashboard', { replace: true });
        } catch (error) {
          toast.error('Google authentication failed');
          navigate('/', { replace: true });
        }
        return;
      }
      
      // Branch 2: Emergent Managed Google Auth flow (session_id in URL hash)
      if (sessionId) {
        try {
          const response = await axios.get(`${API}/auth/session`, {
            headers: { "X-Session-ID": sessionId }
          });
          
          setUser(response.data);
          if (response.data.session_token) {
            setToken(response.data.session_token);
            localStorage.setItem("freestays_token", response.data.session_token);
          }
          
          toast.success("Welcome to Freestays!");
          navigate('/dashboard', { replace: true });
        } catch (error) {
          toast.error("Authentication failed");
          navigate('/', { replace: true });
        }
      } else {
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        <p className="mt-4 text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

// ==================== CHILDREN AGES DIALOG ====================
const ChildrenAgesDialog = ({ open, onOpenChange, childrenCount, childrenAges, onAgesChange, onConfirm }) => {
  const { t } = useTranslation();

  // Initialize ages based on childrenCount, ensuring proper defaults
  const getInitialAges = (count, existingAges) => {
    const newAges = [...(existingAges || [])];
    while (newAges.length < count) {
      newAges.push(10); // Default age — matches backend defensive default
    }
    while (newAges.length > count) {
      newAges.pop();
    }
    return newAges;
  };

  const [ages, setAges] = useState(() => getInitialAges(childrenCount, childrenAges));

  // Update ages when dialog opens with different childrenCount
  useEffect(() => {
    if (open) {
      setAges(getInitialAges(childrenCount, childrenAges));
    }
  }, [open, childrenCount, childrenAges]);

  const updateAge = (index, newAge) => {
    const newAges = [...ages];
    newAges[index] = newAge;
    setAges(newAges);
  };

  const handleConfirm = () => {
    onAgesChange(ages);
    onConfirm?.();
    onOpenChange(false);
  };

  // Block close-by-overlay/escape — user MUST confirm so ages are always set.
  const handleDialogOpenChange = (next) => {
    if (next === false) {
      // Auto-confirm with current values instead of cancelling
      onAgesChange(ages);
      onConfirm?.();
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="children-ages-dialog">
        <DialogHeader>
          <DialogTitle className="font-sans font-bold flex items-center gap-2">
            <Users className="w-5 h-5" />
            {t('search.childrenAgesTitle', "Children's Ages")}
          </DialogTitle>
          <DialogDescription>
            {t('search.childrenAgesHint', 'Hotel pricing depends on each child\'s age (0-17 years). Default: 10 years.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {Array.from({ length: childrenCount }).map((_, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
              <span className="font-medium">{t('search.childNumber', 'Child {{number}}').replace('{{number}}', index + 1)}</span>
              <Select
                value={String(ages[index] ?? 10)}
                onValueChange={(value) => updateAge(index, parseInt(value))}
              >
                <SelectTrigger className="w-32" data-testid={`child-age-select-${index}`}>
                  <SelectValue placeholder={t('search.agePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 18 }).map((_, age) => (
                    <SelectItem key={age} value={String(age)}>
                      {age === 0 ? t('search.underOneYear', 'Under 1 year') : age === 1 ? t('search.oneYear', '1 year') : t('search.years', '{{age}} years').replace('{{age}}', age)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button onClick={handleConfirm} data-testid="confirm-children-ages" className="w-full">
            {t('search.confirmAges', 'Confirm Ages')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==================== GUEST SELECTOR WITH CHILDREN AGES ====================
export const GuestSelector = ({ guests, setGuests, childrenAges, setChildrenAges, variant = "default" }) => {
  const { t } = useTranslation();
  const [showAgesDialog, setShowAgesDialog] = useState(false);
  const [guestsPopoverOpen, setGuestsPopoverOpen] = useState(false);

  const handleChildrenChange = (newCount) => {
    const updatedGuests = { ...guests, children: newCount };
    setGuests(updatedGuests);

    // Adjust ages array — always keep length === newCount with sane default (age 10).
    // Matches the backend defensive default so search never fails on missing ages.
    if (newCount === 0) {
      setChildrenAges([]);
    } else {
      const newAges = [...childrenAges];
      while (newAges.length < newCount) {
        newAges.push(10);
      }
      while (newAges.length > newCount) {
        newAges.pop();
      }
      setChildrenAges(newAges);
    }

    // Open ages dialog whenever children count CHANGES (up or down),
    // so user can verify/adjust ages match the new count. Closes
    // automatically when count = 0.
    if (newCount > 0 && newCount !== guests.children) {
      setShowAgesDialog(true);
    }
  };

  const buttonClass = variant === "outline" 
    ? "h-11" 
    : "h-12 rounded-xl bg-white/50 border-0 px-4";

  return (
    <>
      <Popover open={guestsPopoverOpen} onOpenChange={setGuestsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className={buttonClass} data-testid="guest-selector">
            <Users className="mr-2 h-5 w-5 text-muted-foreground" />
            {guests.adults} {t('search.adults', 'Adults')}, {guests.children} {t('search.children', 'Children')}
            <ChevronDown className="ml-2 w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium">{t('search.adults', 'Adults')}</span>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-8 w-8" 
                  onClick={() => setGuests({...guests, adults: Math.max(1, guests.adults - 1)})}
                >-</Button>
                <span className="w-8 text-center">{guests.adults}</span>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-8 w-8" 
                  onClick={() => setGuests({...guests, adults: guests.adults + 1})}
                >+</Button>
              </div>
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">{t('search.children', 'Children')}</span>
                <p className="text-xs text-muted-foreground">{t('search.childrenAges', 'Ages 0-17')}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-8 w-8" 
                  onClick={() => handleChildrenChange(Math.max(0, guests.children - 1))}
                >-</Button>
                <span className="w-8 text-center">{guests.children}</span>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-8 w-8" 
                  onClick={() => handleChildrenChange(guests.children + 1)}
                >+</Button>
              </div>
            </div>

            {guests.children > 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('search.childrenAgesShort', "Children's ages:")}</span>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-primary p-0 h-auto"
                      onClick={() => setShowAgesDialog(true)}
                      data-testid="edit-children-ages"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      {t('search.editAges', 'Edit ages')}
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {childrenAges.map((age, i) => (
                      <Badge key={i} variant="secondary" className="px-3 py-1">
                        {t('search.childNumber', 'Child {{number}}').replace('{{number}}', i + 1)}: {age === 0 ? t('search.underOneYear', 'Under 1 year') : age === 1 ? t('search.oneYear', '1 year') : t('search.years', '{{age}} years').replace('{{age}}', age)}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Separator />
            <Button
              onClick={() => setGuestsPopoverOpen(false)}
              className="w-full"
              data-testid="guests-popover-done"
            >
              {t('search.done', 'Done')}
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <ChildrenAgesDialog
        open={showAgesDialog}
        onOpenChange={setShowAgesDialog}
        childrenCount={guests.children}
        childrenAges={childrenAges}
        onAgesChange={setChildrenAges}
      />
    </>
  );
};

// ==================== DESTINATION AUTOCOMPLETE ====================
export const DestinationAutocomplete = ({ value, onChange, onSelect, placeholder = "City, region, or hotel name" }) => {
  const { query, setQuery, destinations, loading, setSelectedDestination } = useDestinationSearch();
  const [open, setOpen] = useState(false);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const { t } = useTranslation();
  const containerRef = useRef(null);

  // Close dropdown when clicking outside (delayed to allow button clicks to register first)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setTimeout(() => setOpen(false), 0);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setQuery(value);
  }, [value, setQuery]);

  const handleSelect = (dest) => {
    setSelectedDestination(dest);
    const parts = [dest.name];
    if (dest.city) parts.push(dest.city);
    if (dest.country) parts.push(dest.country);
    onChange(parts.join(', '));
    onSelect(dest);
    setOpen(false);
  };

  const handleNearby = () => {
    if (!navigator.geolocation) return;
    setNearbyLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          // Store coords for map view on search page
          sessionStorage.setItem('nearbyCoords', JSON.stringify({ lat: latitude, lng: longitude }));
          const response = await axios.get(`${API}/destinations/nearby`, {
            params: { lat: latitude, lng: longitude }
          });
          const data = response.data;
          if (data.destinations?.length > 0) {
            // Pick the best destination: closest one with >= 3 hotels, or fallback to most hotels
            const dests = data.destinations;
            const bestDest = dests.find(d => (d.hotel_count || 0) >= 3) || 
                             dests.sort((a, b) => (b.hotel_count || 0) - (a.hotel_count || 0))[0];
            handleSelect(bestDest);
          }
        } catch (err) {
          console.error('Nearby search error:', err);
        }
        setNearbyLoading(false);
      },
      () => setNearbyLoading(false),
      { timeout: 10000, maximumAge: 300000 }
    );
  };

  // Separate destinations and hotels for better display
  const destinationResults = destinations.filter(d => d.type !== 'hotel');
  const hotelResults = destinations.filter(d => d.type === 'hotel');
  const showNearby = open && value.length < 2;

  return (
    <div className="relative" ref={containerRef}>
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground z-10" />
      <Input 
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        className="pl-10 h-14 rounded-xl bg-white/50 border-0 text-base"
        data-testid="search-destination"
      />
      
      {/* Near me dropdown - shown when focused and no/short query */}
      {showNearby && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-card rounded-xl shadow-lg border z-50">
          <button
            className="w-full px-4 py-3 text-left hover:bg-secondary flex items-center gap-3 transition-colors rounded-xl"
            onClick={handleNearby}
            data-testid="nearby-search-btn"
          >
            <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
              {nearbyLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              ) : (
                <Navigation className="w-4 h-4 text-blue-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium">{t('search.nearMe', 'Near me')}</p>
              <p className="text-sm text-muted-foreground">{t('search.nearMeDesc', 'Search hotels near your location')}</p>
            </div>
          </button>
        </div>
      )}

      {open && (value.length >= 2) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-card rounded-xl shadow-lg border z-50 max-h-80 overflow-auto">
          {loading ? (
            <div className="p-4 text-center">
              <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : destinations.length > 0 ? (
            <>
              {/* Destinations Section */}
              {destinationResults.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-secondary/50 text-xs font-semibold text-muted-foreground uppercase tracking-wide sticky top-0">
                    Destinations
                  </div>
                  {destinationResults.map((dest) => (
                    <button
                      key={`dest-${dest.id}`}
                      className="w-full px-4 py-3 text-left hover:bg-secondary flex items-center gap-3 transition-colors"
                      onClick={() => handleSelect(dest)}
                      data-testid={`dest-option-${dest.id}`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{dest.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{dest.country} • {dest.hotel_count || 0} hotels</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Hotels Section */}
              {hotelResults.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-secondary/50 text-xs font-semibold text-muted-foreground uppercase tracking-wide sticky top-0">
                    Hotels
                  </div>
                  {hotelResults.map((dest) => (
                    <button
                      key={`hotel-${dest.hotel_id}`}
                      className="w-full px-4 py-3 text-left hover:bg-secondary flex items-center gap-3 transition-colors"
                      onClick={() => handleSelect(dest)}
                      data-testid={`hotel-option-${dest.hotel_id}`}
                    >
                      {dest.thumbnail ? (
                        <img 
                          src={dest.thumbnail} 
                          alt={dest.name}
                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0 bg-secondary"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-12 h-12 rounded-lg bg-accent/20 items-center justify-center flex-shrink-0 ${dest.thumbnail ? 'hidden' : 'flex'}`}
                      >
                        <Building2 className="w-5 h-5 text-accent-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{dest.name}</p>
                        <p className="text-sm text-muted-foreground truncate">{dest.city ? `${dest.city}, ` : ''}{dest.country}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              No destinations or hotels found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== LAST MINUTE DEALS SECTION ====================
const LastMinuteDeals = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, hasValidPass } = useAuth();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['lastMinuteDeals'],
    queryFn: async () => {
      const response = await axios.get(`${API}/hotels/last-minute`);
      return response.data;
    }
  });

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setSubscribed(true);
      toast.success('Successfully subscribed to our newsletter!');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="grid md:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <Card key={i} className="overflow-hidden">
            <div className="h-48 skeleton" />
            <div className="p-4 space-y-2">
              <div className="h-5 w-32 skeleton rounded" />
              <div className="h-4 w-24 skeleton rounded" />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  const hotels = data?.hotels || [];
  const badgeText = data?.badge_text || t('lastMinute.badge', 'Hot Deals');

  // Show "no offers" card when no real last minute deals are available
  if (hotels.length === 0) {
    return (
      <Card className="overflow-hidden bg-gradient-to-br from-accent/10 to-primary/10 border-accent/30">
        <div className="p-8 md:p-12 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-accent/20 flex items-center justify-center">
            <Clock className="w-10 h-10 text-accent" />
          </div>
          <h3 className="text-2xl font-sans font-bold font-semibold mb-3">
            {t('lastMinute.emptyTitle', 'Here we will show you our Last Minute deals')}
          </h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            {t('lastMinute.emptySubtitle', 'Check later for our exclusive offers with up to 30% off selected hotels!')}
          </p>
          
          {!subscribed ? (
            <div className="max-w-sm mx-auto">
              <p className="text-sm font-medium mb-3">
                {t('lastMinute.subscribeTitle', 'Subscribe to get Last Minute deals in your inbox')}
              </p>
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <Input
                  type="email"
                  placeholder={t('newsletter.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1"
                  required
                />
                <Button type="submit" disabled={subscribing} className="shrink-0">
                  {subscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-1" />
                      {t('lastMinute.subscribeButton', 'Subscribe')}
                    </>
                  )}
                </Button>
              </form>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">You're subscribed! We'll notify you of new deals.</span>
            </div>
          )}
        </div>
      </Card>
    );
  }

  // Dynamically adjust grid columns based on number of hotels
  const gridCols = hotels.length <= 3 ? 'md:grid-cols-3' : hotels.length === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3 lg:grid-cols-5';
  
  return (
    <div className={`grid ${gridCols} gap-6`}>
      {hotels.map(hotel => {
        const pricing = calculatePricing(hotel.min_price, hasValidPass?.());
        // Calculate nights from stored check-in/out dates
        const hotelCheckIn = hotel.last_minute_check_in ? new Date(hotel.last_minute_check_in) : addDays(new Date(), 1);
        const hotelCheckOut = hotel.last_minute_check_out ? new Date(hotel.last_minute_check_out) : addDays(new Date(), 3);
        const hotelNights = differenceInDays(hotelCheckOut, hotelCheckIn) || 2;
        
        return (
          <Card key={hotel.hotel_id} className="overflow-hidden card-hover group" data-testid={`lastminute-${hotel.hotel_id}`}>
            <div className="relative h-48 overflow-hidden bg-secondary">
              {(hotel.image_url || hotel.images?.[0]) ? (
                <img 
                  src={proxyImage(hotel.image_url || hotel.images?.[0], 480, 78)} 
                  alt={hotel.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  onError={(e) => { e.target.style.display = 'none'; }}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Hotel className="w-10 h-10 text-muted-foreground/30" />
                </div>
              )}
              <Badge className="absolute top-3 left-3 bg-red-500 text-white">
                <Zap className="w-3 h-3 mr-1" />
                {badgeText}
              </Badge>
              <Badge className="absolute top-3 right-3 bg-accent text-accent-foreground">
                -30%
              </Badge>
            </div>
            <div className="p-4">
              <h3 className="font-semibold truncate">{hotel.name}</h3>
              <p className="text-sm text-muted-foreground">{hotel.city_localized || hotel.city}, {hotel.country_localized || hotel.country}</p>
              <div className="flex items-end justify-between mt-3">
                <div>
                  <span className="text-xl font-semibold">€{pricing.roomTotal.toFixed(0)}</span>
                  <span className="text-sm text-muted-foreground"> / {hotelNights} {t('lastMinute.nightsLabel', 'nights')}</span>
                </div>
                <Button 
                  size="sm" 
                  className="rounded-full"
                  onClick={() => navigate(`/hotel/${hotel.hotel_id}?checkIn=${hotel.last_minute_check_in || format(addDays(new Date(), 1), 'yyyy-MM-dd')}&checkOut=${hotel.last_minute_check_out || format(addDays(new Date(), 3), 'yyyy-MM-dd')}&adults=2&children=0&b2c=1&destinationId=${hotel.fetched_destination_id || hotel.destination_id || ''}&destination=${encodeURIComponent(hotel.fetched_destination || hotel.city || '')}`)}
                >
                  Book Now
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};


// Last Minute Section with dynamic settings
// NOTE: uses Sunhotels b2c=1 API (separate flow from main search b2c=0).
// Managed via /admin/lastminuts "Download B2C=1 Offers". DO NOT couple to main flows.
export const LastMinuteSection = () => {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['lastMinuteDeals'],
    queryFn: async () => {
      const response = await axios.get(`${API}/hotels/last-minute`);
      return response.data;
    }
  });

  // Use translations for non-English, CMS content for English
  const lang = (i18n.language || 'en').split('-')[0];
  const title = lang === 'en' ? (data?.title || t('lastMinute.sectionTitle', 'Last Minute Offers')) : t('lastMinute.sectionTitle', 'Last Minute Offers');
  const subtitle = lang === 'en' ? (data?.subtitle || t('lastMinute.sectionSubtitle', 'Book now and save up to 50% on selected hotels')) : t('lastMinute.sectionSubtitle', 'Book now and save up to 50% on selected hotels');
  const badgeText = lang === 'en' ? (data?.badge_text || t('lastMinute.badge', 'Hot Deals')) : t('lastMinute.badge', 'Hot Deals');
  
  // Get dates from the first hotel's stored dates (from admin fetch)
  const hotels = data?.hotels || [];
  const firstHotel = hotels[0];
  const checkIn = firstHotel?.last_minute_check_in || data?.check_in;
  const checkOut = firstHotel?.last_minute_check_out || data?.check_out;

  return (
    <section className="py-20 md:py-28">
      <div className="max-w-[1760px] mx-auto px-4 md:px-8">
        <div className="text-center mb-16">
          <div>
            <Badge className="mb-2 bg-red-500 text-white">
              <Zap className="w-3 h-3 mr-1" /> {badgeText}
            </Badge>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-[1.05] tracking-tight" style={{textShadow: '2px 3px 6px rgba(0,0,0,0.5)'}}>
              <span className="block text-white">{t('lastMinute.smartTitle1', 'Last-Minute Escapes')}</span>
              <span className="block" style={{color: '#E8A317'}}>{t('lastMinute.smartTitle2', "Book Before It's Gone")}</span>
            </h2>
            <p className="text-white/80">{subtitle}</p>
            {checkIn && checkOut && (
              <p className="text-sm text-white/60 mt-1">
                {t('lastMinute.travelDates', 'Travel dates')}: {format(new Date(checkIn), 'MMM d, yyyy')} - {format(new Date(checkOut), 'MMM d, yyyy')}
              </p>
            )}
          </div>
          <Link to="/last-minute" className="inline-flex items-center text-sm font-medium text-primary hover:underline mt-3">
            {t('lastMinute.viewAll', 'Bekijk alles')} <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        <LastMinuteDeals />
      </div>
    </section>
  );
};

// ==================== SITE FOOTER COMPONENT ====================
const SiteFooter = () => {
  const { t, i18n } = useTranslation();
  const [footerSettings, setFooterSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const isEnglish = (i18n.language || 'en').split('-')[0] === 'en';

  useEffect(() => {
    const fetchFooterSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/footer`);
        setFooterSettings(response.data);
      } catch (error) {
        console.error('Failed to load footer settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFooterSettings();
  }, []);

  // Don't render while loading to prevent flicker
  if (loading) return null;

  return (
    <footer className="relative z-10 py-16 bg-[#081D33] text-white" data-testid="site-footer">
      <div className="max-w-[1760px] mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-4 gap-10">
          {/* Column 1 - Company Info */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="/assets/freestays-eu-logo_wit.png" 
                alt="Freestays" 
                className="h-12 w-auto object-contain"
              />
            </div>
            <p className="text-sm text-white/60 mb-4">
              {isEnglish ? (footerSettings?.column1_tagline || t('footer.tagline')) : t('footer.tagline')}
            </p>
            <p className="text-xs text-white/40">
              {isEnglish ? (footerSettings?.column1_company || t('footer.company')) : t('footer.company')}
            </p>
          </div>
          
          {/* Column 2 - Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">{isEnglish ? (footerSettings?.column2_title || t('footer.quickLinks')) : t('footer.quickLinks')}</h4>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-white/60">
              {(footerSettings?.column2_links || [
                { label: t('footer.about'), url: '/who-we-are' },
                { label: t('footer.referFriend'), url: '/refer-a-friend' },
                { label: t('footer.contact'), url: '/contact' }
              ]).map((link, idx) => (
                <li key={idx}>
                  {link.url?.startsWith('http') ? (
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#00A9E0] transition-colors">
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.url || '#'} className="hover:text-[#00A9E0] transition-colors">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Column 3 - Menu/Legal */}
          <div>
            <h4 className="font-semibold mb-4">{isEnglish ? (footerSettings?.column3_title || 'Menu') : t('footer.quickLinks')}</h4>
            <ul className="space-y-2 text-sm text-white/60">
              {(footerSettings?.column3_links || [
                { label: t('footer.privacy'), url: '/privacy' },
                { label: t('footer.terms'), url: '/terms' }
              ]).map((link, idx) => (
                <li key={idx}>
                  {link.url?.startsWith('http') ? (
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#00A9E0] transition-colors">
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.url || '#'} className="hover:text-[#00A9E0] transition-colors">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Column 4 - Social Links */}
          <div>
            <h4 className="font-semibold mb-4">{footerSettings?.column4_title || 'Connect'}</h4>
            <div className="flex gap-3 flex-wrap">
              {(footerSettings?.social_links || [
                { type: 'website', url: 'https://freestays.eu' },
                { type: 'email', url: 'mailto:hello@freestays.eu' }
              ]).map((social, idx) => {
                const getSocialIcon = (type) => {
                  switch (type) {
                    case 'facebook': return <Facebook className="w-5 h-5" />;
                    case 'twitter': return <Twitter className="w-5 h-5" />;
                    case 'instagram': return <Instagram className="w-5 h-5" />;
                    case 'linkedin': return <Linkedin className="w-5 h-5" />;
                    case 'youtube': return <Youtube className="w-5 h-5" />;
                    case 'email': return <Mail className="w-5 h-5" />;
                    default: return <Globe className="w-5 h-5" />;
                  }
                };
                return (
                  <a 
                    key={idx}
                    href={social.url} 
                    target={social.type !== 'email' ? '_blank' : undefined}
                    rel={social.type !== 'email' ? 'noopener noreferrer' : undefined}
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#00A9E0] hover:text-white transition-all"
                    data-testid={`footer-social-${social.type}`}
                  >
                    {getSocialIcon(social.type)}
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Popular Destinations - Full width SEO Internal Linking row */}
        <div className="mt-10 pt-8 border-t border-white/10">
          <FooterDestinationLinks limit={12} />
        </div>
        <Separator className="my-10 bg-white/10" />
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            {(footerSettings?.copyright || t('footer.copyright', { year: new Date().getFullYear() })).replace('{{year}}', new Date().getFullYear())}
          </p>
          <div className="flex gap-6 text-sm text-muted-foreground">
            {(footerSettings?.column3_links || [
              { label: t('footer.privacy'), url: '/about' },
              { label: t('footer.terms'), url: '/about' }
            ]).map((link, idx) => (
              link.url?.startsWith('http') ? (
                <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#00A9E0] transition-colors">
                  {link.label}
                </a>
              ) : (
                <Link key={idx} to={link.url || '#'} className="hover:text-[#00A9E0] transition-colors">
                  {link.label}
                </Link>
              )
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

// ==================== HOME PAGE ====================
// Extracted to /app/frontend/src/pages/Home.jsx (Fase 4b).
// Includes TestimonialSubmitForm + TestimonialsSection (HomePage-only helpers).
// Uses circular imports for useAuth + GuestSelector + DestinationAutocomplete + LastMinuteSection.
import { HomePage } from './pages/Home';
// ==================== SEARCH PAGE ====================
const SearchPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasValidPass, user } = useAuth();
  
  const [destination, setDestination] = useState(searchParams.get('destination') || '');
  const [destinationId, setDestinationId] = useState(searchParams.get('destinationId') || '');
  const [resortId, setResortId] = useState(searchParams.get('resortId') || '');
  
  const [checkIn, setCheckIn] = useState(() => {
    const dateStr = searchParams.get('checkIn');
    return dateStr ? parseLocalDate(dateStr) : addDays(new Date(), 7);
  });
  const [checkOut, setCheckOut] = useState(() => {
    const dateStr = searchParams.get('checkOut');
    return dateStr ? parseLocalDate(dateStr) : addDays(new Date(), 10);
  });
  const [guests, setGuests] = useState({
    adults: parseInt(searchParams.get('adults') || '2'),
    children: parseInt(searchParams.get('children') || '0'),
    rooms: parseInt(searchParams.get('rooms') || '1')
  });
  // Parse children ages from URL
  const initialChildrenAges = searchParams.get('childrenAges') 
    ? searchParams.get('childrenAges').split(',').filter(a => a).map(a => parseInt(a))
    : [];
  const [childrenAges, setChildrenAges] = useState(initialChildrenAges);
  const [selectedHotel, setSelectedHotel] = useState(null); // For direct hotel selection
  const userModifiedText = useRef(false); // Tracks if user typed manually (without selecting from dropdown)
  
  // Sync state with URL params when navigating back (e.g., after language change)
  useEffect(() => {
    const urlDest = searchParams.get('destination') || '';
    const urlDestId = searchParams.get('destinationId') || '';
    const urlResortId = searchParams.get('resortId') || '';
    const urlCheckIn = searchParams.get('checkIn');
    const urlCheckOut = searchParams.get('checkOut');
    const urlAdults = searchParams.get('adults');
    const urlChildren = searchParams.get('children');
    const urlRooms = searchParams.get('rooms');
    const urlChildrenAges = searchParams.get('childrenAges');
    
    // Always sync URL params to state to handle back navigation properly
    if (urlDest) setDestination(urlDest);
    if (urlDestId) setDestinationId(urlDestId);
    if (urlResortId) setResortId(urlResortId);
    if (urlCheckIn) setCheckIn(parseLocalDate(urlCheckIn));
    if (urlCheckOut) setCheckOut(parseLocalDate(urlCheckOut));
    if (urlAdults || urlChildren || urlRooms) {
      setGuests({
        adults: parseInt(urlAdults || '2'),
        children: parseInt(urlChildren || '0'),
        rooms: parseInt(urlRooms || '1')
      });
    }
    if (urlChildrenAges) {
      setChildrenAges(urlChildrenAges.split(',').filter(a => a).map(a => parseInt(a)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  
  // Date picker popover state
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [starFilter, setStarFilter] = useState([]);
  const [themeFilter, setThemeFilter] = useState([]);
  const [amenityFilter, setAmenityFilter] = useState([]);
  const [sortBy, setSortBy] = useState("recommended");
  const [mealPackageFilter, setMealPackageFilter] = useState(false);
  
  // Email capture popup state
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [captureEmail, setCaptureEmail] = useState('');
  const [emailCaptureSubmitted, setEmailCaptureSubmitted] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  
  // Nearby / Map view state
  const isNearbySearch = searchParams.get('nearby') === 'true';
  const userLat = parseFloat(searchParams.get('userLat')) || null;
  const userLng = parseFloat(searchParams.get('userLng')) || null;
  const [showMapView, setShowMapView] = useState(isNearbySearch);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const HOTELS_PER_PAGE = 12;
  
  // Map interaction state
  const [mapHoveredHotelId, setMapHoveredHotelId] = useState(null);
  const [mapSelectedHotelId, setMapSelectedHotelId] = useState(null);
  const hotelCardRefs = useRef({});
  
  // Sidebar filters collapse state
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Detect "pending changes" — local state differs from URL params.
  // When true, the "Update" button is shown (with brief pulse) so the user
  // can sync URL (for sharing/refresh/back-nav) and trigger a visible refetch.
  const hasPendingChanges = useMemo(() => {
    try {
      const urlCheckIn = searchParams.get('checkIn') || '';
      const urlCheckOut = searchParams.get('checkOut') || '';
      const urlAdults = searchParams.get('adults') || '2';
      const urlChildren = searchParams.get('children') || '0';
      const urlRooms = searchParams.get('rooms') || '1';
      const urlDestId = searchParams.get('destinationId') || '';
      const urlResortId = searchParams.get('resortId') || '';
      const urlAges = searchParams.get('childrenAges') || '';
      if (!checkIn || !checkOut) return false;
      if (format(checkIn, 'yyyy-MM-dd') !== urlCheckIn) return true;
      if (format(checkOut, 'yyyy-MM-dd') !== urlCheckOut) return true;
      if (String(guests.adults) !== urlAdults) return true;
      if (String(guests.children) !== urlChildren) return true;
      if (String(guests.rooms) !== urlRooms) return true;
      if ((destinationId || '') !== urlDestId) return true;
      if ((resortId || '') !== urlResortId) return true;
      if (childrenAges.join(',') !== urlAges) return true;
      return false;
    } catch (_) {
      return false;
    }
  }, [searchParams, checkIn, checkOut, guests, destinationId, resortId, childrenAges]);

  // Brief pulse animation (2s) when pending changes become true
  const [pulseUpdate, setPulseUpdate] = useState(false);
  useEffect(() => {
    if (hasPendingChanges) {
      setPulseUpdate(true);
      const t = setTimeout(() => setPulseUpdate(false), 2000);
      return () => clearTimeout(t);
    }
    setPulseUpdate(false);
  }, [hasPendingChanges]);

  const { data: searchResults, isLoading } = useQuery({
    queryKey: ['hotels', resortId || destinationId || destination, format(checkIn, 'yyyy-MM-dd'), format(checkOut, 'yyyy-MM-dd'), guests.adults, guests.children, guests.rooms, childrenAges.join(','), getCustomerCountry()],
    queryFn: async () => {
      const response = await axios.post(`${API}/hotels/search`, {
        destination: destination,
        destination_id: destinationId || null,
        resort_id: resortId || null,
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        adults: guests.adults,
        children: guests.children,
        children_ages: childrenAges.length > 0 ? childrenAges : null,
        rooms: guests.rooms,
        currency: 'EUR',
        b2c: 0,
        language: 'en',
        customer_country: getCustomerCountry()
      });
      return response.data;
    },
    enabled: destination.length > 0 || destinationId.length > 0 || resortId.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - cache results, translations happen client-side
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  });

  // Batch social-proof for the visible hotels (used for "🔥 X viewing" badge on each card)
  const visibleHotelIds = useMemo(() => {
    const list = searchResults?.hotels || [];
    return list.slice(0, 50).map((h) => String(h.hotel_id)).filter(Boolean);
  }, [searchResults]);

  const { data: socialProofMap = {} } = useQuery({
    queryKey: ['social-proof-batch', visibleHotelIds.join(',')],
    queryFn: async () => {
      if (visibleHotelIds.length === 0) return {};
      const r = await axios.post(`${API}/hotels/social-proof-batch`, {
        hotel_ids: visibleHotelIds,
      });
      return r.data || {};
    },
    enabled: visibleHotelIds.length > 0,
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  // Show email capture popup for non-logged-in users when comparison data is available
  // Skip for country-level / very broad searches (>150 hotels) — the average savings
  // across an entire country is statistically unreliable and looks salesy.
  useEffect(() => {
    const hasShownPopup = sessionStorage.getItem('email_capture_shown');
    const cmp = searchResults?.comparison_data;
    const isBroadSearch = (cmp?.hotels_count || 0) > 150 || cmp?.destination_type === 'country';
    if (!user && cmp?.hotels_with_savings > 0 && !hasShownPopup && !emailCaptureSubmitted && !isBroadSearch) {
      // Show popup after a short delay
      const timer = setTimeout(() => {
        setShowEmailCapture(true);
        sessionStorage.setItem('email_capture_shown', 'true');
      }, 3000); // Show after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [user, searchResults, emailCaptureSubmitted]);

  // Auto-save comparison for logged-in users
  useEffect(() => {
    if (user && searchResults?.comparison_data?.hotels_with_savings > 0) {
      const saveKey = `comp_saved_${searchResults.comparison_data.destination}_${searchResults.comparison_data.check_in}`;
      if (sessionStorage.getItem(saveKey)) return; // Prevent duplicate saves
      
      const token = localStorage.getItem('freestays_token');
      axios.post(`${API}/price-comparison/save`, {
        comparison_data: searchResults.comparison_data,
        visitor_email: user.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => {
        sessionStorage.setItem(saveKey, 'true');
      }).catch(err => console.log('Auto-save comparison failed:', err));
    }
  }, [user, searchResults]);

  const handleEmailCapture = async (e) => {
    e.preventDefault();
    if (!captureEmail) return;
    
    try {
      // Save comparison with visitor email
      await axios.post(`${API}/price-comparison/save`, {
        comparison_data: searchResults.comparison_data,
        visitor_email: captureEmail
      });
      setEmailCaptureSubmitted(true);
      toast.success(t('priceComparison.subscribed'));
      setShowEmailCapture(false);
    } catch (error) {
      toast.error("Failed to save. Please try again.");
    }
  };

  const nights = differenceInDays(parseLocalDate(checkOut), parseLocalDate(checkIn));
  
  // Show map toast when search results load (desktop only, once per search session)
  const prevIsLoadingRef = useRef(true);
  const lastToastKeyRef = useRef(null);
  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading && searchResults?.hotels?.length > 0) {
      // Skip on mobile — toast overlaps the datepicker/calendar
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      // Dedup by (destination + checkIn + checkOut) so filter/sort changes don't re-trigger
      const searchKey = `${destinationId || destination}|${checkIn}|${checkOut}`;
      if (!isMobile && lastToastKeyRef.current !== searchKey) {
        lastToastKeyRef.current = searchKey;
        const count = searchResults.hotels.length;
        const params = new URLSearchParams(window.location.search);
        toast(
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => {
              sessionStorage.setItem('searchMapHotels', JSON.stringify({
                hotels: searchResults.hotels,
                nearbyHotels: []
              }));
              navigate(`/search/map?${params.toString()}`);
            }}
            data-testid="map-toast-cta"
          >
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-sm">{t('search.viewOnMap', 'View Location on Map')}</p>
              <p className="text-xs text-muted-foreground">{count} {t('search.hotelsOnMap', 'hotels on map')} — {t('search.mapCta', 'see prices & locations')}</p>
            </div>
          </div>,
          { id: `map-toast-${searchKey}`, duration: 8000 }
        );
      }
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading, searchResults]);

  let filteredHotels = searchResults?.hotels || [];
  
  // === NATIONALITY GATE FILTER (TC Kimlik) ===
  // Soft UX filter: hide hotels in blocked countries for visitors without TC Kimlik.
  // Runs ONLY when: gate rule active, visitor from trigger_country (TR), and tc_kimlik_status === 'no'.
  const [nationalityGate, setNationalityGate] = useState(null);
  useEffect(() => {
    getNationalityGateConfig().then(setNationalityGate).catch(() => {});
  }, []);
  if (
    nationalityGate?.enabled &&
    getCustomerCountry() === nationalityGate.trigger_country &&
    getTCKimlikStatus() === 'no'
  ) {
    const blocked = (nationalityGate.blocked_destinations || []).map(c => String(c).toUpperCase());
    const ISO_TO_NAME = { TR: ['TURKEY', 'TURKIJE', 'TÜRKIYE', 'TURQUIA', 'TURCHIA', 'TÜRKEI'] };
    const blockedNames = new Set();
    blocked.forEach(code => (ISO_TO_NAME[code] || []).forEach(n => blockedNames.add(n)));
    filteredHotels = filteredHotels.filter(h => {
      const hcCode = String(h.country_code || '').toUpperCase();
      const hcName = String(h.country || '').toUpperCase();
      if (blocked.includes(hcCode)) return false;
      if (blockedNames.has(hcName)) return false;
      return true;
    });
  }
  
  // Apply star filter
  if (starFilter.length > 0) {
    filteredHotels = filteredHotels.filter(h => starFilter.includes(Math.floor(h.star_rating)));
  }
  
  // Apply price filter
  filteredHotels = filteredHotels.filter(h => h.min_price >= priceRange[0] && h.min_price <= priceRange[1]);
  
  // Apply theme filter
  if (themeFilter.length > 0) {
    filteredHotels = filteredHotels.filter(h => {
      const hotelThemes = h.themes || [];
      return themeFilter.some(t => hotelThemes.some(ht => ht.toLowerCase().includes(t.toLowerCase())));
    });
  }
  
  // Apply amenity filter
  if (amenityFilter.length > 0) {
    filteredHotels = filteredHotels.filter(h => {
      const hotelAmenities = h.amenities || [];
      return amenityFilter.every(a => hotelAmenities.some(ha => ha.toLowerCase().includes(a.toLowerCase())));
    });
  }
  
  // Apply meal package filter (Free Room with Meals)
  if (mealPackageFilter) {
    filteredHotels = filteredHotels.filter(h => h.has_meal_packages === true);
  }
  
  // Apply sorting
  if (sortBy === 'price_low') {
    filteredHotels = [...filteredHotels].sort((a, b) => a.min_price - b.min_price);
  } else if (sortBy === 'price_high') {
    filteredHotels = [...filteredHotels].sort((a, b) => b.min_price - a.min_price);
  } else if (sortBy === 'rating') {
    filteredHotels = [...filteredHotels].sort((a, b) => b.review_score - a.review_score);
  } else if (sortBy === 'stars') {
    filteredHotels = [...filteredHotels].sort((a, b) => b.star_rating - a.star_rating);
  }

  // Pagination calculations
  const totalHotels = filteredHotels.length;
  const totalPages = Math.ceil(totalHotels / HOTELS_PER_PAGE);
  const startIndex = (currentPage - 1) * HOTELS_PER_PAGE;
  const endIndex = startIndex + HOTELS_PER_PAGE;
  const paginatedHotels = filteredHotels.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [starFilter, themeFilter, amenityFilter, priceRange, sortBy, mealPackageFilter, searchResults]);

  // Centralised "user clicked a hotel pin on the map" handler.
  // Fixes: on mobile (and any time the picked hotel is not on the current page)
  // the scrollIntoView call silently failed because the DOM element wasn't
  // rendered. We now jump to the right page first, then scroll after re-render.
  const handleMapHotelSelect = useCallback((id) => {
    setMapSelectedHotelId(id);
    if (!id) return;
    const idx = filteredHotels.findIndex(h => h.hotel_id === id);
    const scrollToCard = () => {
      const el = hotelCardRefs.current[id];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };
    if (idx < 0) {
      // Hotel not in current filtered list — fallback to direct navigation.
      const hotel = filteredHotels[0];
      if (hotel) scrollToCard();
      return;
    }
    const targetPage = Math.floor(idx / HOTELS_PER_PAGE) + 1;
    if (targetPage !== currentPage) {
      setCurrentPage(targetPage);
      // Wait one render tick so the new page's cards mount and refs populate.
      setTimeout(scrollToCard, 120);
    } else {
      scrollToCard();
    }
  }, [filteredHotels, currentPage, HOTELS_PER_PAGE]);

  const handleSearch = () => {
    // If a specific hotel is selected, go directly to hotel page
    if (selectedHotel) {
      const checkInStr = format(checkIn, 'yyyy-MM-dd');
      const checkOutStr = format(checkOut, 'yyyy-MM-dd');
      const destParam = selectedHotel.city ? `&destination=${encodeURIComponent(selectedHotel.city)}` : '';
      const destIdParam = selectedHotel.dest_id ? `&destinationId=${selectedHotel.dest_id}` : '';
      const resortIdParam = selectedHotel.resort_id ? `&resortId=${selectedHotel.resort_id}` : '';
      navigate(`/hotel/${selectedHotel.hotel_id}?checkIn=${checkInStr}&checkOut=${checkOutStr}&adults=${guests.adults}&children=${guests.children}${destParam}${destIdParam}${resortIdParam}`);
      return;
    }
    
    // If user manually typed text (without selecting from dropdown), clear stale IDs
    // so the backend uses text-based search instead of the old destination ID
    const effectiveDestId = userModifiedText.current ? '' : (destinationId || '');
    const effectiveResortId = userModifiedText.current ? '' : (resortId || '');
    
    // Update URL params - the query will auto-refetch due to queryKey change
    const params = new URLSearchParams({
      destination,
      destinationId: effectiveDestId,
      resortId: effectiveResortId,
      checkIn: format(checkIn, 'yyyy-MM-dd'),
      checkOut: format(checkOut, 'yyyy-MM-dd'),
      adults: guests.adults.toString(),
      children: guests.children.toString(),
      childrenAges: childrenAges.join(','),
      rooms: guests.rooms.toString(),
      customerCountry: getCustomerCountry()
    });
    setSearchParams(params);
    // Sync state with cleared IDs if user typed manually
    if (userModifiedText.current) {
      setDestinationId('');
      setResortId('');
      userModifiedText.current = false;
    }
    // Reset to first page when search params change
    setCurrentPage(1);

    // Force refetch + visible feedback so the Update click feels responsive
    // (Without this, when state already changed via pickers, the click feels like a no-op)
    try { queryClient.invalidateQueries({ queryKey: ['hotels'] }); } catch (_) {}
    // Scroll to top of results so the user sees the refresh
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDestinationSelect = (dest) => {
    const displayParts = [dest.name];
    if (dest.city) displayParts.push(dest.city);
    if (dest.country) displayParts.push(dest.country);
    const displayName = displayParts.join(', ');
    
    userModifiedText.current = false; // User selected from dropdown, not manual text
    
    // If it's a hotel, store hotel info and let user pick dates
    if (dest.type === 'hotel' && dest.hotel_id) {
      setSelectedHotel({
        hotel_id: dest.hotel_id,
        name: dest.name,
        dest_id: dest.id,
        resort_id: dest.resort_id || '',
        thumbnail: dest.thumbnail
      });
      setDestination(displayName);
      setDestinationId(dest.id);
      return;
    }
    // Otherwise, set destination for search
    setSelectedHotel(null);
    setDestination(displayName);
    setDestinationId(dest.id);
    setResortId(dest.resort_id || '');
  };
  
  const clearSelectedHotel = () => {
    setSelectedHotel(null);
    setDestination('');
    setDestinationId('');
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#081D33]" data-testid="search-page">
      {/* Search Header - Premium Design */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 pt-20">
        <div className="max-w-[1760px] mx-auto px-4 md:px-8 py-6">
          {/* Breadcrumbs */}
          <Breadcrumbs items={[
            { label: t('search.searchTitle', 'Search Results'), href: null }
          ]} />
          
          {/* Search Summary */}
          <div className="mt-4 flex flex-wrap items-center gap-4 text-white">
            <h1 className="font-sans font-bold text-2xl md:text-3xl">
              {destination || t('search.hotels', 'Hotels')}
            </h1>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <span>{format(checkIn, "MMM d")} - {format(checkOut, "MMM d, yyyy")}</span>
              <span>•</span>
              <span>{guests.adults} {t('search.adults')} {guests.children > 0 && `, ${guests.children} ${t('search.children')}`}</span>
              <span>•</span>
              <span>{nights} {t('search.nights', 'nights')}</span>
            </div>
          </div>

          {/* Compact Search Bar */}
          <div className="mt-4 flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              {selectedHotel ? (
                <div className="relative p-2 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm">
                  <div className="flex items-center gap-2">
                    {selectedHotel.thumbnail && (
                      <img 
                        src={selectedHotel.thumbnail} 
                        alt={selectedHotel.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-white truncate">{selectedHotel.name}</p>
                      <p className="text-xs text-primary-foreground/70">{t('search.hotelSelected', 'Hotel selected')}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-white hover:bg-white/10"
                      onClick={clearSelectedHotel}
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl">
                  <DestinationAutocomplete 
                    value={destination}
                    onChange={(val) => { setDestination(val); userModifiedText.current = true; }}
                    onSelect={handleDestinationSelect}
                    placeholder={t('search.whereTo')}
                  />
                </div>
              )}
            </div>
            
            <Button onClick={handleSearch} className={`h-11 rounded-xl bg-white text-slate-900 hover:bg-white/90 font-semibold shadow-lg ${pulseUpdate ? 'animate-pulse ring-2 ring-primary' : ''} ${!selectedHotel && !hasPendingChanges ? 'hidden' : ''}`} data-testid="search-submit" title={t('search.confirmChanges', 'Confirm changes')}>
              {selectedHotel ? (
                <>
                  <Building2 className="w-4 h-4 mr-2" />
                  {t('search.viewHotel', 'View Hotel')}
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  {t('search.updateSearch', 'Update')}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Date & Guests Selector Row - Sticky */}
      <div className="bg-white dark:bg-slate-900 border-b shadow-sm sticky top-16 z-40">
        <div className="max-w-[1760px] mx-auto px-4 md:px-8 py-3">
          <div className="flex flex-wrap items-center gap-3 justify-center md:justify-start">
            {/* Date Range Selector */}
            <div className="flex items-center gap-1 bg-secondary/50 rounded-xl p-1">
              <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" className="h-10 px-3 hover:bg-white dark:hover:bg-slate-800 rounded-lg" data-testid="checkin-picker">
                    <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                    <div className="text-left">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('search.checkIn')}</div>
                      <div className="font-semibold text-sm">{format(checkIn, "EEE, MMM d")}</div>
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={checkIn}
                    onSelect={(date) => {
                      if (date) {
                        setCheckIn(date);
                        if (date >= checkOut) {
                          setCheckOut(addDays(date, 1));
                        }
                        setCheckInOpen(false);
                        setTimeout(() => setCheckOutOpen(true), 150);
                      }
                    }}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <ArrowRight className="w-4 h-4 text-muted-foreground mx-1" />

              <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" className="h-10 px-3 hover:bg-white dark:hover:bg-slate-800 rounded-lg" data-testid="checkout-picker">
                    <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                    <div className="text-left">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('search.checkOut')}</div>
                      <div className="font-semibold text-sm">{format(checkOut, "EEE, MMM d")}</div>
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={checkOut}
                    onSelect={(date) => {
                      if (date) {
                        setCheckOut(date);
                        setCheckOutOpen(false);
                      }
                    }}
                    disabled={(date) => date <= checkIn}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <div className="h-8 w-px bg-border mx-1" />

              <div className="px-3 py-1 text-center">
                <div className="text-xs text-muted-foreground">Nights</div>
                <div className="font-semibold text-primary">{nights}</div>
              </div>
            </div>

            {/* Guest Selector */}
            <GuestSelector 
              guests={guests}
              setGuests={setGuests}
              childrenAges={childrenAges}
              setChildrenAges={setChildrenAges}
              variant="outline"
            />

            {/* Country Flag Selector */}
            <CountrySelector compact />

            {/* Update Search Button - only visible when local state differs from URL */}
            {hasPendingChanges && (
              <Button 
                onClick={handleSearch} 
                size="sm" 
                className={`rounded-full px-6 ${pulseUpdate ? 'animate-pulse ring-2 ring-primary' : ''}`}
                data-testid="update-search-btn"
                title={t('search.confirmChanges', 'Confirm changes')}
              >
                <Search className="w-4 h-4 mr-2" />
                {t('search.updateSearch', 'Update')}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-[1760px] mx-auto px-4 md:px-8 py-4 md:py-8">
        {/* Mobile Compact Filter Bar */}
        <div className="md:hidden mb-4">
          <div className="relative">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide snap-x px-1 -mx-1">
            {/* Price Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant={priceRange[0] > 0 || priceRange[1] < 1000 ? "default" : "secondary"} 
                  size="sm" 
                  className="flex-shrink-0 h-9 rounded-full gap-1 max-w-[150px]"
                >
                  <Euro className="w-3.5 h-3.5" />
                  <span className="truncate">
                  {priceRange[0] > 0 || priceRange[1] < 1000 
                    ? `€${priceRange[0]}-${priceRange[1] === 1000 ? '1000+' : priceRange[1]}`
                    : t('search.price', 'Price')
                  }
                  </span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-3" align="start">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('search.price', 'Price')}</span>
                    <span className="font-medium">€{priceRange[0]} - €{priceRange[1] === 1000 ? '1000+' : priceRange[1]}</span>
                  </div>
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    max={1000}
                    step={25}
                  />
                  <div className="grid grid-cols-3 gap-1">
                    {[100, 200, 500].map(price => (
                      <Button
                        key={price}
                        variant={priceRange[1] === price ? "default" : "outline"}
                        size="sm"
                        className="text-xs h-7 px-2"
                        onClick={() => setPriceRange([0, price])}
                      >
                        &lt;€{price}
                      </Button>
                    ))}
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Stars Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant={starFilter.length > 0 ? "default" : "secondary"} 
                  size="sm" 
                  className="flex-shrink-0 h-9 rounded-full gap-1 max-w-[150px]"
                >
                  <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  <span className="truncate">
                  {starFilter.length > 0 
                    ? `${starFilter.length} ${t('search.selected', 'selected')}`
                    : t('search.stars', 'Stars')
                  }
                  </span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-3" align="start">
                <div className="space-y-2">
                  {[5, 4, 3, 2].map(star => (
                    <Button
                      key={star}
                      variant={starFilter.includes(star) ? "default" : "outline"}
                      size="sm"
                      className="w-full h-9 justify-start gap-2"
                      onClick={() => {
                        if (starFilter.includes(star)) {
                          setStarFilter(starFilter.filter(s => s !== star));
                        } else {
                          setStarFilter([...starFilter, star]);
                        }
                      }}
                    >
                      {Array.from({ length: star }).map((_, i) => (
                        <Star key={i} className={`w-3 h-3 ${starFilter.includes(star) ? 'fill-white text-white' : 'fill-yellow-400 text-yellow-400'}`} />
                      ))}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Hotel Type Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant={themeFilter.length > 0 ? "default" : "secondary"} 
                  size="sm" 
                  className="flex-shrink-0 h-9 rounded-full gap-1 max-w-[150px]"
                >
                  <Building2 className="w-3.5 h-3.5" />
                  <span className="truncate">
                  {themeFilter.length > 0 
                    ? `${themeFilter.length} types`
                    : t('search.hotelType', 'Type')
                  }
                  </span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="start">
                <ScrollArea className="h-48">
                  <div className="space-y-1">
                    {[
                      { id: 'family', icon: '👨‍👩‍👧‍👦', label: t('themes.family', 'Family') },
                      { id: 'romantic', icon: '💑', label: t('themes.romantic', 'Romantic') },
                      { id: 'business', icon: '💼', label: t('themes.business', 'Business') },
                      { id: 'beach', icon: '🏖️', label: t('themes.beach', 'Beach') },
                      { id: 'spa', icon: '🧖', label: t('themes.spa', 'Spa & Wellness') },
                      { id: 'budget', icon: '💰', label: t('themes.budget', 'Budget') },
                      { id: 'nightlife', icon: '🌃', label: t('themes.nightlife', 'Nightlife') },
                      { id: 'quiet', icon: '🍃', label: t('themes.quiet', 'Quiet') }
                    ].map(theme => (
                      <Button
                        key={theme.id}
                        variant={themeFilter.includes(theme.id) ? "default" : "ghost"}
                        size="sm"
                        className="w-full h-8 justify-start gap-2 text-sm"
                        onClick={() => {
                          if (themeFilter.includes(theme.id)) {
                            setThemeFilter(themeFilter.filter(t => t !== theme.id));
                          } else {
                            setThemeFilter([...themeFilter, theme.id]);
                          }
                        }}
                      >
                        <span>{theme.icon}</span>
                        {theme.label}
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>

            {/* Free Room with Meals Toggle */}
            <Button 
              variant={mealPackageFilter ? "default" : "secondary"} 
              size="sm" 
              className={`flex-shrink-0 h-9 rounded-full gap-1.5 max-w-[140px] ${mealPackageFilter ? 'bg-[#00A9E0] hover:bg-emerald-600' : ''}`}
              onClick={() => setMealPackageFilter(!mealPackageFilter)}
              data-testid="meal-package-filter"
            >
              <Utensils className="w-3.5 h-3.5" />
              <span className="truncate">{t('hero.freeWithMeals', 'Free Room')}</span>
            </Button>

            {/* Sort Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="secondary" size="sm" className="flex-shrink-0 h-9 rounded-full gap-1 max-w-[120px]">
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  <span className="truncate">{t('search.sort', 'Sort')}</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-2" align="start">
                {[
                  { value: 'recommended', label: t('search.recommended', 'Recommended') },
                  { value: 'price_low', label: t('search.priceAsc', 'Price: Low to High') },
                  { value: 'price_high', label: t('search.priceDesc', 'Price: High to Low') },
                  { value: 'rating', label: t('search.rating', 'Rating') },
                  { value: 'stars', label: t('search.stars', 'Stars') }
                ].map(option => (
                  <Button
                    key={option.value}
                    variant={sortBy === option.value ? "default" : "ghost"}
                    size="sm"
                    className="w-full h-8 justify-start text-sm"
                    onClick={() => setSortBy(option.value)}
                  >
                    {option.label}
                  </Button>
                ))}
              </PopoverContent>
            </Popover>

            {/* Clear Filters - Only show if filters active */}
            {(starFilter.length > 0 || themeFilter.length > 0 || priceRange[0] > 0 || priceRange[1] < 1000 || mealPackageFilter) && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="flex-shrink-0 h-9 text-xs text-muted-foreground"
                onClick={() => {
                  setStarFilter([]);
                  setThemeFilter([]);
                  setAmenityFilter([]);
                  setPriceRange([0, 1000]);
                  setMealPackageFilter(false);
                }}
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />
                {t('search.clear', 'Clear')}
              </Button>
            )}
          </div>
            <div className="absolute right-0 top-0 bottom-2 w-6 bg-gradient-to-l from-background to-transparent pointer-events-none" />
          </div>
          
          {/* Results count for mobile */}
          <div className="text-sm text-muted-foreground">
            {filteredHotels.length} {t('search.hotelsFound', 'hotels found')}
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Enhanced Filter Sidebar - Hidden on Mobile */}
          <aside className="hidden md:block w-80 flex-shrink-0">
           <div className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto scrollbar-thin">
            <Card className="p-6 shadow-lg border-0">
             <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
              <div className="flex items-center justify-between">
                <CollapsibleTrigger asChild>
                  <button className="flex items-center gap-2 group cursor-pointer" data-testid="filters-toggle-btn">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                      <Settings className="w-5 h-5 text-primary" />
                      {t('search.filters')}
                    </h3>
                    <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`} />
                  </button>
                </CollapsibleTrigger>
                {(starFilter.length > 0 || themeFilter.length > 0 || amenityFilter.length > 0 || priceRange[0] > 0 || priceRange[1] < 1000 || mealPackageFilter) && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-xs text-muted-foreground hover:text-[#00A9E0]"
                    onClick={() => {
                      setStarFilter([]);
                      setThemeFilter([]);
                      setAmenityFilter([]);
                      setPriceRange([0, 1000]);
                      setMealPackageFilter(false);
                    }}
                  >
                    {t('search.clearFilters')}
                  </Button>
                )}
              </div>
              
              {/* Active filters count shown when collapsed */}
              {!filtersOpen && (starFilter.length > 0 || themeFilter.length > 0 || amenityFilter.length > 0 || mealPackageFilter || priceRange[0] > 0 || priceRange[1] < 1000) && (
                <div className="mt-3 flex items-center gap-2 text-sm text-primary font-medium">
                  <CheckCircle className="w-4 h-4" />
                  {starFilter.length + themeFilter.length + amenityFilter.length + (mealPackageFilter ? 1 : 0) + (priceRange[0] > 0 || priceRange[1] < 1000 ? 1 : 0)} {t('search.filtersActive', 'filters active')}
                </div>
              )}

              <CollapsibleContent className="mt-6">
              <div className="space-y-5">
                {/* Price Range Filter - Enhanced */}
                <div className="bg-gray-50 dark:bg-[#0a2540] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-sm font-semibold flex items-center gap-2">
                      <Euro className="w-4 h-4 text-primary" />
                      {t('search.price')}
                    </Label>
                    <span className="text-sm font-medium text-primary">
                      €{priceRange[0]} - €{priceRange[1] === 1000 ? '1000+' : priceRange[1]}
                    </span>
                  </div>
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    max={1000}
                    step={25}
                    className="mb-3"
                  />
                  <div className="flex gap-2">
                    {[100, 200, 500].map(price => (
                      <Button
                        key={price}
                        variant={priceRange[1] <= price ? "default" : "outline"}
                        size="sm"
                        className="flex-1 text-xs h-8"
                        onClick={() => setPriceRange([0, price])}
                      >
                        &lt;€{price}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Star Rating Filter - Interactive Stars */}
                <div className="bg-gray-50 dark:bg-[#0a2540] rounded-xl p-4">
                  <Label className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    {t('search.stars')}
                  </Label>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {[5, 4, 3, 2].map(star => (
                      <Button
                        key={star}
                        variant={starFilter.includes(star) ? "default" : "outline"}
                        size="sm"
                        className={`h-10 justify-start gap-1 ${starFilter.includes(star) ? 'bg-primary' : 'hover:bg-primary/10'}`}
                        onClick={() => {
                          if (starFilter.includes(star)) {
                            setStarFilter(starFilter.filter(s => s !== star));
                          } else {
                            setStarFilter([...starFilter, star]);
                          }
                        }}
                      >
                        {Array.from({ length: star }).map((_, i) => (
                          <Star key={i} className={`w-3.5 h-3.5 ${starFilter.includes(star) ? 'fill-white text-white' : 'fill-yellow-400 text-yellow-400'}`} />
                        ))}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Hotel Type Filter - Dropdown with Popup */}
                <div className="bg-gray-50 dark:bg-[#0a2540] rounded-xl p-4">
                  <Label className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary" />
                    {t('search.hotelType', 'Hotel Type')}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between h-10 mt-2">
                        <span className="text-sm">
                          {themeFilter.length === 0 
                            ? t('search.allTypes', 'All types') 
                            : `${themeFilter.length} ${t('search.selected', 'selected')}`}
                        </span>
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-4" align="start">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between pb-2 border-b">
                          <span className="font-semibold text-sm">{t('search.selectHotelTypes', 'Select Hotel Types')}</span>
                          {themeFilter.length > 0 && (
                            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setThemeFilter([])}>
                              {t('search.clear', 'Clear')}
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                          {[
                            { name: 'Beach', icon: '🏖️' },
                            { name: 'City', icon: '🏙️' },
                            { name: 'Spa', icon: '💆' },
                            { name: 'Business', icon: '💼' },
                            { name: 'Family', icon: '👨‍👩‍👧‍👦' },
                            { name: 'Romantic', icon: '💕' },
                            { name: 'Budget', icon: '💰' },
                            { name: 'Luxury', icon: '👑' },
                            { name: 'Boutique', icon: '🎨' },
                            { name: 'Resort', icon: '🌴' },
                            { name: 'Nightlife', icon: '🌃' },
                            { name: 'Quiet', icon: '🍃' }
                          ].map(theme => (
                            <Button
                              key={theme.name}
                              variant={themeFilter.includes(theme.name) ? "default" : "outline"}
                              size="sm"
                              className={`justify-start gap-2 h-9 ${themeFilter.includes(theme.name) ? '' : 'hover:bg-primary/10'}`}
                              onClick={() => {
                                if (themeFilter.includes(theme.name)) {
                                  setThemeFilter(themeFilter.filter(t => t !== theme.name));
                                } else {
                                  setThemeFilter([...themeFilter, theme.name]);
                                }
                              }}
                            >
                              <span>{theme.icon}</span>
                              <span className="text-xs">{t(`themes.${theme.name.toLowerCase()}`, theme.name)}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {themeFilter.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {themeFilter.map(theme => (
                        <Badge 
                          key={theme} 
                          variant="secondary" 
                          className="text-xs cursor-pointer hover:bg-destructive hover:text-white transition-colors"
                          onClick={() => setThemeFilter(themeFilter.filter(t => t !== theme))}
                        >
                          {t(`themes.${String(theme).toLowerCase()}`, theme)} <X className="w-3 h-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Amenities Filter - Dropdown with Popup */}
                <div className="bg-gray-50 dark:bg-[#0a2540] rounded-xl p-4">
                  <Label className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    {t('search.amenities', 'Amenities')}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between h-10 mt-2">
                        <span className="text-sm">
                          {amenityFilter.length === 0 
                            ? t('search.anyAmenities', 'Any amenities') 
                            : `${amenityFilter.length} ${t('search.selected', 'selected')}`}
                        </span>
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-4" align="start">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between pb-2 border-b">
                          <span className="font-semibold text-sm">{t('search.mustHaveAmenities', 'Must-have Amenities')}</span>
                          {amenityFilter.length > 0 && (
                            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setAmenityFilter([])}>
                              {t('search.clear', 'Clear')}
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                          {[
                            { name: 'WiFi', icon: <Wifi className="w-4 h-4" /> },
                            { name: 'Pool', icon: '🏊' },
                            { name: 'Parking', icon: <Car className="w-4 h-4" /> },
                            { name: 'Restaurant', icon: <UtensilsCrossed className="w-4 h-4" /> },
                            { name: 'Gym', icon: '🏋️' },
                            { name: 'Spa', icon: '💆' },
                            { name: 'Air Conditioning', icon: '❄️' },
                            { name: 'Pet Friendly', icon: '🐕' },
                            { name: 'Bar', icon: '🍸' },
                            { name: 'Room Service', icon: '🛎️' }
                          ].map(amenity => (
                            <Button
                              key={amenity.name}
                              variant={amenityFilter.includes(amenity.name) ? "default" : "outline"}
                              size="sm"
                              className={`justify-start gap-2 h-9 ${amenityFilter.includes(amenity.name) ? '' : 'hover:bg-primary/10'}`}
                              onClick={() => {
                                if (amenityFilter.includes(amenity.name)) {
                                  setAmenityFilter(amenityFilter.filter(a => a !== amenity.name));
                                } else {
                                  setAmenityFilter([...amenityFilter, amenity.name]);
                                }
                              }}
                            >
                              <span className="text-sm">{typeof amenity.icon === 'string' ? amenity.icon : amenity.icon}</span>
                              <span className="text-xs">{amenity.name}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {amenityFilter.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {amenityFilter.map(amenity => (
                        <Badge 
                          key={amenity} 
                          variant="secondary" 
                          className="text-xs cursor-pointer hover:bg-destructive hover:text-white transition-colors"
                          onClick={() => setAmenityFilter(amenityFilter.filter(a => a !== amenity))}
                        >
                          {amenity} <X className="w-3 h-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* FreeStays Pass Filters - Special Section */}
                <div className="bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-950/30 dark:to-blue-950/30 rounded-xl p-4 border border-emerald-200 dark:border-emerald-800">
                  <Label className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-[#00A9E0]" />
                    {t('hero.passEligible', 'FreeStays Pass Eligible')}
                  </Label>
                  <div className="space-y-2 mt-3">
                    {/* Free Room with Meals Toggle */}
                    <Button 
                      variant={mealPackageFilter ? "default" : "outline"} 
                      size="sm" 
                      className={`w-full justify-start gap-2 h-10 ${mealPackageFilter ? 'bg-[#00A9E0] hover:bg-emerald-600 border-[#00A9E0]' : 'hover:bg-emerald-50 hover:border-emerald-300'}`}
                      onClick={() => setMealPackageFilter(!mealPackageFilter)}
                      data-testid="sidebar-meal-package-filter"
                    >
                      <Utensils className={`w-4 h-4 ${mealPackageFilter ? 'text-white' : 'text-[#00A9E0]'}`} />
                      <span className="text-xs">{t('hero.freeWithMeals', 'Free Room with Meals')}</span>
                    </Button>
                  </div>
                </div>

                {/* Active Filters Summary */}
                {(starFilter.length > 0 || themeFilter.length > 0 || amenityFilter.length > 0 || mealPackageFilter) && (
                  <div className="bg-[#00A9E0]/5 rounded-xl p-4 border border-[#00A9E0]/20">
                    <div className="flex items-center gap-2 text-sm text-primary font-medium">
                      <CheckCircle className="w-4 h-4" />
                      {starFilter.length + themeFilter.length + amenityFilter.length + (mealPackageFilter ? 1 : 0)} filters active
                    </div>
                  </div>
                )}
              </div>
              </CollapsibleContent>
             </Collapsible>
            </Card>
            
            {/* Mini Map */}
            {filteredHotels.length > 0 && (
              <div className="mt-4" data-testid="sidebar-mini-map">
                <HotelMap
                  hotels={filteredHotels.slice(0, 50)}
                  height="220px"
                  showInfoWindows={true}
                  selectedHotelId={mapHoveredHotelId || mapSelectedHotelId}
                  onHotelSelect={handleMapHotelSelect}
                  onHotelHover={setMapHoveredHotelId}
                  className="shadow-lg"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-2 gap-2"
                  onClick={() => {
                    sessionStorage.setItem('searchMapHotels', JSON.stringify({
                      hotels: filteredHotels,
                      nearbyHotels: []
                    }));
                    const params = new URLSearchParams(window.location.search);
                    navigate(`/search/map?${params.toString()}`);
                  }}
                  data-testid="sidebar-open-map-btn"
                >
                  <MapPin className="w-4 h-4" />
                  {t('search.viewOnMap', 'View Location on Map')}
                </Button>
              </div>
            )}
           </div>
          </aside>

          <main className="flex-1">
            {/* Nearby Map View - shows when searched via geolocation */}
            {showMapView && !isLoading && filteredHotels.length > 0 ? (
              <div data-testid="nearby-map-view">
                {/* Header with back button */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h1 className="font-bold text-2xl">
                      {destination ? `Hotels in ${destination}` : t('search.searchTitle', 'Search Results')}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      {filteredHotels.length} {t('search.hotelsFoundText', 'hotels found')} • {nights} {nights !== 1 ? t('search.nights') : t('search.night')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 shadow-sm"
                    onClick={() => setShowMapView(false)}
                    data-testid="back-to-list-btn"
                  >
                    <List className="w-4 h-4" />
                    {t('search.backToNormalList', 'Back to hotel list')}
                  </Button>
                </div>

                {/* Map */}
                <div className="mb-6">
                  <HotelMap
                    hotels={filteredHotels.slice(0, 80)}
                    height="450px"
                    showInfoWindows={true}
                    selectedHotelId={mapHoveredHotelId || mapSelectedHotelId}
                    onHotelSelect={handleMapHotelSelect}
                    onHotelHover={setMapHoveredHotelId}
                    userLocation={userLat && userLng ? { lat: userLat, lng: userLng } : null}
                    className="shadow-lg"
                  />
                </div>

                {/* Hotel list below map */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-muted-foreground">{t('search.sortBy')}:</span>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[200px] bg-white text-gray-900 shadow-sm" data-testid="map-sort-select">
                      <SelectValue placeholder={t('search.sortBy')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recommended"><span className="flex items-center gap-2"><Sparkles className="w-4 h-4" /> {t('search.recommended', 'Recommended')}</span></SelectItem>
                      <SelectItem value="price_low"><span className="flex items-center gap-2"><ArrowRight className="w-4 h-4 rotate-[-90deg]" /> {t('search.priceAsc', 'Price: Low to High')}</span></SelectItem>
                      <SelectItem value="price_high"><span className="flex items-center gap-2"><ArrowRight className="w-4 h-4 rotate-90" /> {t('search.priceDesc', 'Price: High to Low')}</span></SelectItem>
                      <SelectItem value="rating"><span className="flex items-center gap-2"><Star className="w-4 h-4" /> {t('search.rating')}</span></SelectItem>
                      <SelectItem value="stars"><span className="flex items-center gap-2"><Crown className="w-4 h-4" /> {t('search.stars')}</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-6">
                  {paginatedHotels.map(hotel => (
                    <div
                      key={hotel.hotel_id}
                      ref={el => { hotelCardRefs.current[hotel.hotel_id] = el; }}
                      className={`transition-all duration-200 rounded-xl ${
                        (mapHoveredHotelId === hotel.hotel_id || mapSelectedHotelId === hotel.hotel_id)
                          ? 'ring-2 ring-primary shadow-lg scale-[1.01]'
                          : ''
                      }`}
                      onMouseEnter={() => setMapHoveredHotelId(hotel.hotel_id)}
                      onMouseLeave={() => setMapHoveredHotelId(null)}
                    >
                      <HotelCard 
                        hotel={hotel} 
                        nights={nights}
                        checkIn={format(checkIn, 'yyyy-MM-dd')}
                        checkOut={format(checkOut, 'yyyy-MM-dd')}
                        guests={guests}
                        childrenAges={childrenAges}
                        socialProof={socialProofMap[String(hotel.hotel_id)]}
                        allHotels={filteredHotels}
                        destination={destination}
                        destinationId={destinationId}
                      />
                    </div>
                  ))}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 pt-6 border-t">
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-muted-foreground px-3">
                        {currentPage} / {totalPages}
                      </span>
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
            <>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
              <div>
                <h1 className="font-bold text-2xl">
                  {destination ? `Hotels in ${destination}` : t('search.searchTitle', 'Search Results')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('search.searching', 'Searching...')}
                    </span>
                  ) : (
                    <>{filteredHotels.length} {t('search.hotelsFoundText', 'hotels found')} • {nights} {nights !== 1 ? t('search.nights') : t('search.night')}</>
                  )}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* Show map view button (toggle) */}
                {isNearbySearch && filteredHotels.length > 0 && !isLoading && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 shadow-sm border-primary/30 text-primary"
                    onClick={() => setShowMapView(true)}
                    data-testid="show-map-view-btn"
                  >
                    <Map className="w-4 h-4" />
                    {t('search.showOnMap', 'Show on map')}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 shadow-sm"
                  onClick={() => {
                    sessionStorage.setItem('searchMapHotels', JSON.stringify({
                      hotels: filteredHotels,
                      nearbyHotels: []
                    }));
                    const params = new URLSearchParams(window.location.search);
                    navigate(`/search/map?${params.toString()}`);
                  }}
                  data-testid="open-map-btn"
                >
                  <MapPin className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('search.viewOnMap', 'View Location on Map')}</span>
                </Button>
                <span className="text-sm text-muted-foreground hidden sm:inline">{t('search.sortBy')}:</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[200px] bg-white text-gray-900 shadow-sm" data-testid="sort-select">
                    <SelectValue placeholder={t('search.sortBy')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recommended">
                      <span className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4" /> {t('search.recommended', 'Recommended')}
                      </span>
                    </SelectItem>
                    <SelectItem value="price_low">
                      <span className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 rotate-[-90deg]" /> {t('search.priceAsc', 'Price: Low to High')}
                      </span>
                    </SelectItem>
                    <SelectItem value="price_high">
                      <span className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 rotate-90" /> {t('search.priceDesc', 'Price: High to Low')}
                      </span>
                    </SelectItem>
                    <SelectItem value="rating">
                      <span className="flex items-center gap-2">
                        <Star className="w-4 h-4" /> {t('search.rating')}
                      </span>
                    </SelectItem>
                    <SelectItem value="stars">
                      <span className="flex items-center gap-2">
                        <Crown className="w-4 h-4" /> {t('search.stars')}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {isLoading ? (
              <div className="space-y-6">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      <div className="w-full md:w-72 h-48 skeleton" />
                      <div className="flex-1 p-6 space-y-4">
                        <div className="h-6 w-48 skeleton rounded" />
                        <div className="h-4 w-32 skeleton rounded" />
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : paginatedHotels.length > 0 ? (
              <div className="space-y-6">
                {/* Phase 4: Hotel Network Activation Banner */}
                {currentPage === 1 && (
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary via-primary/90 to-accent p-4 md:p-6 text-[#081D33] shadow-xl">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
                    <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 min-w-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="text-center px-3 py-2 bg-[#081D33]/10 backdrop-blur-sm rounded-xl flex-shrink-0">
                          <p className="text-xl md:text-2xl font-bold leading-none">30%</p>
                          <p className="text-[10px] md:text-xs text-[#081D33]/60 mt-0.5">{t('dashboard.avgSavings', 'Avg. Savings')}</p>
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-base md:text-xl mb-0.5 leading-snug">{t('search.networkBanner', 'Join 400,000+ hotels in the FreeStays Network')}</h3>
                          <p className="text-xs md:text-sm text-[#081D33]/70 hidden md:block">{t('search.networkBannerSub', 'Hotels offering free rooms with meal packages')}</p>
                        </div>
                      </div>
                      <Button 
                        variant="secondary" 
                        className="rounded-full bg-white text-primary hover:bg-white/90 w-full md:w-auto h-11 md:h-10 flex-shrink-0"
                        onClick={() => setMealPackageFilter(true)}
                      >
                        <Utensils className="w-4 h-4 mr-2" />
                        {t('dashboard.findMealPackages', 'Find Meal Package Hotels')}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Map View Banner - Prominent CTA */}
                {filteredHotels.length > 0 && (
                  <div
                    className="group cursor-pointer rounded-2xl border-2 border-[#00A9E0]/20 bg-gradient-to-r from-blue-50 to-sky-50 dark:from-blue-950/40 dark:to-sky-950/40 p-4 flex items-center justify-between gap-4 hover:border-primary/50 hover:shadow-lg transition-all duration-200"
                    onClick={() => {
                      sessionStorage.setItem('searchMapHotels', JSON.stringify({
                        hotels: filteredHotels,
                        nearbyHotels: []
                      }));
                      const params = new URLSearchParams(window.location.search);
                      navigate(`/search/map?${params.toString()}`);
                    }}
                    data-testid="map-banner-cta"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                        <MapPin className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{t('search.viewOnMap', 'View Location on Map')}</p>
                        <p className="text-xs text-muted-foreground">{filteredHotels.length} {t('search.hotelsOnMap', 'hotels on map')} — {t('search.mapCta', 'see prices & locations')}</p>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-primary flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                  </div>
                )}
                
                {paginatedHotels.map(hotel => (
                  <div
                    key={hotel.hotel_id}
                    ref={el => { hotelCardRefs.current[hotel.hotel_id] = el; }}
                    className={`transition-all duration-200 rounded-xl ${
                      (mapHoveredHotelId === hotel.hotel_id || mapSelectedHotelId === hotel.hotel_id)
                        ? 'ring-2 ring-primary shadow-lg scale-[1.01]'
                        : ''
                    }`}
                    onMouseEnter={() => setMapHoveredHotelId(hotel.hotel_id)}
                    onMouseLeave={() => setMapHoveredHotelId(null)}
                  >
                    <HotelCard 
                      hotel={hotel} 
                      nights={nights}
                      checkIn={format(checkIn, 'yyyy-MM-dd')}
                      checkOut={format(checkOut, 'yyyy-MM-dd')}
                      guests={guests}
                      childrenAges={childrenAges}
                      socialProof={socialProofMap[String(hotel.hotel_id)]}
                      allHotels={filteredHotels}
                      destination={destination}
                      destinationId={destinationId}
                    />
                  </div>
                ))}
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {startIndex + 1}-{Math.min(endIndex, totalHotels)} of {totalHotels} hotels
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="hidden sm:flex"
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span className="hidden sm:inline ml-1">Previous</span>
                      </Button>
                      
                      <div className="flex items-center gap-1">
                        {/* Page numbers */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              className="w-9 h-9"
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        <span className="hidden sm:inline mr-1">Next</span>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="hidden sm:flex"
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-bold text-xl mb-2">{t('search.noResults')}</h3>
                <p className="text-muted-foreground mb-6">{t('search.tryDifferent')}</p>
                <Button variant="outline" onClick={() => { setPriceRange([0, 1000]); setStarFilter([]); }}>
                  {t('search.clearFilters')}
                </Button>
              </Card>
            )}
            </>
            )}
          </main>
        </div>
      </div>

      {/* Email Capture Popup for Non-logged-in Users */}
      <Dialog open={showEmailCapture} onOpenChange={setShowEmailCapture}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center font-sans font-bold text-2xl">
              {t('priceComparison.title', 'Save Your Price Comparison')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('priceComparison.subtitle', 'Get these deals delivered to your inbox!')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Savings Summary */}
            {searchResults?.comparison_data && (
              <div className="bg-accent/10 rounded-xl p-4 text-center border border-accent/30">
                <p className="text-sm text-muted-foreground mb-2">{t('priceComparison.averageSavings', 'See how much you can save on average per night with Freestays')}</p>
                <p className="text-3xl font-bold text-accent">€{(searchResults.comparison_data.total_savings / Math.max(searchResults.comparison_data.hotels_with_savings, 1))?.toFixed(0)}</p>
                <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
                  <p>• €{(searchResults.comparison_data.total_savings / Math.max(searchResults.comparison_data.hotels_with_savings, 1))?.toFixed(0)} {t('priceComparison.perRoom', 'per room')}</p>
                  <p>• {searchResults.comparison_data.hotels_with_savings} {t('priceComparison.availableHotels', 'available hotels in')} {searchResults.comparison_data.destination} {t('priceComparison.basedOnSelection', 'based on your selection')}</p>
                </div>
              </div>
            )}

            {!emailCaptureSubmitted ? (
              <form onSubmit={handleEmailCapture} className="space-y-4">
                <div>
                  <Label>{t('priceComparison.getEmailAlerts')}</Label>
                  <Input 
                    type="email"
                    placeholder={t('priceComparison.emailPlaceholder')}
                    value={captureEmail}
                    onChange={(e) => setCaptureEmail(e.target.value)}
                    className="mt-2"
                    required
                    data-testid="email-capture-input"
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" data-testid="email-capture-submit">
                  <Mail className="w-4 h-4 mr-2" />
                  {t('priceComparison.subscribe')}
                </Button>
              </form>
            ) : (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-primary mx-auto mb-3" />
                <p className="font-semibold text-primary">{t('priceComparison.subscribed')}</p>
              </div>
            )}

            <div className="border-t pt-4">
              <p className="text-sm text-center text-muted-foreground mb-3">
                {t('priceComparison.registerPrompt')}
              </p>
              <div className="flex gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="flex-1 rounded-full" data-testid="register-from-popup">
                      <UserPlus className="w-4 h-4 mr-2" />
                      {t('priceComparison.registerNow')}
                    </Button>
                  </DialogTrigger>
                  <AuthDialog defaultTab="register" />
                </Dialog>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" className="flex-1 rounded-full">
                      {t('priceComparison.login')}
                    </Button>
                  </DialogTrigger>
                  <AuthDialog defaultTab="login" />
                </Dialog>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== TRIPADVISOR BADGE COMPONENT ====================
const TripAdvisorBadge = ({ rating, reviewCount, tripAdvisorUrl, compact = false }) => {
  const { t } = useTranslation();
  const fullBubbles = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  
  const handleClick = (e) => {
    e.stopPropagation();
    if (tripAdvisorUrl) {
      window.open(tripAdvisorUrl, '_blank', 'noopener,noreferrer');
    }
  };
  
  // Official TripAdvisor Owl Logo
  const TripAdvisorOwl = ({ className }) => (
    <svg viewBox="0 0 32 32" className={className} xmlns="http://www.w3.org/2000/svg">
      {/* Owl body/head */}
      <ellipse cx="16" cy="18" rx="14" ry="12" fill="#00AA6C"/>
      {/* Left eye white */}
      <circle cx="10" cy="16" r="6" fill="white"/>
      {/* Right eye white */}
      <circle cx="22" cy="16" r="6" fill="white"/>
      {/* Left eye black */}
      <circle cx="10" cy="16" r="3.5" fill="#333"/>
      {/* Right eye black */}
      <circle cx="22" cy="16" r="3.5" fill="#333"/>
      {/* Left eye pupil */}
      <circle cx="10" cy="16" r="1.5" fill="white"/>
      {/* Right eye pupil */}
      <circle cx="22" cy="16" r="1.5" fill="white"/>
      {/* Beak */}
      <path d="M16 19 L14 23 L16 22 L18 23 Z" fill="#FF9500"/>
      {/* Left ear */}
      <path d="M4 10 Q6 6 10 8 Q8 12 4 10" fill="#00AA6C"/>
      {/* Right ear */}
      <path d="M28 10 Q26 6 22 8 Q24 12 28 10" fill="#00AA6C"/>
    </svg>
  );
  
  if (compact) {
    return (
      <button 
        onClick={handleClick}
        className="flex items-center gap-1.5 text-xs bg-white/90 hover:bg-white px-2 py-1 rounded-full shadow-sm transition-colors cursor-pointer"
        title="View on TripAdvisor"
      >
        <TripAdvisorOwl className="w-5 h-5" />
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <div 
              key={i} 
              className={`w-2 h-2 rounded-full ${
                i <= fullBubbles 
                  ? 'bg-[#00AA6C]' 
                  : i === fullBubbles + 1 && hasHalf 
                    ? 'bg-gradient-to-r from-[#00AA6C] to-gray-300' 
                    : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </button>
    );
  }
  
  return (
    <button 
      onClick={handleClick}
      className="flex items-center gap-2 text-xs hover:bg-secondary/50 px-2 py-1.5 rounded-lg transition-colors cursor-pointer"
      title="View on TripAdvisor"
    >
      {/* TripAdvisor Owl Logo - Larger size */}
      <TripAdvisorOwl className="w-8 h-8 flex-shrink-0" />
      <div className="flex flex-col">
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <div 
              key={i} 
              className={`w-3 h-3 rounded-full ${
                i <= fullBubbles 
                  ? 'bg-[#00AA6C]' 
                  : i === fullBubbles + 1 && hasHalf 
                    ? 'bg-gradient-to-r from-[#00AA6C] to-gray-300' 
                    : 'bg-gray-300'
              }`}
            />
          ))}
          <span className="text-sm font-medium ml-1">{rating.toFixed(1)}</span>
        </div>
        {reviewCount && (
          <span className="text-[11px] text-muted-foreground">{reviewCount.toLocaleString()} {t('hotel.reviews', 'reviews')}</span>
        )}
      </div>
    </button>
  );
};

// ==================== HOTEL CARD ====================
const HotelCard = ({ hotel, nights, checkIn, checkOut, guests, childrenAges = [], socialProof = null, allHotels = [], destination = '', destinationId = '' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasValidPass, user, token } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [tripAdvisorData, setTripAdvisorData] = useState(null);
  
  const validPass = hasValidPass?.() || false;
  const pricing = calculatePricing(hotel.min_price, validPass); // Sunhotels price is already total for all nights

  // Fetch TripAdvisor data for this hotel (on-demand with caching)
  useEffect(() => {
    if (hotel.hotel_id && hotel.name) {
      // Build query params for on-demand fetching
      const params = new URLSearchParams({
        hotel_name: hotel.name,
        city: hotel.city || hotel.destination || '',
        country: hotel.country || ''
      });
      
      axios.get(`${API}/hotels/${hotel.hotel_id}/tripadvisor?${params}`)
        .then(res => {
          if (res.data.has_data) {
            setTripAdvisorData(res.data);
          }
        })
        .catch(() => {});
    }
  }, [hotel.hotel_id, hotel.name]);

  // Function to navigate to hotel detail and store nearby hotels for the sidebar
  const goToHotelDetail = () => {
    // Store other hotels from search results for "Hotels nearby" section
    const otherHotels = allHotels.filter(h => h.hotel_id !== hotel.hotel_id);
    sessionStorage.setItem('nearbyHotelsFromSearch', JSON.stringify(otherHotels.slice(0, 10)));
    
    // Build URL with all search params for breadcrumb navigation back
    const destParam = destination ? `&destination=${encodeURIComponent(destination)}` : '';
    const destIdParam = (hotel.dest_id || destinationId) ? `&destinationId=${hotel.dest_id || destinationId}` : '';
    const agesParam = childrenAges && childrenAges.length > 0 ? `&childrenAges=${childrenAges.join(',')}` : '';
    navigate(`/hotel/${hotel.hotel_id}?checkIn=${checkIn}&checkOut=${checkOut}&adults=${guests.adults}&children=${guests.children}${agesParam}${destParam}${destIdParam}`);
  };

  // Check if hotel is favorited
  useEffect(() => {
    if (user && token) {
      axios.get(`${API}/favorites/check/${hotel.hotel_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setIsFavorite(res.data.is_favorite)).catch(() => {});
    }
  }, [hotel.hotel_id, user, token]);

  const toggleFavorite = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.error("Please sign in to save favorites");
      return;
    }
    
    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${hotel.hotel_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
        toast.success("Removed from favorites");
      } else {
        await axios.post(`${API}/favorites`, {
          hotel_id: hotel.hotel_id,
          hotel_name: hotel.name,
          star_rating: hotel.star_rating,
          image_url: hotel.image_url,
          location: `${hotel.city}, ${hotel.country}`,
          min_price: hotel.min_price
        }, { headers: { Authorization: `Bearer ${token}` } });
        setIsFavorite(true);
        toast.success("Added to favorites!");
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Failed to update favorites: ${detail}`);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Theme to style mapping (no emojis as per guidelines)
  const themeStyles = {
    'Beach': { icon: Waves, bg: 'bg-blue-50 text-blue-700 border-blue-200' },
    'City': { icon: Building2, bg: 'bg-slate-50 text-slate-700 border-slate-200' },
    'Spa': { icon: Heart, bg: 'bg-pink-50 text-pink-700 border-pink-200' },
    'Business': { icon: Briefcase, bg: 'bg-gray-50 text-gray-700 border-gray-200' },
    'Family': { icon: Users, bg: 'bg-[#00A9E0]/10 text-[#00A9E0] border-[#00A9E0]/30' },
    'Romantic': { icon: Heart, bg: 'bg-rose-50 text-rose-700 border-rose-200' },
    'Budget': { icon: Tag, bg: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    'Luxury': { icon: Crown, bg: 'bg-amber-50 text-amber-700 border-amber-200' },
    'Boutique': { icon: Sparkles, bg: 'bg-purple-50 text-purple-700 border-purple-200' },
    'Resort': { icon: TreePine, bg: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    'Nightlife': { icon: Music, bg: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200' },
    'Quiet': { icon: Leaf, bg: 'bg-teal-50 text-teal-700 border-teal-200' }
  };

  const getThemeStyle = (theme) => {
    const lowerTheme = theme.toLowerCase();
    for (const [key, style] of Object.entries(themeStyles)) {
      if (lowerTheme.includes(key.toLowerCase())) {
        return style;
      }
    }
    return { icon: Building2, bg: 'bg-secondary text-secondary-foreground border-border' };
  };

  return (
    <Card
      className="overflow-hidden rounded-2xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
      data-testid={`hotel-card-${hotel.hotel_id}`}
      onClick={goToHotelDetail}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToHotelDetail();
        }
      }}
    >
      <div className="flex flex-col lg:flex-row">
        {/* Image Section */}
        <div className="relative w-full lg:w-80 h-56 lg:h-auto flex-shrink-0 overflow-hidden bg-secondary">
          <img 
            src={proxyImage(hotel.image_url || hotel.images?.[0], 640, 78)} 
            alt={hotel.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
              // Hide broken image, show placeholder background
              e.target.style.display = 'none';
            }}
            loading="lazy"
            decoding="async"
          />
          {/* Fallback placeholder when no image */}
          {!hotel.image_url && !hotel.images?.[0] && (
            <div className="absolute inset-0 flex items-center justify-center bg-secondary">
              <Hotel className="w-12 h-12 text-muted-foreground/30" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          
          {/* Favorite Button */}
          <button
            onClick={(e) => { e.stopPropagation(); toggleFavorite(e); }}
            disabled={favoriteLoading}
            className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              isFavorite 
                ? 'bg-red-500 text-white shadow-lg scale-110' 
                : 'bg-white/90 text-gray-600 hover:bg-red-50 hover:text-red-500 shadow-md'
            } ${favoriteLoading ? 'opacity-50' : ''}`}
            data-testid={`favorite-btn-${hotel.hotel_id}`}
          >
            <Heart className={`w-5 h-5 transition-transform ${isFavorite ? 'fill-current scale-110' : ''}`} />
          </button>
          
          {/* Badges Container */}
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
            {/* Row 1: Savings badges */}
            <div className="flex items-start gap-2">
              {hotel.is_last_minute && (
                <Badge className="bg-red-500 text-white font-semibold shadow-lg animate-pulse">
                  <Zap className="w-3.5 h-3.5 mr-1" /> Last Minute
                </Badge>
              )}
              
              {validPass ? (
                <Badge className="bg-[#00A9E0] text-white font-semibold shadow-lg animate-pulse-glow">
                  <Gift className="w-3.5 h-3.5 mr-1" />
                  {t('search.save', 'Save')} €{pricing.discountAmount.toFixed(0)}
                </Badge>
              ) : (
                <Badge className="bg-white/90 text-[#00A9E0] backdrop-blur-sm shadow-lg group-hover:bg-[#00A9E0] group-hover:text-white transition-all">
                  <Sparkles className="w-3.5 h-3.5 mr-1" />
                  {t('search.save', 'Save')} €{pricing.potentialSavings.toFixed(0)}
                </Badge>
              )}
              
              {/* Best Value badge - Phase 4 */}
              {pricing.potentialSavings > 50 && (
                <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold shadow-lg">
                  <Award className="w-3.5 h-3.5 mr-1" />
                  {t('search.bestValue', 'Best Value')}
                </Badge>
              )}
            </div>
            
            {/* Row 2: FreeStays Pass badges */}
            <div className="flex items-start gap-2 flex-wrap max-w-[70%]">
              {/* Free Room with Meal Package badge - shows when hotel has meal plans */}
              {(hotel.has_meal_packages || hotel.board_types?.some(bt => bt.toLowerCase() !== 'room only' && bt.toLowerCase() !== 'self catering')) && (
                <Badge className="bg-[#00A9E0] text-white font-semibold shadow-lg">
                  <Utensils className="w-3.5 h-3.5 mr-1" />
                  {t('hero.freeWithMeals', 'Free Room with Meals')}
                </Badge>
              )}
              
              {/* Transfer Available badge */}
              {hotel.has_transfers && (
                <Badge className="bg-blue-500 text-white font-semibold shadow-lg">
                  <Car className="w-3.5 h-3.5 mr-1" />
                  {t('hero.transferTeaser', 'Transfer Options')}
                </Badge>
              )}
            </div>
          </div>

          {/* Social Proof Badges - Top Right, vertical stack */}
          {(socialProof?.viewers_live || socialProof?.viewers_24h || socialProof?.bookings_24h || socialProof?.views_7d) && (
            <div className="absolute top-3 right-14 flex flex-col items-end gap-1.5 max-w-[55%] z-10" data-testid={`social-proof-${hotel.hotel_id}`}>
              {/* Live viewers (last 30 min) - highest priority */}
              {socialProof?.viewers_live ? (
                <Badge className="bg-orange-500 text-white font-semibold shadow-lg animate-pulse" data-testid={`viewers-live-${hotel.hotel_id}`}>
                  <Flame className="w-3.5 h-3.5 mr-1" />
                  {t('search.viewersLiveBadge', '{{n}} viewing now').replace('{{n}}', socialProof.viewers_live)}
                </Badge>
              ) : socialProof?.viewers_24h && (
                <Badge className="bg-orange-500 text-white font-semibold shadow-lg" data-testid={`viewers-${hotel.hotel_id}`}>
                  <Flame className="w-3.5 h-3.5 mr-1" />
                  {t('search.viewersBadge', '{{n}} viewing').replace('{{n}}', socialProof.viewers_24h)}
                </Badge>
              )}

              {/* Bookings in last 24h */}
              {socialProof?.bookings_24h && (
                <Badge className="bg-emerald-600 text-white font-semibold shadow-lg" data-testid={`booked-${hotel.hotel_id}`}>
                  <CheckCircle className="w-3.5 h-3.5 mr-1" />
                  {t('search.bookedBadge', 'Booked {{n}}× today').replace('{{n}}', socialProof.bookings_24h)}
                </Badge>
              )}

              {/* Fallback: total views in last 7 days (for hotels without live activity) */}
              {!socialProof?.viewers_live && !socialProof?.viewers_24h && !socialProof?.bookings_24h && socialProof?.views_7d && (
                <Badge className="bg-slate-700/90 text-white font-medium shadow-lg backdrop-blur-sm" data-testid={`views-7d-${hotel.hotel_id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1" />
                  {t('search.views7dBadge', '{{n}} views this week').replace('{{n}}', socialProof.views_7d)}
                </Badge>
              )}
            </div>
          )}
          
          {/* FreeStays Partner badge - Bottom right, doesn't overlap with favorite button */}
          {(hotel.is_partner || hotel.star_rating >= 4) && (
            <Badge className="absolute bottom-3 right-3 bg-gradient-to-r from-primary to-primary/80 text-white font-semibold shadow-lg border border-white/20">
              <Shield className="w-3.5 h-3.5 mr-1" />
              {t('search.partnerHotel', 'FreeStays Partner')}
            </Badge>
          )}
          
          {/* Guest Count Badge */}
          <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 shadow-lg">
            <Users className="w-3.5 h-3.5" />
            {guests.adults} {t('search.adults', 'Adults')}{guests.children > 0 && `, ${guests.children} ${t('search.children', 'Children')}`}
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 p-5 lg:p-6">
          <div className="flex flex-col h-full">
            <div className="flex-1">
              {/* Stars & TripAdvisor Row - Above Hotel Name */}
              <div className="flex items-center justify-between mb-2">
                {/* Hotel Stars - Left */}
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: Math.floor(hotel.star_rating) }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                {/* TripAdvisor Badge - Right */}
                {tripAdvisorData && tripAdvisorData.rating && (
                  <TripAdvisorBadge 
                    rating={tripAdvisorData.rating}
                    reviewCount={tripAdvisorData.review_count}
                    tripAdvisorUrl={tripAdvisorData.tripadvisor_direct_url || tripAdvisorData.tripadvisor_url}
                    compact={false}
                  />
                )}
              </div>

              {/* Header */}
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <h3 
                    className="font-bold text-xl hover:text-[#00A9E0] cursor-pointer transition-colors line-clamp-1" 
                    onClick={goToHotelDetail}
                  >
                    {hotel.name}
                  </h3>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {hotel.city_localized || hotel.city}, {hotel.country_localized || hotel.country}
                  </div>
                </div>
              </div>

              {/* Hotel Themes - With Icons */}
              {hotel.themes && hotel.themes.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {[...new Set(hotel.themes.map(t => t.toLowerCase()))].map((themeLower, idx) => {
                    const theme = hotel.themes.find(t => t.toLowerCase() === themeLower) || themeLower;
                    const style = getThemeStyle(theme);
                    const IconComponent = style.icon;
                    const label = t(`themes.${theme.toLowerCase()}`, theme);
                    return (
                      <Badge key={`theme-${idx}`} variant="outline" className={`text-xs ${style.bg} font-medium`}>
                        <IconComponent className="w-3 h-3 mr-1" />
                        {label}
                      </Badge>
                    );
                  })}
                </div>
              )}

              {/* Amenities */}
              <div className="flex flex-wrap gap-1.5 mb-3">
                {[...new Set(hotel.amenities?.map(a => a.toLowerCase()))].slice(0, 4).map((amenityLower, idx) => {
                  const amenity = hotel.amenities?.find(a => a.toLowerCase() === amenityLower) || amenityLower;
                  const translatedAmenity = t(`amenities.${amenity}`, amenity);
                  return (
                    <span key={`${amenity}-${idx}`} className="text-xs text-muted-foreground bg-secondary/50 px-2 py-1 rounded-md">
                      {translatedAmenity}
                    </span>
                  );
                })}
                {hotel.amenities?.length > 4 && (
                  <span className="text-xs text-primary font-medium px-2 py-1">
                    +{hotel.amenities.length - 4} {t('search.more', 'more')}
                  </span>
                )}
              </div>

              {/* Distances */}
              {hotel.distances && hotel.distances.length > 0 && (
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {hotel.distances.slice(0, 2).map((dist, idx) => (
                    <span key={idx} className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-primary" />
                      {dist.name || dist.type}: {dist.distance || dist.value}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Price & CTA */}
            <div className="flex items-end justify-between pt-4 mt-4 border-t border-border/50">
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {nights} {nights === 1 ? t('search.night', 'night') : t('search.nights', 'nights')}, {' '}
                  {(guests.adults || 0) + (guests.children || 0)}{' '}
                  {((guests.adults || 0) + (guests.children || 0)) === 1 ? t('search.guest', 'guest') : t('search.guests', 'guests')}
                </p>
                
                {/* Price Comparison Badge (PRICE CHECK only - savings text moved to right column) */}
                {hotel.price_comparison?.show_comparison && (
                  <div className="mb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-primary/20 text-primary border-primary/30 text-xs font-semibold">
                        <Check className="w-3 h-3 mr-1" />
                        {t('search.priceCheck', 'PRICE CHECK')}
                      </Badge>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button type="button" className="inline-flex">
                            <Info className="w-3.5 h-3.5 text-muted-foreground hover:text-[#00A9E0] cursor-help" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent side="top" className="max-w-xs p-3">
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex items-start gap-2">
                              <Badge className="bg-primary/100 text-white text-xs font-bold shrink-0">TIP</Badge>
                              <p className="text-sm">Other platforms show price per person per night instead of the total price per night for this booking</p>
                            </div>
                            <PopoverClose asChild>
                              <button className="text-muted-foreground hover:text-foreground shrink-0">
                                <X className="w-4 h-4" />
                              </button>
                            </PopoverClose>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                )}
                
                <div>
                  <div className="flex items-baseline gap-2">
                    {/* Show crossed out original price for pass holders when comparison is active */}
                    {validPass && hotel.price_comparison?.show_comparison && (
                      <span className="text-sm text-muted-foreground line-through">€{hotel.price_comparison.ota_estimated_price?.toFixed(0)}</span>
                    )}
                    {/* Show crossed out price for pass holders when NO comparison */}
                    {validPass && !hotel.price_comparison?.show_comparison && (
                      <span className="text-sm text-muted-foreground line-through">€{pricing.priceBeforeDiscount.toFixed(0)}</span>
                    )}
                    {/* Main price: pass holders see freestays price, others see normal price */}
                    <span className="text-2xl font-bold text-[#00A9E0]">
                      €{hotel.price_comparison?.show_comparison 
                        ? (validPass 
                          ? hotel.price_comparison.freestays_price?.toFixed(0)
                          : hotel.price_comparison.ota_estimated_price?.toFixed(0))
                        : pricing.roomTotal.toFixed(0)}
                    </span>
                    <span className="text-xs text-muted-foreground">/ {nights} {nights === 1 ? t('search.night', 'night') : t('search.nights', 'nights')}</span>
                  </div>
                </div>
              </div>

              {/* Right column: Save badge above View Deal button */}
              <div className="flex flex-col items-end gap-2">
                {hotel.price_comparison?.show_comparison && hotel.price_comparison.savings_amount > 0 && (
                  <p className="text-xs text-emerald-600 font-semibold text-right" data-testid={`save-badge-${hotel.hotel_id}`}>
                    {t('search.saveVsOther', 'Save €{{amount}} with Freestays Pass', { amount: hotel.price_comparison.savings_amount?.toFixed(0) })}
                  </p>
                )}
                <Button 
                  className="rounded-full px-6 h-11 font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all"
                  onClick={goToHotelDetail}
                >
                  {t('search.viewDeal', 'View Deal')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

// ==================== NO ROOMS AVAILABLE COMPONENT ====================
const NoRoomsAvailable = ({ hotelId, hotelName, checkIn, checkOut, adults, children, destination, destinationId }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [alternatives, setAlternatives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [findingNextAvailable, setFindingNextAvailable] = useState(false);
  const [nextAvailability, setNextAvailability] = useState(null);
  
  const nights = differenceInDays(parseLocalDate(checkOut), parseLocalDate(checkIn));

  const findAlternatives = async () => {
    setLoading(true);
    setShowAlternatives(true);
    try {
      const response = await axios.get(`${API}/hotels/${hotelId}/alternatives`, {
        params: {
          check_in: checkIn,
          check_out: checkOut,
          adults: adults,
          children: children,
          destination_id: destinationId
        }
      });
      setAlternatives(response.data.alternatives || []);
    } catch (error) {
      console.error('Error finding alternatives:', error);
      setAlternatives([]);
    } finally {
      setLoading(false);
    }
  };

  const findNextAvailable = async () => {
    setFindingNextAvailable(true);
    setNextAvailability(null);
    try {
      const response = await axios.get(`${API}/hotels/${hotelId}/next-availability`, {
        params: {
          adults: adults,
          children: children,
          b2c: 0,
          max_days: 60  // Search up to 60 days ahead
        }
      });
      setNextAvailability(response.data);
      if (response.data.found) {
        toast.success(t('hotel.foundAvailability', `Found availability starting ${response.data.check_in}!`).replace('${date}', response.data.check_in));
      }
    } catch (error) {
      console.error('Error finding next availability:', error);
      toast.error(t('hotel.errorFindingAvailability', 'Could not find availability'));
    } finally {
      setFindingNextAvailable(false);
    }
  };

  const goToNextAvailable = () => {
    if (nextAvailability?.found) {
      navigate(`/hotel/${hotelId}?checkIn=${nextAvailability.check_in}&checkOut=${nextAvailability.check_out}&adults=${adults}&children=${children}&destination=${encodeURIComponent(destination)}&destinationId=${destinationId || ''}`);
    }
  };

  const tryDifferentDates = () => {
    // Navigate to search page with destination to find availability
    if (destinationId) {
      navigate(`/search?destinationId=${destinationId}&checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}`);
    } else {
      navigate('/');
    }
  };

  return (
    <Card className="p-8 text-center bg-gray-50 dark:bg-[#0a2540] border-dashed" data-testid="no-rooms-available">
      <div className="max-w-md mx-auto">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <CalendarX className="w-8 h-8 text-amber-600 dark:text-[#FFAF00]" />
        </div>
        
        <h3 className="font-semibold text-lg mb-2">
          {t('hotel.noRoomsAvailable', 'No Rooms Available')}
        </h3>
        
        <p className="text-muted-foreground mb-6 text-sm">
          {t('hotel.noRoomsMessage', `Unfortunately, ${hotelName || 'this hotel'} has no availability for your selected dates. Try different dates or check out similar hotels nearby.`).replace('${hotelName}', hotelName || 'this hotel')}
        </p>

        <div className="flex flex-col gap-3">
          {/* Primary actions row */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={findNextAvailable}
              disabled={findingNextAvailable}
              className="rounded-full bg-primary hover:bg-primary/90"
              data-testid="find-next-available-btn"
            >
              {findingNextAvailable ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CalendarCheck className="w-4 h-4 mr-2" />
              )}
              {t('hotel.findNextAvailable', 'Find Next Available')}
            </Button>
            
            <Button
              variant="outline"
              onClick={tryDifferentDates}
              className="rounded-full"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              {t('hotel.tryDifferentDates', 'Try Different Dates')}
            </Button>
            
            <Button
              variant="outline"
              onClick={findAlternatives}
              disabled={loading}
              className="rounded-full"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Building2 className="w-4 h-4 mr-2" />
              )}
              {t('hotel.findNearbyHotels', 'Find Hotels Nearby')}
            </Button>
          </div>
        </div>

        {/* Next Availability Result */}
        {nextAvailability && (
          <div className="mt-6 pt-6 border-t border-border">
            {nextAvailability.found ? (
              <Card className="p-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-green-700 dark:text-green-400">
                    {t('hotel.availabilityFound', 'Availability Found!')}
                  </h4>
                </div>
                <div className="text-sm text-muted-foreground mb-3">
                  <p className="font-medium text-foreground">
                    {nextAvailability.check_in} → {nextAvailability.check_out}
                  </p>
                  <p>{nextAvailability.rooms_available} {t('hotel.roomsAvailable', 'rooms available')}</p>
                  <p className="text-green-600 font-semibold mt-1">
                    {t('hotel.fromPrice', 'From')} €{nextAvailability.cheapest_price?.toFixed(2)} / {t('hotel.perNight', 'night')}
                  </p>
                </div>
                <Button onClick={goToNextAvailable} className="rounded-full">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  {t('hotel.viewTheseDates', 'View These Dates')}
                </Button>
              </Card>
            ) : (
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <p className="text-amber-700 dark:text-[#FFAF00] text-sm">
                  {t('hotel.noAvailabilityNext60Days', 'No availability found in the next 60 days. Try searching for alternative hotels.')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Alternative Hotels Section */}
        {showAlternatives && (
          <div className="mt-8 pt-6 border-t border-border">
            <h4 className="font-medium mb-4 flex items-center justify-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              {t('hotel.alternativeHotels', 'Available Hotels Nearby')}
            </h4>
            
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : alternatives.length > 0 ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {alternatives.slice(0, 4).map(hotel => (
                  <Card 
                    key={hotel.hotel_id} 
                    className="p-4 cursor-pointer hover:shadow-md transition-all text-left"
                    onClick={() => navigate(`/hotel/${hotel.hotel_id}?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}&destination=${encodeURIComponent(destination)}&destinationId=${destinationId || ''}`)}
                  >
                    <div className="flex gap-3">
                      <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-secondary">
                        <img 
                          src={proxyImage(hotel.image_url || hotel.images?.[0], 160, 75)}
                          alt={hotel.name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium text-sm line-clamp-2">{hotel.name}</h5>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <MapPin className="w-3 h-3" />
                          {hotel.city}
                        </div>
                        <div className="flex items-center gap-1 mt-2">
                          {Array.from({ length: Math.floor(hotel.star_rating || 0) }).map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                        <p className="text-primary font-semibold text-sm mt-1">
                          €{hotel.min_price?.toFixed(0) || '---'} <span className="text-xs text-muted-foreground font-normal">/ {t('search.night', 'night')}</span>
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm py-4">
                {t('hotel.noAlternativesFound', 'No alternative hotels found for your dates. Try adjusting your search dates.')}
              </p>
            )}
            
            {alternatives.length > 4 && (
              <Button 
                variant="link" 
                className="mt-4"
                onClick={tryDifferentDates}
              >
                {t('hotel.viewMoreHotels', 'View all available hotels')} →
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

// ==================== HOTEL DETAIL PAGE ====================
const HotelDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { hotelId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, hasValidPass } = useAuth();
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [roomGalleryOpen, setRoomGalleryOpen] = useState(false);
  const [roomGalleryImages, setRoomGalleryImages] = useState([]);
  const [roomGalleryIndex, setRoomGalleryIndex] = useState(0);
  const [roomGalleryTitle, setRoomGalleryTitle] = useState('');
  
  const checkIn = searchParams.get('checkIn') || format(addDays(new Date(), 7), 'yyyy-MM-dd');
  const checkOut = searchParams.get('checkOut') || format(addDays(new Date(), 10), 'yyyy-MM-dd');
  const adults = parseInt(searchParams.get('adults') || '2');
  const children = parseInt(searchParams.get('children') || '0');
  // Children ages flow through the entire booking lifecycle to Sunhotels.
  // Default to age 10 per missing entry so we never send empty childrenAges with children>0.
  const childrenAges = (() => {
    const raw = searchParams.get('childrenAges') || '';
    const parsed = raw.split(',').filter(a => a !== '').map(a => parseInt(a)).filter(n => !isNaN(n));
    while (parsed.length < children) parsed.push(10);
    return parsed.slice(0, children);
  })();
  const childrenAgesParam = childrenAges.length > 0 ? `&childrenAges=${childrenAges.join(',')}` : '';
  const b2c = parseInt(searchParams.get('b2c') || '0');  // 1 for last minute deals
  const destinationIdParam = searchParams.get('destinationId') || '';  // City context for room search
  const resortId = searchParams.get('resortId') || '';  // Resort/area context
  const destinationParam = searchParams.get('destination') || '';  // For breadcrumb back navigation
  
  const nights = differenceInDays(parseLocalDate(checkOut), parseLocalDate(checkIn));
  const validPass = hasValidPass?.() || false;
  
  // Nearby hotels state
  const [nearbyHotels, setNearbyHotels] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [nearbyHotelsCount, setNearbyHotelsCount] = useState(4);
  
  // TripAdvisor data state
  const [tripAdvisorData, setTripAdvisorData] = useState(null);
  
  // OTA Price data state (for price comparison)
  const [otaPrices, setOtaPrices] = useState(null);
  const [loadingOtaPrices, setLoadingOtaPrices] = useState(false);
  
  // Transfer state
  const [transferSettings, setTransferSettings] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [transferTime, setTransferTime] = useState('12:00');
  const [flightNumber, setFlightNumber] = useState('');
  
  // Return transfer state
  const [returnTransfers, setReturnTransfers] = useState([]);
  const [selectedReturnTransfer, setSelectedReturnTransfer] = useState(null);
  const [returnTransferTime, setReturnTransferTime] = useState('10:00');
  const [returnFlightNumber, setReturnFlightNumber] = useState('');
  const [returnTransferDialogOpen, setReturnTransferDialogOpen] = useState(false);
  const [loadingReturnTransfers, setLoadingReturnTransfers] = useState(false);

  // Booking confirmation dialog state
  const [bookingConfirmOpen, setBookingConfirmOpen] = useState(false);
  const [selectedRoomForBooking, setSelectedRoomForBooking] = useState(null);

  // Load transfer settings and available transfers
  useEffect(() => {
    const loadTransferData = async () => {
      try {
        // First get transfer settings
        const settingsRes = await axios.get(`${API}/transfers/settings`);
        setTransferSettings(settingsRes.data);
        
        // Only search for transfers if enabled and we have hotel ID
        // Note: destinationId is no longer required by the API
        if (settingsRes.data?.enabled && settingsRes.data?.show_on_hotel_page && hotelId) {
          setLoadingTransfers(true);
          setLoadingReturnTransfers(true);
          
          // Search for arrival transfers (airport → hotel)
          const arrivalRes = await axios.post(`${API}/transfers/search`, {
            hotel_id: hotelId,
            arrival_date: checkIn,
            arrival_time: transferTime,
            resort_id: resortId || '',  // Optional
            transfer_type: 'arrival'
          });
          
          if (arrivalRes.data?.success) {
            setTransfers(arrivalRes.data.transfers || []);
          }
          
          // Search for return transfers (hotel → airport)
          const departureRes = await axios.post(`${API}/transfers/search`, {
            hotel_id: hotelId,
            arrival_date: checkOut,  // Use checkout date for departure
            arrival_time: returnTransferTime,
            departure_date: checkOut,
            departure_time: returnTransferTime,
            resort_id: resortId || '',  // Optional
            transfer_type: 'departure'
          });
          
          if (departureRes.data?.success) {
            setReturnTransfers(departureRes.data.transfers || []);
          }
        }
      } catch (error) {
        console.error('Error loading transfer data:', error);
      } finally {
        setLoadingTransfers(false);
        setLoadingReturnTransfers(false);
      }
    };
    
    loadTransferData();
  }, [hotelId, checkIn, checkOut, resortId]);

  const { data: hotel, isLoading } = useQuery({
    queryKey: ['hotel', hotelId, checkIn, checkOut, adults, children, b2c, destinationIdParam, resortId, i18n.language],
    queryFn: async () => {
      const response = await axios.get(`${API}/hotels/${hotelId}`, {
        params: {
          check_in: checkIn,
          check_out: checkOut,
          adults: adults,
          children: children,
          b2c: b2c,  // Pass b2c flag for last minute availability
          destination_id: destinationIdParam || undefined,  // City context for room search
          resort_id: resortId || undefined,  // Resort/area context
          language: i18n.language || 'en'
        }
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - cache results per language
    refetchOnWindowFocus: false
  });

  // Fire-and-forget view tracking for social-proof counter (max 1/hr per fp)
  useEffect(() => {
    if (!hotelId) return;
    axios.post(`${API}/hotels/${hotelId}/track-view`).catch(() => {});
  }, [hotelId]);

  // Fetch TripAdvisor data for this hotel (on-demand with caching)
  useEffect(() => {
    if (hotelId && hotel?.name) {
      // Build query params for on-demand fetching
      const params = new URLSearchParams({
        hotel_name: hotel.name,
        city: hotel.city || '',
        country: hotel.country || ''
      });
      
      axios.get(`${API}/hotels/${hotelId}/tripadvisor?${params}`)
        .then(res => {
          if (res.data && res.data.rating) {
            setTripAdvisorData(res.data);
          }
        })
        .catch(() => {});
    }
  }, [hotelId, hotel?.name]);

  // Fetch OTA prices for price comparison (on-demand with caching)
  useEffect(() => {
    if (hotelId && hotel?.name && checkIn && checkOut) {
      setLoadingOtaPrices(true);
      
      const params = new URLSearchParams({
        check_in: checkIn,
        check_out: checkOut
      });
      
      axios.get(`${API}/hotels/${hotelId}/ota-prices?${params}`)
        .then(res => {
          if (res.data && res.data.prices_by_source && Object.keys(res.data.prices_by_source).length > 0) {
            setOtaPrices(res.data);
          }
        })
        .catch(() => {})
        .finally(() => setLoadingOtaPrices(false));
    }
  }, [hotelId, hotel?.name, checkIn, checkOut]);

  // Use hotel data as fallback for destination info (for breadcrumb navigation)
  const destination = destinationParam || hotel?.city || '';
  const destinationId = destinationIdParam || hotel?.dest_id || '';
  
  // Build search URL for breadcrumb back navigation
  const searchBackUrl = `/search?destination=${encodeURIComponent(destination)}&destinationId=${destinationId}&checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}`;

  // Keyboard navigation for gallery
  useEffect(() => {
    if (!galleryOpen || !hotel?.images) return;
    
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          setGalleryOpen(false);
          break;
        case 'ArrowLeft':
          setGalleryIndex((prev) => (prev === 0 ? hotel.images.length - 1 : prev - 1));
          break;
        case 'ArrowRight':
          setGalleryIndex((prev) => (prev === hotel.images.length - 1 ? 0 : prev + 1));
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when gallery is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [galleryOpen, hotel?.images]);

  // Keyboard navigation for room gallery
  useEffect(() => {
    if (!roomGalleryOpen || roomGalleryImages.length === 0) return;
    
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'Escape':
          setRoomGalleryOpen(false);
          break;
        case 'ArrowLeft':
          setRoomGalleryIndex((prev) => (prev === 0 ? roomGalleryImages.length - 1 : prev - 1));
          break;
        case 'ArrowRight':
          setRoomGalleryIndex((prev) => (prev === roomGalleryImages.length - 1 ? 0 : prev + 1));
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [roomGalleryOpen, roomGalleryImages]);

  // Load nearby hotels - hybrid approach:
  // 1. First try sessionStorage (from search results page)
  // 2. Fallback to API call if we have destinationId
  useEffect(() => {
    const loadNearbyHotels = async () => {
      if (!hotel) {
        return;
      }
      
      setLoadingNearby(true);
      try {
        // Get admin setting for nearby hotels count
        const settingsRes = await axios.get(`${API}/settings/public`);
        const count = settingsRes.data?.nearby_hotels_count || 4;
        setNearbyHotelsCount(count);
        
        // Check if current hotel is a demo/sample hotel
        const currentIsDemo = String(hotelId).startsWith('demo_');
        const isLastMinute = b2c === 1;
        
        // For Last Minute hotels, try to get from stored last minute offers first
        if (isLastMinute) {
          try {
            const lastMinuteRes = await axios.get(`${API}/hotels/last-minute`);
            const lastMinuteHotels = lastMinuteRes.data?.hotels || [];
            
            // Filter out current hotel and get nearby ones (same destination or city)
            const nearbyLastMinute = lastMinuteHotels
              .filter(h => {
                if (String(h.hotel_id) === String(hotelId)) return false;
                // Prefer hotels from same destination/city
                if (hotel.city && h.city === hotel.city) return true;
                if (hotel.fetched_destination && h.fetched_destination === hotel.fetched_destination) return true;
                return true; // Include all as fallback
              })
              .slice(0, count);
            
            if (nearbyLastMinute.length > 0) {
              setNearbyHotels(nearbyLastMinute);
              setLoadingNearby(false);
              return;
            }
          } catch (err) {
            console.log('Could not fetch last minute nearby hotels');
          }
        }
        
        // Try to get hotels from sessionStorage first (from search results navigation)
        const storedHotels = sessionStorage.getItem('nearbyHotelsFromSearch');
        if (storedHotels) {
          const hotels = JSON.parse(storedHotels);
          // Filter out current hotel and apply demo filter logic
          const filteredHotels = hotels
            .filter(h => {
              const hId = String(h.hotel_id);
              if (hId === String(hotelId)) return false; // Exclude current hotel
              // If current hotel is real, only show real nearby hotels
              // If current hotel is demo, show other demo hotels from same search
              if (!currentIsDemo && hId.startsWith('demo_')) return false;
              return true;
            })
            .slice(0, count);
          
          if (filteredHotels.length > 0) {
            setNearbyHotels(filteredHotels);
            setLoadingNearby(false);
            return;
          }
        }
        
        // Fallback: Make API call if we have destinationId (for direct autocomplete selection)
        if (destinationId) {
          const destinationName = hotel.city || hotel.address || 'nearby';
          const searchRes = await axios.post(`${API}/hotels/search`, {
            destination: destinationName,
            destination_id: destinationId,
            resort_id: resortId || null,
            check_in: checkIn,
            check_out: checkOut,
            adults: adults,
            children: children,
            b2c: 0  // Always use regular search for nearby hotels, not last minute
          });
          
          const hotels = searchRes.data?.hotels || [];
          
          // Filter out current hotel and demo hotels (for real hotel views)
          const realNearbyHotels = hotels
            .filter(h => {
              const hId = String(h.hotel_id);
              return hId !== String(hotelId) && !hId.startsWith('demo_');
            })
            .slice(0, count);
          
          setNearbyHotels(realNearbyHotels);
        } else {
          setNearbyHotels([]);
        }
      } catch (error) {
        console.error('Error loading nearby hotels:', error);
        setNearbyHotels([]);
      } finally {
        setLoadingNearby(false);
      }
    };
    
    loadNearbyHotels();
  }, [hotelId, hotel, destinationId, resortId, checkIn, checkOut, adults, children, b2c]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-sans font-bold text-2xl mb-4">{t('common.error', 'Hotel not found')}</h2>
          <Button onClick={() => navigate('/search')}>{t('common.back', 'Back to Search')}</Button>
        </div>
      </div>
    );
  }

  const handleBookRoom = (room) => {
    // Show confirmation dialog first
    setSelectedRoomForBooking(room);
    setBookingConfirmOpen(true);
  };

  const proceedToCheckout = () => {
    if (!selectedRoomForBooking) return;
    const room = selectedRoomForBooking;
    
    const nettPrice = room.price; // Sunhotels price is already total for all nights
    let url = `/booking/new?hotelId=${hotelId}&roomId=${room.room_id}&checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}${childrenAgesParam}&price=${nettPrice}&roomType=${encodeURIComponent(room.room_type)}&boardType=${encodeURIComponent(room.board_type || 'Room Only')}&sunhotelsRoomTypeId=${room.sunhotels_room_type_id || ''}&sunhotelsBlockId=${room.sunhotels_block_id || ''}`;
    
    // Add destination context for transfers and nearby hotels
    if (destinationId) url += `&destinationId=${destinationId}`;
    if (destination) url += `&destination=${encodeURIComponent(destination)}`;
    if (b2c === 1) url += `&b2c=1`;
    
    // Add arrival transfer data if selected
    if (selectedTransfer) {
      url += `&transferId=${selectedTransfer.transfer_id}&transferPrice=${selectedTransfer.price}&transferVehicle=${encodeURIComponent(selectedTransfer.vehicle_name || selectedTransfer.vehicle_type)}&transferTime=${transferTime}`;
      if (flightNumber) {
        url += `&transferFlight=${flightNumber}`;
      }
    }
    
    // Add return transfer data if selected
    if (selectedReturnTransfer) {
      url += `&returnTransferId=${selectedReturnTransfer.transfer_id}&returnTransferPrice=${selectedReturnTransfer.price}&returnTransferVehicle=${encodeURIComponent(selectedReturnTransfer.vehicle_name || selectedReturnTransfer.vehicle_type)}&returnTransferTime=${returnTransferTime}`;
      if (returnFlightNumber) {
        url += `&returnTransferFlight=${returnFlightNumber}`;
      }
    }
    
    setBookingConfirmOpen(false);
    navigate(url);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#081D33]" data-testid="hotel-detail-page">
      {/* Compact Search Bar */}
      <CompactSearchBar />
      
      {/* Hotel Image Gallery - Full Width Hero */}
      <div className="relative h-[45vh] md:h-[55vh] bg-slate-900">
        <div className="h-full max-w-[1920px] mx-auto flex">
          {/* Main Image - 2/3 width */}
          <div 
            className="w-full md:w-2/3 h-full relative overflow-hidden cursor-pointer group"
            onClick={() => { setGalleryIndex(0); setGalleryOpen(true); }}
          >
            <img 
              src={proxyImage(hotel.image_url || hotel.images?.[0], 1280, 80)} 
              alt={hotel.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              onError={(e) => { e.target.style.display = 'none'; }}
              fetchpriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/10" />
            <div className="absolute top-4 left-4 bg-black/40 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 border border-white/10">
              <ImageIcon className="w-4 h-4" />
              {t('hotel.viewAllPhotos', 'View all photos')}
            </div>
          </div>
          
          {/* Thumbnail Gallery - 1/3 width (desktop only) */}
          {hotel.images && hotel.images.length > 1 && (
            <div className="hidden md:flex w-1/3 h-full flex-col gap-1 pl-1 relative">
              <div 
                className="flex-1 flex flex-col gap-1 overflow-y-auto scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {hotel.images.slice(1, 4).map((img, idx) => (
                  <div 
                    key={idx} 
                    className="flex-1 min-h-[80px] relative overflow-hidden cursor-pointer group"
                    onClick={() => { setGalleryIndex(idx + 1); setGalleryOpen(true); }}
                  >
                    <img 
                      src={proxyImage(img, 480, 78)} 
                      alt={`${hotel.name} - ${idx + 2}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                  </div>
                ))}
              </div>
              {/* Show more photos button */}
              {hotel.images.length > 4 && (
                <button 
                  className="absolute bottom-3 right-3 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md text-foreground px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 hover:bg-white dark:hover:bg-slate-800 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5"
                  onClick={() => { setGalleryIndex(0); setGalleryOpen(true); }}
                >
                  <Camera className="w-4 h-4" />
                  +{hotel.images.length - 4} {t('hotel.photos', 'photos')}
                </button>
              )}
            </div>
          )}
          
          {/* Mobile: View photos button */}
          {hotel.images && hotel.images.length > 1 && (
            <button 
              className="md:hidden absolute bottom-24 right-4 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md text-foreground px-4 py-2.5 rounded-xl text-sm font-medium flex items-center gap-2 shadow-xl"
              onClick={() => { setGalleryIndex(0); setGalleryOpen(true); }}
            >
              <Camera className="w-4 h-4" />
              {hotel.images.length} {t('hotel.photos', 'photos')}
            </button>
          )}
          
          {/* Mobile Only: TripAdvisor Badge - Top Right of Photo */}
          {tripAdvisorData && tripAdvisorData.rating && (
            <a 
              href={tripAdvisorData.tripadvisor_direct_url || tripAdvisorData.tripadvisor_url}
              target="_blank"
              rel="noopener noreferrer"
              className="md:hidden absolute top-4 right-4 flex items-center gap-1.5 bg-white/95 backdrop-blur-sm rounded-lg px-2.5 py-1.5 shadow-lg"
            >
              <svg viewBox="0 0 32 32" className="w-6 h-6 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="16" cy="18" rx="14" ry="12" fill="#00AA6C"/>
                <circle cx="10" cy="16" r="6" fill="white"/>
                <circle cx="22" cy="16" r="6" fill="white"/>
                <circle cx="10" cy="16" r="3.5" fill="#333"/>
                <circle cx="22" cy="16" r="3.5" fill="#333"/>
                <circle cx="10" cy="16" r="1.5" fill="white"/>
                <circle cx="22" cy="16" r="1.5" fill="white"/>
                <path d="M16 19 L14 23 L16 22 L18 23 Z" fill="#FF9500"/>
                <path d="M4 10 Q6 6 10 8 Q8 12 4 10" fill="#00AA6C"/>
                <path d="M28 10 Q26 6 22 8 Q24 12 28 10" fill="#00AA6C"/>
              </svg>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div 
                    key={i} 
                    className={`w-2.5 h-2.5 rounded-full ${
                      i <= Math.floor(tripAdvisorData.rating) 
                        ? 'bg-[#00AA6C]' 
                        : i <= tripAdvisorData.rating 
                          ? 'bg-[#00AA6C]/50' 
                          : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs font-semibold text-gray-800">{tripAdvisorData.rating.toFixed(1)}</span>
            </a>
          )}
        </div>
        
        {/* Hotel Info Overlay - Premium Design */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 pointer-events-none">
          <div className="max-w-[1760px] mx-auto">
            <div className="md:w-2/3">
              {/* Star Rating */}
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`w-5 h-5 ${i < hotel.star_rating ? 'fill-amber-400 text-[#FFAF00]' : 'text-white/30'}`} />
                ))}
                {hotel.star_rating && (
                  <span className="text-white/70 text-sm ml-2">{hotel.star_rating}-star hotel</span>
                )}
              </div>
              
              {/* Hotel Name Row with TripAdvisor (Desktop) */}
              <div className="flex items-start justify-between gap-4 mb-3">
                <h1 className="font-sans font-bold text-3xl md:text-5xl text-white text-shadow-lg">{hotel.name}</h1>
                
                {/* Desktop Only: TripAdvisor Badge - Right of Hotel Name */}
                {tripAdvisorData && tripAdvisorData.rating && (
                  <a 
                    href={tripAdvisorData.tripadvisor_direct_url || tripAdvisorData.tripadvisor_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden md:flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-lg px-3 py-2 hover:bg-white transition-colors pointer-events-auto flex-shrink-0"
                  >
                    <svg viewBox="0 0 32 32" className="w-8 h-8 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
                      <ellipse cx="16" cy="18" rx="14" ry="12" fill="#00AA6C"/>
                      <circle cx="10" cy="16" r="6" fill="white"/>
                      <circle cx="22" cy="16" r="6" fill="white"/>
                      <circle cx="10" cy="16" r="3.5" fill="#333"/>
                      <circle cx="22" cy="16" r="3.5" fill="#333"/>
                      <circle cx="10" cy="16" r="1.5" fill="white"/>
                      <circle cx="22" cy="16" r="1.5" fill="white"/>
                      <path d="M16 19 L14 23 L16 22 L18 23 Z" fill="#FF9500"/>
                      <path d="M4 10 Q6 6 10 8 Q8 12 4 10" fill="#00AA6C"/>
                      <path d="M28 10 Q26 6 22 8 Q24 12 28 10" fill="#00AA6C"/>
                    </svg>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div 
                            key={i} 
                            className={`w-3 h-3 rounded-full ${
                              i <= Math.floor(tripAdvisorData.rating) 
                                ? 'bg-[#00AA6C]' 
                                : i <= tripAdvisorData.rating 
                                  ? 'bg-[#00AA6C]/50' 
                                  : 'bg-gray-300'
                            }`}
                          />
                        ))}
                        <span className="text-sm font-semibold text-gray-800 ml-1">{tripAdvisorData.rating.toFixed(1)}</span>
                      </div>
                      {tripAdvisorData.review_count && (
                        <span className="text-xs text-gray-600">{tripAdvisorData.review_count.toLocaleString()} reviews</span>
                      )}
                    </div>
                  </a>
                )}
              </div>
              
              {/* Location */}
              <div className="flex items-center gap-2 text-white/90 text-base md:text-lg">
                <MapPin className="w-5 h-5" />
                <span>{hotel.address && `${hotel.address}, `}{hotel.city}{hotel.country && `, ${hotel.country}`}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Breadcrumbs - Below gallery */}
      <div className="bg-gray-50 dark:bg-[#0a2540] border-b">
        <div className="max-w-[1760px] mx-auto px-4 md:px-8 py-3">
          <Breadcrumbs items={[
            { label: t('search.searchTitle', 'Search'), href: searchBackUrl },
            { label: hotel.name }
          ]} />
        </div>
      </div>

      {/* Fullscreen Gallery Modal */}
      {galleryOpen && hotel.images && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10 p-2"
            onClick={() => setGalleryOpen(false)}
            title="Close (ESC)"
          >
            <XCircle className="w-8 h-8" />
          </button>
          
          {/* Image counter and keyboard hint */}
          <div className="absolute top-4 left-4 text-white/80 text-sm z-10 flex items-center gap-4">
            <span>{galleryIndex + 1} / {hotel.images.length}</span>
            <span className="hidden md:flex items-center gap-2 text-white/50 text-xs">
              <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px]">←</kbd>
              <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px]">→</kbd>
              <span>navigate</span>
              <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px]">ESC</kbd>
              <span>close</span>
            </span>
          </div>
          
          {/* Previous button */}
          <button 
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-2 bg-black/30 rounded-full hover:bg-black/50 transition-colors"
            onClick={() => setGalleryIndex((prev) => (prev === 0 ? hotel.images.length - 1 : prev - 1))}
            title="Previous (←)"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          
          {/* Main image */}
          <img 
            src={proxyImage(hotel.images[galleryIndex], 1600, 82)} 
            alt={`${hotel.name} - ${galleryIndex + 1}`}
            className="max-w-full max-h-[85vh] object-contain"
            decoding="async"
          />
          
          {/* Next button */}
          <button 
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-2 bg-black/30 rounded-full hover:bg-black/50 transition-colors"
            onClick={() => setGalleryIndex((prev) => (prev === hotel.images.length - 1 ? 0 : prev + 1))}
            title="Next (→)"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
          
          {/* Thumbnail strip */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto p-2 bg-black/50 rounded-lg">
            {hotel.images.map((img, idx) => (
              <button
                key={idx}
                className={`w-16 h-12 rounded overflow-hidden flex-shrink-0 transition-all ${idx === galleryIndex ? 'ring-2 ring-white scale-110' : 'opacity-60 hover:opacity-100'}`}
                onClick={() => setGalleryIndex(idx)}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Room Gallery Modal */}
      {roomGalleryOpen && roomGalleryImages.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          {/* Close button */}
          <button 
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10 p-2"
            onClick={() => setRoomGalleryOpen(false)}
            title="Close (ESC)"
          >
            <XCircle className="w-8 h-8" />
          </button>
          
          {/* Room name and image counter */}
          <div className="absolute top-4 left-4 text-white z-10">
            <p className="font-semibold text-lg">{roomGalleryTitle}</p>
            <p className="text-white/70 text-sm">{roomGalleryIndex + 1} / {roomGalleryImages.length}</p>
          </div>
          
          {/* Previous button */}
          {roomGalleryImages.length > 1 && (
            <button 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-2 bg-black/30 rounded-full hover:bg-black/50 transition-colors"
              onClick={() => setRoomGalleryIndex((prev) => (prev === 0 ? roomGalleryImages.length - 1 : prev - 1))}
              title="Previous"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}
          
          {/* Main image */}
          <img 
            src={roomGalleryImages[roomGalleryIndex]} 
            alt={`${roomGalleryTitle} - ${roomGalleryIndex + 1}`}
            className="max-w-full max-h-[85vh] object-contain"
          />
          
          {/* Next button */}
          {roomGalleryImages.length > 1 && (
            <button 
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 p-2 bg-black/30 rounded-full hover:bg-black/50 transition-colors"
              onClick={() => setRoomGalleryIndex((prev) => (prev === roomGalleryImages.length - 1 ? 0 : prev + 1))}
              title="Next"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}
          
          {/* Thumbnail strip */}
          {roomGalleryImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto p-2 bg-black/50 rounded-lg">
              {roomGalleryImages.map((img, idx) => (
                <button
                  key={idx}
                  className={`w-16 h-12 rounded overflow-hidden flex-shrink-0 transition-all ${idx === roomGalleryIndex ? 'ring-2 ring-white scale-110' : 'opacity-60 hover:opacity-100'}`}
                  onClick={() => setRoomGalleryIndex(idx)}
                >
                  <img src={proxyImage(img, 96, 75)} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="max-w-[1760px] mx-auto px-4 md:px-8 py-8">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            {/* Review & Description Card */}
            <Card className="p-6 md:p-8 rounded-2xl">
              {/* Review Score */}
              {hotel.review_score > 0 && (
                <div className="flex items-center gap-4 mb-6 pb-6 border-b">
                  <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-primary/30">
                    {hotel.review_score.toFixed(1)}
                  </div>
                  <div>
                    <p className="font-semibold text-lg">
                      {hotel.review_score >= 9 ? t('hotel.exceptional', 'Exceptional') : hotel.review_score >= 8 ? t('hotel.excellent', 'Excellent') : t('hotel.veryGood', 'Very Good')}
                    </p>
                    <p className="text-sm text-muted-foreground">{t('hotel.basedOn', 'Based on')} {hotel.review_count} {t('hotel.reviews', 'reviews')}</p>
                  </div>
                  <div className="ml-auto hidden md:flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-5 h-5 ${i < Math.round(hotel.review_score / 2) ? 'fill-amber-400 text-[#FFAF00]' : 'text-slate-200'}`} />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Description */}
              {hotel.description && (
                <div>
                  <h2 className="font-bold text-xl mb-3">{t('hotel.about', 'About this hotel')}</h2>
                  <div className="text-muted-foreground leading-relaxed">
                    <p className={!showFullDescription && hotel.description.length > 300 ? 'line-clamp-3' : ''}>
                      {hotel.description}
                    </p>
                    {hotel.description.length > 300 && (
                      <button 
                        onClick={() => setShowFullDescription(!showFullDescription)}
                        className="text-primary hover:underline text-sm font-medium mt-3 flex items-center gap-1"
                      >
                        {showFullDescription ? t('hotel.readLess', 'Read less') : t('hotel.readMore', 'Read more')}
                        <ChevronDown className={`w-4 h-4 transition-transform ${showFullDescription ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </Card>

            {/* Facilities Card */}
            <Card className="p-6 md:p-8 rounded-2xl">
              <h2 className="font-bold text-xl mb-4">{t('hotel.facilities', 'Facilities')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {hotel.amenities?.map(amenity => (
                  <div key={amenity} className="flex items-center gap-2.5 p-2.5 bg-secondary/50 rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Check className="w-4 h-4 text-[#00A9E0]" />
                    </div>
                    <span className="text-sm font-medium">{t(`amenities.${amenity}`, amenity)}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* OTA Price Comparison Section - Shows real prices from other platforms */}
            {otaPrices && Object.keys(otaPrices.prices_by_source || {}).length > 0 && hotel.min_price && (
              <Card className="p-6 border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/30 rounded-2xl" data-testid="ota-price-comparison">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h2 className="font-bold text-lg text-green-800 dark:text-green-200">
                      {t('priceComparison.title', 'Price Comparison')}
                    </h2>
                    <p className="text-sm text-green-600 dark:text-green-400">
                      {t('priceComparison.subtitle', 'Compare prices with other booking platforms')}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {Object.entries(otaPrices.prices_by_source).slice(0, 4).map(([source, price]) => (
                    <div key={source} className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                      <p className="text-xs text-muted-foreground truncate">{source}</p>
                      <p className="text-lg font-semibold text-red-500 line-through">€{price.toFixed(0)}</p>
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center justify-between p-4 bg-green-100 dark:bg-green-900/50 rounded-xl">
                  <div>
                    <p className="text-sm text-green-700 dark:text-green-300 font-medium">Freestays {t('common.price', 'Price')}</p>
                    <p className="text-2xl font-bold text-green-700 dark:text-green-300">
                      €{calculatePricing(hotel.min_price, validPass).roomTotal.toFixed(0)}
                    </p>
                  </div>
                  {otaPrices.average_price && hotel.min_price && (
                    <div className="text-right">
                      <Badge className="bg-green-600 text-white mb-1">
                        <Check className="w-3 h-3 mr-1" />
                        {t('common.saveMoney', 'Save')} €{(otaPrices.average_price - calculatePricing(hotel.min_price, validPass).roomTotal).toFixed(0)}
                      </Badge>
                      <p className="text-xs text-green-600 dark:text-green-400">
                        {t('priceComparison.vsAverage', 'vs. average OTA price')}
                      </p>
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground mt-3 italic">
                  * {t('priceComparison.disclaimer', 'Prices from other platforms are indicative and may vary')}
                </p>
              </Card>
            )}
            
            {/* Loading OTA prices indicator */}
            {loadingOtaPrices && (
              <Card className="p-4 border-gray-200 dark:border-gray-700 rounded-2xl">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">{t('priceComparison.loading', 'Loading price comparison...')}</p>
                </div>
              </Card>
            )}

            {/* Available Rooms Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-xl">
                  {(!hotel.rooms || hotel.rooms.length === 0) 
                    ? t('hotel.noRoomsAvailable', 'No Rooms Available')
                    : t('hotel.roomsAvailable', 'Available Rooms')
                  }
                </h2>
                {hotel.rooms && hotel.rooms.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {hotel.rooms.length} {t('hotel.options', 'options')}
                  </Badge>
                )}
              </div>
              
              {/* Hotel Notes */}
              {hotel.hotel_notes && (
                <Card className="p-4 mb-4 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800 rounded-xl">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
                      <Info className="w-5 h-5 text-amber-600 dark:text-[#FFAF00]" />
                    </div>
                    <div>
                      <p className="font-semibold text-amber-800 dark:text-amber-200 text-sm">{t('hotel.importantInfo', 'Important Information')}</p>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">{hotel.hotel_notes}</p>
                    </div>
                  </div>
                </Card>
              )}
              
              <div className="space-y-4 max-h-[700px] overflow-y-auto pr-2 scrollbar-thin">
                {hotel.rooms && hotel.rooms.length > 0 ? (
                  hotel.rooms.map(room => {
                  const pricing = calculatePricing(room.price, validPass); // Sunhotels price is already total for all nights
                  const roomImages = room.images && room.images.length > 0 ? room.images : (room.image_url ? [room.image_url] : []);

                  return (
                    <Card key={room.room_id} className="p-5 md:p-6 rounded-2xl hover:shadow-lg transition-shadow" data-testid={`room-${room.room_id}`}>
                      <div className="flex flex-col md:flex-row gap-4">
                        {/* Room Image Thumbnail - Click to open gallery */}
                        {roomImages.length > 0 && (
                          <div className="w-full md:w-56 flex-shrink-0">
                            <div 
                              className="relative group cursor-pointer"
                              onClick={() => {
                                // Process all images for gallery — proxied at full quality
                                const processedImages = roomImages.map(img => {
                                  let raw;
                                  if (typeof img === 'object' && img.id) {
                                    raw = img.id.startsWith('http') 
                                      ? img.id 
                                      : `https://hotelimages.sunhotels.net/HotelInfo/hotelImage.aspx?id=${img.id}`;
                                  } else if (typeof img === 'string' && !img.startsWith('http')) {
                                    raw = `https://hotelimages.sunhotels.net/HotelInfo/hotelImage.aspx?id=${img}`;
                                  } else {
                                    raw = img;
                                  }
                                  return proxyImage(raw, 1600, 82);
                                });
                                setRoomGalleryImages(processedImages);
                                setRoomGalleryTitle(room.room_type);
                                setRoomGalleryIndex(0);
                                setRoomGalleryOpen(true);
                              }}
                            >
                              {/* Main thumbnail image */}
                              <div className="w-full h-36 rounded-lg overflow-hidden bg-secondary">
                                <img 
                                  src={(() => {
                                    const img = roomImages[0];
                                    let raw;
                                    if (typeof img === 'object' && img.id) {
                                      raw = img.id.startsWith('http') 
                                        ? img.id 
                                        : `https://hotelimages.sunhotels.net/HotelInfo/hotelImage.aspx?id=${img.id}`;
                                    } else if (typeof img === 'string' && !img.startsWith('http')) {
                                      raw = `https://hotelimages.sunhotels.net/HotelInfo/hotelImage.aspx?id=${img}`;
                                    } else {
                                      raw = img;
                                    }
                                    return proxyImage(raw, 320, 78);
                                  })()} 
                                  alt={room.room_type}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                  onError={(e) => { e.target.src = '/api/public/hotel-fallback-image?width=800'; }}
                                  loading="lazy"
                                  decoding="async"
                                  width="320"
                                  height="144"
                                />
                              </div>
                              {/* Photo count overlay */}
                              {roomImages.length > 1 && (
                                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1.5 group-hover:bg-primary transition-colors">
                                  <Camera className="w-3.5 h-3.5" />
                                  <span>{roomImages.length} {t('hotel.photos', 'photos')}</span>
                                </div>
                              )}
                              {/* Hover overlay */}
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center">
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 dark:bg-black/70 rounded-full p-2">
                                  <Expand className="w-5 h-5" />
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex-1 flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{room.room_type}</h3>
                            <p className="text-sm text-muted-foreground mb-2">{t(`meals.${room.board_type}`, room.board_type)}</p>
                            
                            {/* Airport Transfer Selection Button */}
                            {transferSettings?.enabled && (transfers.length > 0 || returnTransfers.length > 0) && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="mb-2 gap-2 text-xs h-8 rounded-full border-primary/30 text-primary hover:bg-primary/10"
                                onClick={() => setTransferDialogOpen(true)}
                                data-testid="transfer-available-btn"
                              >
                                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                                  <Car className="w-3 h-3 text-primary" />
                                </div>
                                <span>{t('transfer.available', 'Transfer Available')}</span>
                                {selectedTransfer && (
                                  <Badge variant="secondary" className="text-xs ml-1 bg-primary/10 text-primary">
                                    €{selectedTransfer.price.toFixed(0)}
                                  </Badge>
                                )}
                                <ChevronRight className="w-3 h-3" />
                              </Button>
                            )}
                            
                            {/* Room Amenities */}
                            {room.amenities && room.amenities.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-3">
                                {room.amenities.slice(0, 6).map((amenity, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs font-normal">
                                    {amenity}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            
                            <div className="flex flex-wrap items-center gap-2 text-sm mb-3">
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                Up to {room.max_occupancy} guests
                              </span>
                              
                              {/* Room Size */}
                              {room.size_m2 && (
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <Bed className="w-4 h-4" />
                                  {room.size_m2} m²
                                </span>
                              )}
                              
                              {/* Cancellation Policy Badge */}
                              <Badge 
                                variant={room.is_refundable ? "outline" : "destructive"} 
                                className={`text-xs ${room.is_refundable ? 'border-primary text-primary bg-primary/10' : ''}`}
                              >
                                {room.is_refundable ? (
                                  <><CheckCircle className="w-3 h-3 mr-1" /> Refundable</>
                                ) : (
                                  'Non-refundable'
                                )}
                              </Badge>
                            </div>
                            
                            {/* Room Description */}
                            {room.description && (
                              <p className="text-sm text-muted-foreground mb-2">{room.description}</p>
                            )}
                          
                          {/* Room Fees (City Tax, etc.) */}
                          {room.fees && room.fees.length > 0 && (
                            <div className="text-xs text-muted-foreground mb-2">
                              {room.fees.map((fee, idx) => (
                                <span key={idx} className="flex items-center gap-1">
                                  <Info className="w-3 h-3" />
                                  {fee.name}: €{fee.amount} {fee.included_in_price ? '(included)' : '(payable at hotel)'}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* Room Notes */}
                          {room.room_notes && (
                            <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                              <span className="font-medium">Note:</span> {room.room_notes}
                            </div>
                          )}

                          {/* Cancellation Policy Note */}
                          {room.is_refundable && room.cancellation_deadline_display && (
                            <div className="mt-2 p-2 bg-primary/10 rounded text-xs text-primary">
                              <span className="font-medium">Cancellation:</span> Free cancellation until {room.cancellation_deadline_display}
                            </div>
                          )}
                          
                          {/* Cancellation Policy Terms */}
                          <p className="text-xs text-muted-foreground mt-2">
                            Cancellation policy follows the policy and terms of this hotel, you need to accept this at your booking.
                          </p>
                        </div>
                        
                        <div className="text-right min-w-[140px]">
                          <div className="flex items-baseline gap-2 justify-end">
                            {validPass && (
                              <span className="text-sm text-muted-foreground line-through">€{pricing.priceBeforeDiscount.toFixed(0)}</span>
                            )}
                            <span className="text-2xl font-semibold">€{pricing.roomTotal.toFixed(0)}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {nights} nights {!validPass && `+ €${BOOKING_FEE} fee`}
                          </p>
                          {validPass ? (
                            <Badge className="bg-emerald-600 text-white hover:bg-emerald-700 w-full justify-center mb-2 py-1.5" data-testid={`room-save-${room.room_type}`}>
                              <Tag className="w-3 h-3 mr-1" />
                              {t('common.saveMoney', 'Save')} €{pricing.discountAmount.toFixed(0)}
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 w-full justify-center mb-2 py-1.5" data-testid={`room-save-${room.room_type}`}>
                              <Tag className="w-3 h-3 mr-1" />
                              {t('common.saveMoney', 'Save')} €{pricing.potentialSavings.toFixed(0)} {t('booking.discount', 'with pass')}
                            </Badge>
                          )}
                          <Button 
                            className="w-full md:w-auto rounded-full mt-2"
                            onClick={() => handleBookRoom(room)}
                          >
                            {t('hotel.bookNow')}
                          </Button>
                        </div>
                      </div>
                    </div>
                    </Card>
                  );
                })
                ) : (
                  <NoRoomsAvailable 
                    hotelId={hotelId}
                    hotelName={hotel.name}
                    checkIn={checkIn}
                    checkOut={checkOut}
                    adults={adults}
                    children={children}
                    destination={destination}
                    destinationId={destinationId}
                  />
                )}
              </div>
            </div>

            {/* Transfer Options Section - Only show if transfers are available */}
            {transferSettings?.enabled && transferSettings?.show_on_hotel_page && !loadingTransfers && !loadingReturnTransfers && (transfers.length > 0 || returnTransfers.length > 0) && (
              <Card className="p-6 border-[#00A9E0]/20" data-testid="transfer-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Car className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-bold text-xl">{t('transfer.title', 'Airport Transfer')}</h2>
                      <p className="text-sm text-muted-foreground">{t('transfer.roundTrip', 'Book arrival & return transfers')}</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                    {/* Arrival Transfer Section */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <PlaneTakeoff className="w-4 h-4 text-primary" />
                          <h3 className="font-medium">{t('transfer.arrivalTransfer', 'Arrival Transfer')}</h3>
                          <Badge variant="outline" className="text-xs">{checkIn}</Badge>
                        </div>
                        {transfers.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {transfers.length} {t('transfer.options', 'options')}
                          </Badge>
                        )}
                      </div>
                      
                      {transfers.length > 0 ? (
                        <>
                          {/* Show top 2 arrival transfers */}
                          <div className="grid gap-2">
                            {transfers.slice(0, 2).map((transfer) => (
                              <div 
                                key={transfer.transfer_id} 
                                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  selectedTransfer?.transfer_id === transfer.transfer_id 
                                    ? 'border-primary bg-[#00A9E0]/5' 
                                    : 'border-border hover:border-primary/50'
                                }`}
                                onClick={() => setSelectedTransfer(transfer)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Car className="w-5 h-5 text-muted-foreground" />
                                    <div>
                                      <p className="font-medium text-sm">{t(`transferVehicle.${transfer.vehicle_name || transfer.vehicle_type}`, transfer.vehicle_name || transfer.vehicle_type)}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {transfer.airport_name && `${transfer.airport_name} • `}{t('transfer.maxPassengers', 'Max')} {transfer.max_passengers}
                                        {transfer.estimated_duration && ` • ${Math.floor(transfer.estimated_duration / 60)}h ${transfer.estimated_duration % 60}m`}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-semibold text-primary">€{transfer.price.toFixed(2)}</p>
                                    <p className="text-xs text-muted-foreground">{t('transfer.oneWay', 'one way')}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {transfers.length > 2 && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="w-full text-primary"
                              onClick={() => setTransferDialogOpen(true)}
                            >
                              {t('transfer.viewAllOptions', 'View all')} {transfers.length} {t('transfer.options', 'options')}
                            </Button>
                          )}
                          
                          {selectedTransfer && (
                            <div className="flex items-center justify-between p-2 bg-primary/10 rounded-lg">
                              <div className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-[#00A9E0]" />
                                <span className="text-sm">{t(`transferVehicle.${selectedTransfer.vehicle_name || selectedTransfer.vehicle_type}`, selectedTransfer.vehicle_name || selectedTransfer.vehicle_type)}</span>
                                <span className="text-sm font-semibold text-primary">€{selectedTransfer.price.toFixed(2)}</span>
                              </div>
                              <Button size="sm" variant="ghost" onClick={() => setSelectedTransfer(null)}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground py-2">{t('transfer.noArrivalTransfers', 'No arrival transfers available')}</p>
                      )}
                    </div>
                    
                    <Separator />
                    
                    {/* Return Transfer Section */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Plane className="w-4 h-4 text-primary" />
                          <h3 className="font-medium">{t('transfer.returnTransfer', 'Return Transfer')}</h3>
                          <Badge variant="outline" className="text-xs">{checkOut}</Badge>
                        </div>
                        {returnTransfers.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {returnTransfers.length} {t('transfer.options', 'options')}
                          </Badge>
                        )}
                      </div>
                      
                      {returnTransfers.length > 0 ? (
                        <>
                          {/* Show top 2 return transfers */}
                          <div className="grid gap-2">
                            {returnTransfers.slice(0, 2).map((transfer) => (
                              <div 
                                key={transfer.transfer_id} 
                                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  selectedReturnTransfer?.transfer_id === transfer.transfer_id 
                                    ? 'border-primary bg-[#00A9E0]/5' 
                                    : 'border-border hover:border-primary/50'
                                }`}
                                onClick={() => setSelectedReturnTransfer(transfer)}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Car className="w-5 h-5 text-muted-foreground" />
                                    <div>
                                      <p className="font-medium text-sm">{t(`transferVehicle.${transfer.vehicle_name || transfer.vehicle_type}`, transfer.vehicle_name || transfer.vehicle_type)}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {t('transfer.maxPassengers', 'Max')} {transfer.max_passengers}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-semibold text-primary">€{transfer.price.toFixed(2)}</p>
                                    <p className="text-xs text-muted-foreground">{t('transfer.oneWay', 'one way')}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {returnTransfers.length > 2 && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="w-full text-primary"
                              onClick={() => setReturnTransferDialogOpen(true)}
                            >
                              {t('transfer.viewAllOptions', 'View all')} {returnTransfers.length} {t('transfer.options', 'options')}
                            </Button>
                          )}
                          
                          {selectedReturnTransfer && (
                            <div className="flex items-center justify-between p-2 bg-primary/10 rounded-lg">
                              <div className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-[#00A9E0]" />
                                <span className="text-sm">{t(`transferVehicle.${selectedReturnTransfer.vehicle_name || selectedReturnTransfer.vehicle_type}`, selectedReturnTransfer.vehicle_name || selectedReturnTransfer.vehicle_type)}</span>
                                <span className="text-sm font-semibold text-primary">€{selectedReturnTransfer.price.toFixed(2)}</span>
                              </div>
                              <Button size="sm" variant="ghost" onClick={() => setSelectedReturnTransfer(null)}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground py-2">{t('transfer.noReturnTransfers', 'No return transfers available')}</p>
                      )}
                    </div>
                    
                    {/* Total Transfer Cost */}
                    {(selectedTransfer || selectedReturnTransfer) && (
                      <>
                        <Separator />
                        <div className="flex items-center justify-between p-3 bg-secondary rounded-lg">
                          <span className="font-medium">{t('transfer.totalTransferCost', 'Total Transfer Cost')}</span>
                          <span className="text-lg font-bold text-primary">
                            €{((selectedTransfer?.price || 0) + (selectedReturnTransfer?.price || 0)).toFixed(2)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
              </Card>
            )}
            
            {/* Transfer Selection Dialog */}
            <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
              <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Car className="w-5 h-5 text-primary" />
                    {t('transfer.selectArrivalTransfer', 'Select Arrival Transfer')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('transfer.arrivalTransferDesc', 'Airport → Hotel on')} {checkIn}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Time Selection */}
                  <div className="space-y-2">
                    <Label>{t('transfer.arrivalTime', 'Arrival Time')}</Label>
                    <Input
                      type="time"
                      value={transferTime}
                      onChange={(e) => setTransferTime(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  
                  {/* Flight Number (Optional) */}
                  <div className="space-y-2">
                    <Label>{t('transfer.flightNumber', 'Flight Number')} ({t('common.optional', 'optional')})</Label>
                    <Input
                      placeholder="e.g., KL1234"
                      value={flightNumber}
                      onChange={(e) => setFlightNumber(e.target.value.toUpperCase())}
                    />
                  </div>
                  
                  <Separator />
                  
                  {/* Transfer Options */}
                  <div className="space-y-3">
                    {/* No transfers available alert */}
                    {transfers.length === 0 && (
                      <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-[#FFAF00] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-800 dark:text-amber-200">
                              {t('transfer.noArrivalAvailable', 'No arrival transfers available for this date')}
                            </p>
                            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                              {t('transfer.tryDifferentTime', 'Try selecting a different pickup time')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {transfers.map((transfer) => (
                      <div 
                        key={transfer.transfer_id}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedTransfer?.transfer_id === transfer.transfer_id 
                            ? 'border-primary bg-[#00A9E0]/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => setSelectedTransfer(transfer)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {selectedTransfer?.transfer_id === transfer.transfer_id ? (
                              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                            )}
                            <div>
                              <p className="font-medium">{t(`transferVehicle.${transfer.vehicle_name || transfer.vehicle_type}`, transfer.vehicle_name || transfer.vehicle_type)}</p>
                              <p className="text-sm text-muted-foreground">
                                {transfer.airport_name && <><span className="font-medium">{transfer.airport_name}</span> • </>}
                                {t('transfer.maxPassengers', 'Max')} {transfer.max_passengers} {t('transfer.passengers', 'passengers')}
                                {transfer.estimated_duration && ` • ${Math.floor(transfer.estimated_duration / 60)}h ${transfer.estimated_duration % 60}m`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-semibold text-primary">€{transfer.price.toFixed(2)}</p>
                          </div>
                        </div>
                        {transfer.description && (
                          <p className="text-xs text-muted-foreground mt-2 ml-8">{transfer.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <DialogFooter className="flex gap-2">
                  <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button 
                    onClick={() => setTransferDialogOpen(false)}
                    disabled={!selectedTransfer}
                  >
                    {selectedTransfer 
                      ? `${t('transfer.confirm', 'Confirm')} (€${selectedTransfer.price.toFixed(2)})` 
                      : t('transfer.selectOption', 'Select an option')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            {/* Return Transfer Selection Dialog */}
            <Dialog open={returnTransferDialogOpen} onOpenChange={setReturnTransferDialogOpen}>
              <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Car className="w-5 h-5 text-primary" />
                    {t('transfer.selectReturnTransfer', 'Select Return Transfer')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('transfer.returnTransferDesc', 'Hotel → Airport on')} {checkOut}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* Time Selection */}
                  <div className="space-y-2">
                    <Label>{t('transfer.departureTime', 'Departure Time')}</Label>
                    <Input
                      type="time"
                      value={returnTransferTime}
                      onChange={(e) => setReturnTransferTime(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  
                  {/* Flight Number (Optional) */}
                  <div className="space-y-2">
                    <Label>{t('transfer.flightNumber', 'Flight Number')} ({t('common.optional', 'optional')})</Label>
                    <Input
                      placeholder="e.g., KL5678"
                      value={returnFlightNumber}
                      onChange={(e) => setReturnFlightNumber(e.target.value.toUpperCase())}
                    />
                  </div>
                  
                  <Separator />
                  
                  {/* Transfer Options */}
                  <div className="space-y-3">
                    {/* No transfers available alert */}
                    {returnTransfers.length === 0 && (
                      <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-[#FFAF00] flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-800 dark:text-amber-200">
                              {t('transfer.noDepartureAvailable', 'No departure transfers available for this date')}
                            </p>
                            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                              {t('transfer.tryDifferentTime', 'Try selecting a different pickup time')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {returnTransfers.map((transfer) => (
                      <div 
                        key={transfer.transfer_id}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedReturnTransfer?.transfer_id === transfer.transfer_id 
                            ? 'border-primary bg-[#00A9E0]/5' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        onClick={() => setSelectedReturnTransfer(transfer)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {selectedReturnTransfer?.transfer_id === transfer.transfer_id ? (
                              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30" />
                            )}
                            <div>
                              <p className="font-medium">{t(`transferVehicle.${transfer.vehicle_name || transfer.vehicle_type}`, transfer.vehicle_name || transfer.vehicle_type)}</p>
                              <p className="text-sm text-muted-foreground">
                                {t('transfer.maxPassengers', 'Max')} {transfer.max_passengers} {t('transfer.passengers', 'passengers')}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-semibold text-primary">€{transfer.price.toFixed(2)}</p>
                          </div>
                        </div>
                        {transfer.description && (
                          <p className="text-xs text-muted-foreground mt-2 ml-8">{transfer.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <DialogFooter className="flex gap-2">
                  <Button variant="outline" onClick={() => setReturnTransferDialogOpen(false)}>
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button 
                    onClick={() => setReturnTransferDialogOpen(false)}
                    disabled={!selectedReturnTransfer}
                  >
                    {selectedReturnTransfer 
                      ? `${t('transfer.confirm', 'Confirm')} (€${selectedReturnTransfer.price.toFixed(2)})` 
                      : t('transfer.selectOption', 'Select an option')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Booking Confirmation Dialog */}
            <Dialog open={bookingConfirmOpen} onOpenChange={setBookingConfirmOpen}>
              <DialogContent className="w-[calc(100vw-1rem)] max-w-lg max-h-[85vh] overflow-y-auto p-4 sm:p-6 mx-auto">
                <DialogHeader className="pb-2">
                  <DialogTitle className="flex items-center gap-2 text-base">
                    <Hotel className="w-4 h-4 text-primary" />
                    {t('booking.confirmSelection', 'Confirm Your Selection')}
                  </DialogTitle>
                </DialogHeader>
                
                {selectedRoomForBooking && (
                  <div className="space-y-2 py-2">
                    {/* Hotel Info - Compact */}
                    <div className="flex gap-3 items-start">
                      <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-secondary">
                        {(hotel.image_url || hotel.images?.[0]) ? (
                          <img 
                            src={hotel.image_url || hotel.images?.[0]} 
                            alt={hotel.name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Hotel className="w-6 h-6 text-muted-foreground/30" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm leading-tight">{hotel.name}</h4>
                        <div className="flex items-start gap-1 text-xs text-muted-foreground mt-1">
                          <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                          <span className="leading-tight">{hotel.location || hotel.address || destination}</span>
                        </div>
                        {hotel.star_rating && (
                          <div className="flex items-center gap-0.5 mt-1">
                            {[...Array(hotel.star_rating)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 text-[#FFAF00] fill-amber-400" />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Room & Stay Details - Compact Grid */}
                    <div className="bg-secondary/50 rounded-lg p-3 space-y-2">
                      {/* Room Type */}
                      <div className="flex items-center gap-2 text-sm">
                        <Bed className="w-4 h-4 text-primary shrink-0" />
                        <span className="font-medium">{selectedRoomForBooking.room_type}</span>
                      </div>
                      {/* Meal Plan */}
                      <div className="flex items-center gap-2 text-sm">
                        <Utensils className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="text-muted-foreground">{t(`meals.${selectedRoomForBooking.board_type || 'Room Only'}`, selectedRoomForBooking.board_type || 'Room Only')}</span>
                      </div>
                      
                      <div className="grid grid-cols-4 gap-2 text-xs pt-2 border-t border-border/50">
                        <div>
                          <p className="text-muted-foreground text-[10px] sm:text-xs">{t('search.checkIn')}</p>
                          <p className="font-medium">{format(new Date(checkIn), 'MMM d')}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-[10px] sm:text-xs">{t('search.checkOut')}</p>
                          <p className="font-medium">{format(new Date(checkOut), 'MMM d')}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-[10px] sm:text-xs">{t('search.guests')}</p>
                          <p className="font-medium">{adults + children}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground text-[10px] sm:text-xs">{t('search.nights', 'Nights')}</p>
                          <p className="font-medium">{nights}</p>
                        </div>
                      </div>
                    </div>

                    {/* Transfers Summary - Compact */}
                    {(selectedTransfer || selectedReturnTransfer) && (
                      <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-2 text-xs space-y-1">
                        <p className="font-medium flex items-center gap-1.5">
                          <Car className="w-3 h-3 text-blue-600" />
                          {t('transfer.includedTransfers', 'Transfers')}
                        </p>
                        <div className="flex flex-wrap gap-x-3 text-muted-foreground">
                          {selectedTransfer && (
                            <span>✓ {t('transfer.arrival', 'Arrival')}: €{selectedTransfer.price.toFixed(0)}</span>
                          )}
                          {selectedReturnTransfer && (
                            <span>✓ {t('transfer.return', 'Return')}: €{selectedReturnTransfer.price.toFixed(0)}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Price Summary - Compact */}
                    <div className="bg-[#00A9E0]/5 rounded-lg p-3">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
                        <span className="text-sm font-bold">{t('booking.totalPrice', 'Total Price')}</span>
                        <span className="text-2xl font-bold text-[#00A9E0]">
                          €{(calculatePricing(selectedRoomForBooking.price, validPass).finalTotal + (selectedTransfer?.price || 0) + (selectedReturnTransfer?.price || 0)).toFixed(2)}
                        </span>
                      </div>
                      {validPass && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          {t('booking.freeStaysPassApplied', 'Freestays Pass applied')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                
                <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
                  <Button variant="outline" onClick={() => setBookingConfirmOpen(false)} className="w-full sm:w-auto h-9 text-sm">
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button onClick={proceedToCheckout} className="gap-2 w-full sm:w-auto h-9 text-sm">
                    <Check className="w-4 h-4" />
                    {t('booking.agreeAndContinue', 'Continue to Checkout')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="md:col-span-1">
            <Card className="p-6">
              <h3 className="font-semibold mb-4">{t('booking.title', 'Your Stay')}</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-foreground/90">{t('search.checkIn')}</span>
                  <span className="font-medium">{format(new Date(checkIn), 'EEE, MMM d, yyyy')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground/90">{t('search.checkOut')}</span>
                  <span className="font-medium">{format(new Date(checkOut), 'EEE, MMM d, yyyy')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground/90">{t('search.guests')}</span>
                  <span className="font-medium">{adults} {t('search.adults')}{children > 0 ? `, ${children} ${t('search.children')}` : ''}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground/90">{t('search.nights', 'Nights')}</span>
                  <span className="font-medium">{nights}</span>
                </div>
              </div>

              <Separator className="my-4" />

              {validPass ? (
                <div className="bg-accent/20 rounded-lg p-4 text-center">
                  <Crown className="w-6 h-6 mx-auto mb-2 text-accent-foreground" />
                  <p className="text-sm font-medium">{t('pass.yourPass', 'Freestays Pass Active')}</p>
                  <p className="text-xs text-muted-foreground">{t('booking.noBookingFee', 'No booking fee')}</p>
                </div>
              ) : (
                <div className="bg-secondary rounded-lg p-4 text-center">
                  <p className="text-sm font-medium text-white mb-2">Get your Freestays Benefits With the</p>
                  <p className="text-xs text-white/80">€35 {t('pass.oneTimePass', 'One-Time Pass')} or €129 {t('pass.annualPass', 'Annual Pass')}</p>
                </div>
              )}
            </Card>
            
            {/* Hotels Nearby Section - Always visible in sidebar */}
            <Card className="p-4 mt-4" data-testid="nearby-hotels-sidebar">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-primary" />
                {t('hotel.nearbyHotels', 'Hotels nearby:')}
              </h3>
              
              {loadingNearby ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                </div>
              ) : nearbyHotels.length > 0 ? (
                <>
                  {/* Desktop: Compact list view */}
                  <div className="hidden lg:block space-y-3">
                    {nearbyHotels.map((nearbyHotel) => {
                      const nearbyNettPrice = nearbyHotel.min_price || 0; // Sunhotels price is already total for all nights
                      const nearbyPricing = calculatePricing(nearbyNettPrice, validPass);
                      return (
                        <div 
                          key={nearbyHotel.hotel_id} 
                          className="flex gap-3 p-2 rounded-lg hover:bg-secondary/50 cursor-pointer transition-colors group"
                          onClick={() => navigate(`/hotel/${nearbyHotel.hotel_id}?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}&destination=${encodeURIComponent(destination)}&destinationId=${destinationId}`)}
                          data-testid={`nearby-hotel-${nearbyHotel.hotel_id}`}
                        >
                          <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                            <img 
                              src={nearbyHotel.image_url || nearbyHotel.images?.[0] || '/api/public/hotel-fallback-image?width=400'} 
                              alt={nearbyHotel.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => { e.target.src = '/api/public/hotel-fallback-image?width=400'; }}
                            />
                            {nearbyHotel.star_rating && (
                              <div className="absolute bottom-0.5 left-0.5 bg-black/70 text-white px-1 py-0.5 rounded text-[10px] flex items-center gap-0.5">
                                <Star className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" />
                                {nearbyHotel.star_rating}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm line-clamp-1">{nearbyHotel.name}</h4>
                            <p className="text-xs text-muted-foreground line-clamp-1">{nearbyHotel.city || nearbyHotel.address}</p>
                            <p className="text-sm font-bold text-primary mt-1">
                              €{(nearbyPricing.finalTotal || 0).toFixed(0)}
                              <span className="text-xs font-normal text-muted-foreground ml-1">/ {nights} {t('search.nights', 'nights')}</span>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Mobile: Large card view like search results */}
                  <div className="lg:hidden space-y-4">
                    {nearbyHotels.map((nearbyHotel) => {
                      const nearbyNettPrice = nearbyHotel.min_price || 0; // Sunhotels price is already total for all nights
                      const nearbyPricing = calculatePricing(nearbyNettPrice, validPass);
                      return (
                        <Card 
                          key={nearbyHotel.hotel_id} 
                          className="overflow-hidden cursor-pointer hover:shadow-lg transition-all"
                          onClick={() => navigate(`/hotel/${nearbyHotel.hotel_id}?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}&destination=${encodeURIComponent(destination)}&destinationId=${destinationId}`)}
                          data-testid={`nearby-hotel-mobile-${nearbyHotel.hotel_id}`}
                        >
                          {/* Large Image */}
                          <div className="relative aspect-[16/10] w-full overflow-hidden">
                            <img 
                              src={nearbyHotel.image_url || nearbyHotel.images?.[0] || '/api/public/hotel-fallback-image?width=400'} 
                              alt={nearbyHotel.name}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                              onError={(e) => { e.target.src = '/api/public/hotel-fallback-image?width=400'; }}
                            />
                            {/* Star rating badge */}
                            {nearbyHotel.star_rating && (
                              <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded-md text-xs flex items-center gap-1">
                                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                {nearbyHotel.star_rating}
                              </div>
                            )}
                          </div>
                          
                          {/* Hotel Info */}
                          <div className="p-3">
                            <h4 className="font-semibold text-base line-clamp-1">{nearbyHotel.name}</h4>
                            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                              <MapPin className="w-3.5 h-3.5" />
                              <span className="line-clamp-1">{nearbyHotel.city || nearbyHotel.address}</span>
                            </div>
                            
                            {/* Price */}
                            <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
                              <div>
                                <p className="text-lg font-bold text-primary">
                                  €{(nearbyPricing.finalTotal || 0).toFixed(0)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {nights} {t('search.nights', 'nights')}
                                </p>
                              </div>
                              <Button size="sm" variant="outline" className="text-xs">
                                {t('hotel.viewDetails', 'View')}
                              </Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-2">
                  {t('hotel.noNearbyHotels', 'No nearby hotels found')}
                </p>
              )}
            </Card>
            
            {/* Location Map Section */}
            {hotel && (hotel.latitude || hotel.longitude) && (
              <Card className="p-4 mt-4" data-testid="hotel-location-map">
                <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-primary" />
                  {t('hotel.location', 'Location')}
                </h3>
                
                {/* Hotel Address */}
                {hotel.address && (
                  <p className="text-sm text-muted-foreground mb-3">
                    {hotel.address}{hotel.city ? `, ${hotel.city}` : ''}{hotel.country ? `, ${hotel.country}` : ''}
                  </p>
                )}
                
                {/* Google Map */}
                <div className="rounded-lg overflow-hidden mb-3" style={{ height: '200px' }}>
                  <div 
                    id="hotel-map" 
                    className="w-full h-full bg-secondary"
                    ref={(el) => {
                      if (!el || !hotel.latitude || !hotel.longitude) return;
                      const lat = parseFloat(hotel.latitude);
                      const lng = parseFloat(hotel.longitude);
                      if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return;
                      loadGoogleMaps().then(() => {
                        if (!window.google?.maps) return;
                        const map = new window.google.maps.Map(el, {
                          center: { lat, lng },
                          zoom: 15,
                          mapTypeControl: false,
                          streetViewControl: false,
                          fullscreenControl: false,
                          styles: [
                            { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }
                          ]
                        });
                        new window.google.maps.Marker({
                          position: { lat, lng },
                          map: map,
                          title: hotel.name,
                          icon: {
                            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="#1e3a5f" stroke="#ffffff" stroke-width="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                                <circle cx="12" cy="10" r="3" fill="#ffffff"/>
                              </svg>
                            `),
                            scaledSize: new window.google.maps.Size(40, 40),
                            anchor: new window.google.maps.Point(20, 40)
                          }
                        });
                      });
                    }}
                  />
                </div>
                
                {/* View on Google Maps Link */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full text-xs"
                  onClick={() => {
                    const lat = hotel.latitude;
                    const lng = hotel.longitude;
                    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
                  }}
                >
                  <ExternalLink className="w-3 h-3 mr-2" />
                  {t('hotel.viewOnGoogleMaps', 'View on Google Maps')}
                </Button>
                
                {/* Places of Interest */}
                {hotel.points_of_interest && hotel.points_of_interest.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                      {t('hotel.placesOfInterest', 'Places of interest')}
                    </h4>
                    <div className="space-y-1.5">
                      {hotel.points_of_interest.slice(0, 5).map((poi, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground truncate flex-1">{poi.name}</span>
                          <span className="font-medium ml-2">{poi.distance}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Airports */}
                {hotel.airports && hotel.airports.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide flex items-center gap-1">
                      <Plane className="w-3 h-3" />
                      {t('hotel.airports', 'Airports')}
                    </h4>
                    <div className="space-y-1.5">
                      {hotel.airports.slice(0, 3).map((airport, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground truncate flex-1">{airport.name}</span>
                          <span className="font-medium ml-2">{airport.distance}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MOBILE BOOKING SUMMARY ====================
const MobileBookingSummary = ({ 
  hotel, checkIn, checkOut, roomType, room, adults, children, currentBoardType, nights, pricing, hasDiscount, t,
  // Pass info
  passPurchaseType,
  user,
  socialProof,
  // Transfer props
  availableTransfers = [],
  availableReturnTransfers = [],
  selectedTransfer,
  setSelectedTransfer,
  selectedReturnTransfer,
  setSelectedReturnTransfer,
  includeTransfer,
  setIncludeTransfer,
  includeReturnTransfer,
  setIncludeReturnTransfer,
  loadingTransfers,
  transferId,
  returnTransferId,
  totalWithTransfer,
  // Transfer selector dialog props
  setShowTransferSelector,
  setTransferSelectorType
}) => {
  const [expanded, setExpanded] = useState(true); // Default OPEN on mobile
  
  if (!hotel) return null;
  
  // Calculate effective total with transfers for display
  const displayTotal = totalWithTransfer || pricing.finalTotal;
  
  return (
    <Card className="sticky top-16 z-40 rounded-2xl border-0 shadow-lg bg-white dark:bg-slate-900 overflow-hidden">
      {/* Collapsed View - Always Visible */}
      <div 
        className="p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-16 h-16 rounded-xl overflow-hidden shadow-sm bg-secondary flex-shrink-0">
            {(hotel.image_url || hotel.images?.[0]) ? (
              <img 
                src={hotel.image_url || hotel.images?.[0]} 
                alt={hotel.name}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Hotel className="w-6 h-6 text-muted-foreground/30" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm leading-tight line-clamp-1">{hotel.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {format(new Date(checkIn), 'MMM d')} - {format(new Date(checkOut), 'MMM d')} • {nights} {t('search.nights', 'nights')}
            </p>
            <div className="flex items-center justify-between mt-1">
              <span className="text-lg font-bold text-primary">€{displayTotal.toFixed(2)}</span>
              {hasDiscount && (
                <Badge variant="secondary" className="text-xs bg-success/10 text-success">
                  {isPackageBoard(currentBoardType) ? t('booking.roomIsFree', 'Room is Free') : t('booking.passDiscount', 'Pass Discount')} -€{pricing.discountAmount.toFixed(0)}
                </Badge>
              )}
            </div>
          </div>
          <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>
      
      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t">
          <div className="pt-3 space-y-3">
            {/* Room & Meal */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('booking.room', 'Room')}</span>
              <span className="font-medium text-right max-w-[60%] truncate">{roomType || room?.room_type}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('booking.mealplan', 'Mealplan')}</span>
              <span className="font-medium">{currentBoardType}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('search.guests', 'Guests')}</span>
              <span className="font-medium">{adults + children}</span>
            </div>

            {/* Selected Pass info */}
            {(passPurchaseType || user?.pass_type) && (passPurchaseType !== null || (user?.pass_type && user.pass_type !== 'free')) && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  {t('booking.selectedPass', 'Selected Pass')}
                </span>
                <span className="font-semibold text-primary">
                  {(passPurchaseType === 'annual' || user?.pass_type === 'annual')
                    ? t('pass.annualPass', 'Annual Pass')
                    : (passPurchaseType === 'one_time' || user?.pass_type === 'one_time')
                      ? t('pass.oneTimePass', 'One-Time Pass')
                      : user?.pass_type === 'b2b'
                        ? 'B2B Pass'
                        : ''}
                </span>
              </div>
            )}

            <Separator />
            
            {/* Price Breakdown */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('booking.roomPrice', 'Room')} × {nights}</span>
                <span>€{pricing.priceBeforeDiscount.toFixed(2)}</span>
              </div>
              {hasDiscount && (
                <div className="flex justify-between text-sm text-success">
                  <span>{isPackageBoard(currentBoardType) ? t('booking.roomIsFree', 'Room is Free') : t('booking.passDiscount', 'Pass Discount')}</span>
                  <span>-€{pricing.discountAmount.toFixed(2)}</span>
                </div>
              )}
              {pricing.bookingFee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('booking.bookingFee', 'Booking fee')}</span>
                  <span>€{pricing.bookingFee.toFixed(2)}</span>
                </div>
              )}
              {pricing.referralDiscountApplied && (
                <div className="flex justify-between text-sm text-success">
                  <span className="flex items-center gap-1.5">
                    <Gift className="w-3.5 h-3.5" />
                    {t('booking.referralDiscount', 'Referral Discount')}
                  </span>
                  <span className="font-medium">-€{BOOKING_FEE.toFixed(2)}</span>
                </div>
              )}
              {pricing.passPrice > 0 && (
                <div className="flex justify-between text-sm text-primary font-medium">
                  <span>{passPurchaseType === 'annual' ? t('pass.annualPass', 'Annual Pass') : t('pass.oneTimePass', 'One-Time Pass')}</span>
                  <span>€{pricing.passPrice.toFixed(2)}</span>
                </div>
              )}
            </div>
            
            {/* Transfer Section - Mobile */}
            {!transferId && !returnTransferId && (availableTransfers.length > 0 || availableReturnTransfers.length > 0) && (
              <>
                <Separator />
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-primary flex items-center gap-2">
                    <Car className="w-4 h-4" />
                    {t('transfer.addAirportTransfer', 'Add Airport Transfer')}
                  </p>
                  
                  {/* Arrival Transfer */}
                  {availableTransfers.length > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between p-2 bg-[#00A9E0]/5 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="mobile-add-arrival-transfer"
                            checked={!!selectedTransfer}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedTransfer(availableTransfers[0]);
                                setIncludeTransfer(true);
                              } else {
                                setSelectedTransfer(null);
                                setIncludeTransfer(false);
                              }
                            }}
                          />
                          <Label htmlFor="mobile-add-arrival-transfer" className="text-xs cursor-pointer flex items-center gap-1">
                            <PlaneTakeoff className="w-3 h-3 text-primary" />
                            {t('transfer.arrival', 'Arrival')} ({checkIn})
                          </Label>
                        </div>
                        {selectedTransfer ? (
                          <span className="text-xs font-semibold text-primary">€{selectedTransfer.price.toFixed(2)}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('transfer.fromPrice', 'from')} €{Math.min(...availableTransfers.map(t => t.price)).toFixed(2)}</span>
                        )}
                      </div>
                      {selectedTransfer && setShowTransferSelector && (
                        <div className="ml-6 text-xs text-muted-foreground flex items-center justify-between">
                          <span className="truncate max-w-[150px]">{selectedTransfer.airport_name} - {t(`transferVehicle.${selectedTransfer.vehicle_type}`, selectedTransfer.vehicle_type)}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-xs h-5 px-2 text-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTransferSelectorType('arrival');
                              setShowTransferSelector(true);
                            }}
                          >
                            {t('transfer.change', 'Change')}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Return Transfer */}
                  {availableReturnTransfers.length > 0 && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between p-2 bg-[#00A9E0]/5 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Checkbox 
                            id="mobile-add-return-transfer"
                            checked={!!selectedReturnTransfer}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedReturnTransfer(availableReturnTransfers[0]);
                                setIncludeReturnTransfer(true);
                              } else {
                                setSelectedReturnTransfer(null);
                                setIncludeReturnTransfer(false);
                              }
                            }}
                          />
                          <Label htmlFor="mobile-add-return-transfer" className="text-xs cursor-pointer flex items-center gap-1">
                            <Plane className="w-3 h-3 text-primary" />
                            {t('transfer.return', 'Return')} ({checkOut})
                          </Label>
                        </div>
                        {selectedReturnTransfer ? (
                          <span className="text-xs font-semibold text-primary">€{selectedReturnTransfer.price.toFixed(2)}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('transfer.fromPrice', 'from')} €{Math.min(...availableReturnTransfers.map(t => t.price)).toFixed(2)}</span>
                        )}
                      </div>
                      {selectedReturnTransfer && setShowTransferSelector && (
                        <div className="ml-6 text-xs text-muted-foreground flex items-center justify-between">
                          <span className="truncate max-w-[150px]">{selectedReturnTransfer.airport_name} - {t(`transferVehicle.${selectedReturnTransfer.vehicle_type}`, selectedReturnTransfer.vehicle_type)}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-xs h-5 px-2 text-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTransferSelectorType('return');
                              setShowTransferSelector(true);
                            }}
                          >
                            {t('transfer.change', 'Change')}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Transfer Subtotal */}
                  {(selectedTransfer || selectedReturnTransfer) && (
                    <div className="flex justify-between text-xs pt-1">
                      <span className="text-muted-foreground">{t('transfer.transferSubtotal', 'Transfer subtotal')}</span>
                      <span className="font-semibold">€{((selectedTransfer?.price || 0) + (selectedReturnTransfer?.price || 0)).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* Loading transfers indicator */}
            {loadingTransfers && (
              <>
                <Separator />
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {t('transfer.loadingTransfers', 'Loading transfer options...')}
                </div>
              </>
            )}
            
            <Separator />
            
            <div className="flex justify-between items-center">
              <span className="font-semibold">{t('booking.total', 'Total')}</span>
              <span className="text-xl font-bold text-primary">€{displayTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

// ==================== BOOKING PAGE ====================
const BookingPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, hasValidPass } = useAuth();
  
  const hotelId = searchParams.get('hotelId');
  const roomId = searchParams.get('roomId');
  const checkIn = searchParams.get('checkIn');
  const checkOut = searchParams.get('checkOut');
  const adults = parseInt(searchParams.get('adults') || '2');
  const children = parseInt(searchParams.get('children') || '0');
  // Parse children ages from URL (comma-separated). Default to 10y per missing entry
  // so Sunhotels never rejects the booking on missing childrenAges.
  const childrenAges = (() => {
    const raw = searchParams.get('childrenAges') || '';
    const parsed = raw.split(',').filter(a => a !== '').map(a => parseInt(a)).filter(n => !isNaN(n));
    while (parsed.length < children) parsed.push(10);
    return parsed.slice(0, children);
  })();
  const nettPrice = parseFloat(searchParams.get('price') || '0');
  const roomType = decodeURIComponent(searchParams.get('roomType') || '');
  const boardType = decodeURIComponent(searchParams.get('boardType') || '');
  const sunhotelsRoomTypeId = searchParams.get('sunhotelsRoomTypeId') || '';
  const sunhotelsBlockId = searchParams.get('sunhotelsBlockId') || '';
  const initialPassPurchase = searchParams.get('passPurchase'); // 'one_time' or 'annual'
  const b2c = parseInt(searchParams.get('b2c') || '0');  // 1 for last minute deals
  const destinationId = searchParams.get('destinationId') || '';
  const destination = decodeURIComponent(searchParams.get('destination') || '');
  
  // Arrival Transfer params from hotel detail page
  const transferId = searchParams.get('transferId') || '';
  const transferPrice = parseFloat(searchParams.get('transferPrice') || '0');
  const transferVehicle = decodeURIComponent(searchParams.get('transferVehicle') || '');
  const transferTime = searchParams.get('transferTime') || '';
  const transferFlight = searchParams.get('transferFlight') || '';
  
  // Return Transfer params from hotel detail page
  const returnTransferId = searchParams.get('returnTransferId') || '';
  const returnTransferPrice = parseFloat(searchParams.get('returnTransferPrice') || '0');
  const returnTransferVehicle = decodeURIComponent(searchParams.get('returnTransferVehicle') || '');
  const returnTransferTime = searchParams.get('returnTransferTime') || '';
  const returnTransferFlight = searchParams.get('returnTransferFlight') || '';
  
  // Check if this is a pass-only purchase (no hotel booking)
  const isPassOnlyPurchase = !hotelId && initialPassPurchase;
  
  const nights = (checkIn && checkOut) ? differenceInDays(parseLocalDate(checkOut), parseLocalDate(checkIn)) : 0;
  const validPass = hasValidPass?.() || false;
  const referralDiscount = user?.referral_discount || 0;

  const [useCredits, setUseCredits] = useState(false);
  const [availableCredits, setAvailableCredits] = useState(0);

  // Fetch user's travel credits
  useEffect(() => {
    if (user) {
      const fetchCredits = async () => {
        try {
          const token = localStorage.getItem('freestays_token');
          const response = await axios.get(`${API}/user/travel-credits`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAvailableCredits(response.data.available_credits || 0);
        } catch (error) {
          console.log('Could not fetch credits');
        }
      };
      fetchCredits();
    }
  }, [user]);

  const [formData, setFormData] = useState({
    firstName: user?.first_name || user?.name?.split(' ')[0] || '',
    lastName: user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '',
    email: user?.email || '',
    phone: user?.phone || '',
    country: user?.country || getCustomerCountry() || 'NL',
    specialRequests: '',
    tcKimlikNumber: '',  // Audit trail for TR bookings by Turkish guests
    // Additional guests (guest 2 onwards)
    additionalGuests: []
  });

  // Nationality Gate config (drives ALL TC Kimlik flow — popup, filters, checkout field)
  // When the CMS rule is disabled, ALL TC Kimlik UI disappears automatically.
  const [bookingNatGate, setBookingNatGate] = useState(null);
  useEffect(() => {
    getNationalityGateConfig().then(setBookingNatGate).catch(() => setBookingNatGate({ enabled: false }));
  }, []);

  // Load profile data on mount if user is logged in
  useEffect(() => {
    if (user?.user_id) {
      // Pre-fill from user profile data if available
      setFormData(prev => ({
        ...prev,
        firstName: user?.first_name || prev.firstName || user?.name?.split(' ')[0] || '',
        lastName: user?.last_name || prev.lastName || user?.name?.split(' ').slice(1).join(' ') || '',
        email: user?.email || prev.email,
        phone: user?.phone || prev.phone
      }));
    }
  }, [user]);

  // Initialize additional guests when adults/children change.
  // Pre-fills the age field for children from the search query (childrenAges)
  // so guests don't have to re-enter what they already chose.
  useEffect(() => {
    const totalGuests = adults + children;
    if (totalGuests > 1) {
      const additionalGuestsNeeded = totalGuests - 1;
      const currentAdditional = formData.additionalGuests || [];
      if (currentAdditional.length !== additionalGuestsNeeded) {
        const newAdditionalGuests = [];
        for (let i = 0; i < additionalGuestsNeeded; i++) {
          const existing = currentAdditional[i] || {};
          const isChild = i >= (adults - 1);
          // Child index = position among children (0-based)
          const childIdx = isChild ? i - (adults - 1) : -1;
          const prefilledAge = isChild && childrenAges[childIdx] != null
            ? String(childrenAges[childIdx])
            : '';
          newAdditionalGuests.push({
            firstName: existing.firstName || '',
            lastName: existing.lastName || '',
            isChild,
            age: existing.age || prefilledAge,
          });
        }
        setFormData(prev => ({...prev, additionalGuests: newAdditionalGuests}));
      }
    }
  }, [adults, children, childrenAges.join(',')]);  // eslint-disable-line react-hooks/exhaustive-deps

  const updateAdditionalGuest = (index, field, value) => {
    const newGuests = [...formData.additionalGuests];
    newGuests[index] = {...newGuests[index], [field]: value};
    setFormData({...formData, additionalGuests: newGuests});
  };

  const [existingPassCode, setExistingPassCode] = useState('');
  const [passCodeValid, setPassCodeValid] = useState(false);
  const [passCodeChecking, setPassCodeChecking] = useState(false);
  const [firstBookingFeeWaived, setFirstBookingFeeWaived] = useState(false);
  const [passPurchaseType, setPassPurchaseType] = useState(initialPassPurchase || null); // null, 'one_time', 'annual'
  const [loading, setLoading] = useState(false);
  const [showDiscountPopup, setShowDiscountPopup] = useState(false);
  
  // Transfer state from URL params
  const [includeTransfer, setIncludeTransfer] = useState(!!transferId);
  const [includeReturnTransfer, setIncludeReturnTransfer] = useState(!!returnTransferId);
  
  // Additional transfer state for in-checkout selection
  const [availableTransfers, setAvailableTransfers] = useState([]);
  const [availableReturnTransfers, setAvailableReturnTransfers] = useState([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [transferSettings, setTransferSettings] = useState(null);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [selectedReturnTransfer, setSelectedReturnTransfer] = useState(null);
  const [showTransferSelector, setShowTransferSelector] = useState(false);
  const [transferSelectorType, setTransferSelectorType] = useState('arrival'); // 'arrival' or 'return'
  
  // Load transfers if not already selected from hotel page
  useEffect(() => {
    const loadTransfers = async () => {
      if (transferId || !hotelId) return; // Already have transfer or no hotel
      
      try {
        // Get transfer settings
        const settingsRes = await axios.get(`${API}/transfers/settings`);
        setTransferSettings(settingsRes.data);
        
        if (settingsRes.data?.enabled) {
          setLoadingTransfers(true);
          
          // Search for arrival transfers
          const arrivalRes = await axios.post(`${API}/transfers/search`, {
            hotel_id: hotelId,
            arrival_date: checkIn,
            arrival_time: '12:00',
            transfer_type: 'arrival'
          });
          
          if (arrivalRes.data?.success && arrivalRes.data?.transfers?.length > 0) {
            setAvailableTransfers(arrivalRes.data.transfers);
          }
          
          // Search for return transfers
          const returnRes = await axios.post(`${API}/transfers/search`, {
            hotel_id: hotelId,
            arrival_date: checkOut,
            departure_date: checkOut,
            departure_time: '12:00',
            transfer_type: 'departure'
          });
          
          if (returnRes.data?.success && returnRes.data?.transfers?.length > 0) {
            setAvailableReturnTransfers(returnRes.data.transfers);
          }
        }
      } catch (error) {
        console.error('Error loading transfers:', error);
      } finally {
        setLoadingTransfers(false);
      }
    };
    
    loadTransfers();
  }, [hotelId, checkIn, checkOut, transferId]);

  // Calculate pricing based on pass status and referral discount
  const hasDiscount = validPass || passCodeValid || passPurchaseType;
  const pricing = calculatePricing(nettPrice, validPass || passCodeValid, passPurchaseType, referralDiscount, firstBookingFeeWaived);
  
  // Calculate total with transfers - use either URL params or in-checkout selected transfers
  const effectiveTransferPrice = transferId ? transferPrice : (selectedTransfer?.price || 0);
  const effectiveReturnTransferPrice = returnTransferId ? returnTransferPrice : (selectedReturnTransfer?.price || 0);
  const arrivalTransferAmount = includeTransfer ? effectiveTransferPrice : 0;
  const returnTransferAmount = includeReturnTransfer ? effectiveReturnTransferPrice : 0;
  const totalTransferAmount = arrivalTransferAmount + returnTransferAmount;
  const totalWithTransfer = pricing.finalTotal + totalTransferAmount;
  
  // Calculate credit discount (cap at total minus €0.50 Stripe minimum)
  const creditDiscount = useCredits ? Math.min(availableCredits, Math.max(0, totalWithTransfer - 0.50)) : 0;
  const finalTotalAfterCredits = totalWithTransfer - creditDiscount;

  const { data: hotel } = useQuery({
    queryKey: ['hotel', hotelId],
    queryFn: async () => {
      const response = await axios.get(`${API}/hotels/${hotelId}`);
      return response.data;
    },
    enabled: !!hotelId && !isPassOnlyPurchase
  });

  // Social proof counters (viewers + bookings 24h)
  const { data: socialProof } = useQuery({
    queryKey: ['social-proof', hotelId],
    queryFn: async () => {
      const r = await axios.get(`${API}/hotels/${hotelId}/social-proof`);
      return r.data || {};
    },
    enabled: !!hotelId && !isPassOnlyPurchase,
    refetchInterval: 60_000,  // refresh every minute
    staleTime: 30_000,
  });

  const room = hotel?.rooms?.find(r => r.room_id === roomId);
  
  // Determine if this is a package board type (must come after room is defined)
  const isPackage = isPackageBoard(boardType || room?.board_type);
  const currentBoardType = boardType || room?.board_type || 'Room Only';

  // Show popup when discount is applied
  useEffect(() => {
    if (hasDiscount && pricing.discountAmount > 0) {
      setShowDiscountPopup(true);
    }
  }, [hasDiscount, pricing.discountAmount]);

  const validatePassCode = async () => {
    if (!existingPassCode || existingPassCode.length < 4) return;
    
    setPassCodeChecking(true);
    try {
      const response = await axios.post(`${API}/pass-code/validate`, {
        pass_code: existingPassCode
      });
      
      if (response.data.valid && response.data.has_discount) {
        setPassCodeValid(true);
        setPassPurchaseType(null); // Don't need to buy pass if valid code
        // Check if first booking fee is waived for this pass holder
        setFirstBookingFeeWaived(response.data.first_booking_fee_waived || false);
        if (response.data.first_booking_fee_waived) {
          toast.success(`${response.data.message} Geen boekingskosten!`);
        } else {
          toast.success(response.data.message);
        }
      } else {
        setPassCodeValid(false);
        setFirstBookingFeeWaived(false);
        toast.error(response.data.message || "Invalid pass code");
      }
    } catch (error) {
      setPassCodeValid(false);
      setFirstBookingFeeWaived(false);
      toast.error("Could not validate pass code");
    } finally {
      setPassCodeChecking(false);
    }
  };

  const [checkoutState, setCheckoutState] = useState(null); // { clientSecret, amount, bookingId, paymentIntentId }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // TC Kimlik validation: only enforce when CMS rule is enabled + TR nationality + TR hotel
    const hotelCountryUpper = String(hotel?.country || '').toUpperCase();
    const isTurkeyHotel = hotelCountryUpper === 'TURKEY' || hotelCountryUpper === 'TURKIJE' || hotelCountryUpper === 'TÜRKIYE' || hotelCountryUpper === 'TR';
    const tcRequired = !!(
      bookingNatGate?.enabled &&
      bookingNatGate.trigger_country === formData.country &&
      isTurkeyHotel
    );
    if (tcRequired) {
      if (!formData.tcKimlikNumber || formData.tcKimlikNumber.length !== 11 || !/^\d{11}$/.test(formData.tcKimlikNumber)) {
        toast.error(t('booking.tcKimlikInvalid', 'TC Kimlik number must be exactly 11 digits.'));
        return;
      }
    }

    // Adult guest name validation — Sunhotels BookV3 requires one full name per adult.
    // We need (adults - 1) additional adult entries (lead guest is already captured
    // via formData.firstName + lastName). Each must have BOTH firstName + lastName.
    const adultGuests = (formData.additionalGuests || []).filter(g => !g.isChild);
    const expectedAdultExtras = Math.max(0, (adults || 1) - 1);
    if (adultGuests.length < expectedAdultExtras) {
      toast.error(
        t('booking.adultNamesMissing',
          'Please provide a first and last name for each adult guest.')
      );
      setTimeout(() => {
        document.querySelector('[data-testid="additional-guests-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    const invalidAdult = adultGuests.find(g =>
      !String(g.firstName || '').trim() || !String(g.lastName || '').trim()
    );
    if (invalidAdult) {
      toast.error(
        t('booking.adultNamesMissing',
          'Please provide a first and last name for each adult guest.')
      );
      setTimeout(() => {
        document.querySelector('[data-testid="additional-guests-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }

    // Child age validation — every child entry must have a valid age (0-17).
    const childEntries = (formData.additionalGuests || []).filter(g => g.isChild);
    const invalidChild = childEntries.find(g => {
      const a = parseInt(g.age);
      return !Number.isFinite(a) || a < 0 || a > 17;
    });
    if (invalidChild) {
      toast.error(
        t('booking.childAgeInvalid', 'Please provide a valid age (0–17) for each child.')
      );
      setTimeout(() => {
        document.querySelector('[data-testid="additional-guests-section"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }

    setLoading(true);

    // Build the final children_ages list from the additionalGuests form
    // (user may have edited an age in the checkout). Fall back to URL values.
    const childAgesFromForm = (formData.additionalGuests || [])
      .filter((g) => g.isChild)
      .map((g, i) => {
        const a = parseInt(g.age);
        return Number.isFinite(a) ? a : (childrenAges[i] ?? 10);
      });
    const finalChildrenAges = childAgesFromForm.length === children
      ? childAgesFromForm
      : childrenAges;

    try {
      const bookingResponse = await axios.post(`${API}/bookings`, {
        hotel_id: hotelId,
        room_id: roomId,
        hotel_name: hotel?.name,
        room_type: roomType || room?.room_type,
        check_in: checkIn,
        check_out: checkOut,
        adults,
        children,
        children_ages: finalChildrenAges,
        guest_first_name: formData.firstName,
        guest_last_name: formData.lastName,
        guest_email: formData.email,
        guest_phone: formData.phone,
        // Additional ADULT guest names (children are handled via children_ages).
        // Sunhotels BookV3 requires a name per adult; without this, bookings with
        // 2+ adults fail with "adultsList count != numberOfAdults".
        additional_guests: (formData.additionalGuests || [])
          .filter(g => !g.isChild && (String(g.firstName || '').trim() || String(g.lastName || '').trim()))
          .map(g => ({
            first_name: String(g.firstName || '').trim(),
            last_name: String(g.lastName || '').trim(),
          })),
        customer_country: formData.country || getCustomerCountry(),
        special_requests: formData.specialRequests,
        tc_kimlik_number: tcRequired ? formData.tcKimlikNumber : null,
        total_price: nettPrice,
        currency: 'EUR',
        pass_code: passCodeValid ? existingPassCode : (validPass ? user?.pass_code : null),
        pass_purchase_type: passPurchaseType,
        board_type: currentBoardType,
        sunhotels_room_type_id: sunhotelsRoomTypeId,
        sunhotels_block_id: sunhotelsBlockId,
        use_referral_discount: referralDiscount > 0,
        is_last_minute: b2c === 1,  // b2c=1 for Last Minute deals
        // Arrival Transfer details if selected
        transfer_selected: includeTransfer,
        transfer_id: includeTransfer ? transferId : null,
        transfer_price: includeTransfer ? transferPrice : null,
        transfer_pickup: includeTransfer ? 'Airport' : null,
        transfer_dropoff: includeTransfer ? hotel?.name : null,
        transfer_date: includeTransfer ? checkIn : null,
        transfer_time: includeTransfer ? transferTime : null,
        transfer_flight_number: includeTransfer ? transferFlight : null,
        // Return Transfer details if selected
        return_transfer_selected: includeReturnTransfer,
        return_transfer_id: includeReturnTransfer ? returnTransferId : null,
        return_transfer_price: includeReturnTransfer ? returnTransferPrice : null,
        return_transfer_pickup: includeReturnTransfer ? hotel?.name : null,
        return_transfer_dropoff: includeReturnTransfer ? 'Airport' : null,
        return_transfer_date: includeReturnTransfer ? checkOut : null,
        return_transfer_time: includeReturnTransfer ? returnTransferTime : null,
        return_transfer_flight_number: includeReturnTransfer ? returnTransferFlight : null
      }, {
        headers: user ? { Authorization: `Bearer ${localStorage.getItem('freestays_token')}` } : {}
      });

      // Calculate total amount including all transfers minus credits
      const totalAmount = bookingResponse.data.final_price + totalTransferAmount - creditDiscount;

      // Create PaymentIntent for embedded checkout
      const paymentResponse = await axios.post(`${API}/payments/create-payment-intent`, {
        booking_id: bookingResponse.data.booking_id,
        amount: totalAmount,
        currency: 'EUR',
        credits_applied: creditDiscount
      });

      setCheckoutState({
        clientSecret: paymentResponse.data.client_secret,
        paymentIntentId: paymentResponse.data.payment_intent_id,
        bookingId: bookingResponse.data.booking_id,
        amount: totalAmount,
        publishableKey: paymentResponse.data.publishable_key
      });
      setLoading(false);

    } catch (error) {
      toast.error(error.response?.data?.detail || "Booking failed. Please try again.");
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    setLoading(true);
    try {
      // Deduct travel credits if used
      if (useCredits && creditDiscount > 0) {
        try {
          await axios.post(`${API}/user/use-credits`, {
            amount: creditDiscount,
            booking_id: checkoutState.bookingId
          }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('freestays_token')}` }
          });
        } catch (creditErr) {
          console.error('Credit deduction failed:', creditErr);
        }
      }
      
      const confirmRes = await axios.post(`${API}/payments/confirm-embedded`, {
        payment_intent_id: paymentIntent.id,
        booking_id: checkoutState.bookingId
      });
      if (confirmRes.data.success) {
        navigate(`/booking/success?booking_id=${checkoutState.bookingId}`);
      } else {
        toast.error("Payment received but booking confirmation pending. We'll contact you.");
        navigate(`/booking/success?booking_id=${checkoutState.bookingId}`);
      }
    } catch {
      // Payment succeeded at Stripe, navigate to success anyway
      navigate(`/booking/success?booking_id=${checkoutState.bookingId}`);
    }
  };

  const handlePaymentCancel = () => {
    setCheckoutState(null);
  };

  // Handle pass-only purchase
  const [passCheckoutState, setPassCheckoutState] = useState(null);
  
  const handlePassOnlyPurchase = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/payments/create-pass-intent`, {
        pass_type: passPurchaseType,
        user_email: user?.email || formData.email
      }, {
        headers: user ? { Authorization: `Bearer ${localStorage.getItem('freestays_token')}` } : {}
      });
      
      setPassCheckoutState({
        clientSecret: response.data.client_secret,
        paymentIntentId: response.data.payment_intent_id,
        purchaseId: response.data.purchase_id,
        amount: response.data.amount,
        publishableKey: response.data.publishable_key
      });
      setLoading(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Purchase failed. Please try again.");
      setLoading(false);
    }
  };

  const handlePassPaymentSuccess = async (paymentIntent) => {
    toast.success("Pass purchased successfully!");
    navigate('/');
  };

  // If pass-only purchase and no hotel, show pass purchase UI
  if (isPassOnlyPurchase) {
    const passPrice = passPurchaseType === 'annual' ? 129 : 35;
    const passName = passPurchaseType === 'annual' ? 'Annual Pass' : 'One-Time Pass';
    
    return (
      <div className="min-h-screen pt-24 pb-20 notranslate" translate="no">
        <Header />
        <div className="max-w-2xl mx-auto px-4">
          <Card className="rounded-3xl overflow-hidden shadow-xl">
            <div className="bg-gradient-to-br from-primary to-primary/80 p-8 text-center text-primary-foreground">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-10 h-10" />
              </div>
              <h1 className="font-sans font-bold text-3xl mb-2">Purchase {passName}</h1>
              <p className="text-primary-foreground/80">
                {passPurchaseType === 'annual' 
                  ? 'Use your Annual Pass for unlimited bookings'
                  : 'Use your Voucher for a one time booking'}
              </p>
            </div>
            
            <div className="p-8">
              <div className="bg-gray-50 dark:bg-[#0a2540] rounded-2xl p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-semibold">{passName}</span>
                  <span className="text-2xl font-bold text-[#00A9E0]">€{passPrice}</span>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#00A9E0]" />
                    Booking with meal package, the room is Free
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#00A9E0]" />
                    Up to 30% discount on room only booking
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#00A9E0]" />
                    No booking fees
                  </li>
                  {passPurchaseType === 'annual' ? (
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#00A9E0]" />
                      Valid for 1 year with unlimited bookings
                    </li>
                  ) : (
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-[#00A9E0]" />
                      Valid for your next booking
                    </li>
                  )}
                </ul>
              </div>
              
              {!user && (
                <div className="mb-6">
                  <Label className="text-sm font-medium">Your Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder={t('booking.emailPlaceholder')}
                    className="h-12 rounded-xl mt-1.5"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('passPage.passSentToEmail', 'Your pass code will be sent to this email')}</p>
                </div>
              )}
              
              {passCheckoutState ? (
                <EmbeddedCheckout
                  clientSecret={passCheckoutState.clientSecret}
                  amount={passCheckoutState.amount}
                  currency="EUR"
                  publishableKey={passCheckoutState.publishableKey}
                  onSuccess={handlePassPaymentSuccess}
                  onCancel={() => setPassCheckoutState(null)}
                  bookingDetails={{ hotelName: passName, dates: passPurchaseType === 'annual' ? t('passPage.validYear', 'Valid for 1 year') : t('passPage.validBooking', 'Valid for 1 booking') }}
                />
              ) : (
                <>
                  <Button
                    onClick={handlePassOnlyPurchase}
                    disabled={loading || (!user && !formData.email)}
                    className="w-full h-14 rounded-xl text-lg font-semibold"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CreditCard className="w-5 h-5 mr-2" />}
                    {t('checkout.pay', 'Pay')} €{passPrice} {t('passPage.now', 'Now')}
                  </Button>
                  
                  <p className="text-xs text-center text-muted-foreground mt-4">
                    {t('checkout.securedByStripe', 'Secured by Stripe')}
                  </p>
                </>
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Discount message based on board type
  const DiscountMessage = () => {
    if (!hasDiscount || pricing.discountAmount <= 0) return null;
    
    if (isPackage) {
      return (
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00A9E0] to-[#0077B6] flex items-center justify-center mx-auto mb-4">
            <Gift className="w-10 h-10 text-white" />
          </div>
          <h3 className="font-bold text-2xl mb-2 text-primary">
            {t('booking.yourRoomFree', 'Your room is for FREE!')}
          </h3>
          <p className="text-primary font-medium mb-2">{t('booking.thatsFreestays', "That's Freestays!")}</p>
          <p className="text-muted-foreground">
            {t('booking.discountCoversRoom', 'With {{boardType}}, your discount of €{{amount}} covers the room cost!', { boardType: currentBoardType, amount: pricing.discountAmount.toFixed(2) })}
          </p>
        </div>
      );
    }
    
    return (
      <div className="text-center">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-4">
          <Tag className="w-10 h-10 text-white" />
        </div>
        <h3 className="font-bold text-2xl mb-2 text-amber-700">
          {t('booking.yourDiscountIs', 'Your discount is €{{amount}}', { amount: pricing.discountAmount.toFixed(2) })}
        </h3>
        <p className="text-muted-foreground">
          {t('booking.freestaysDiscountOn', 'Freestays discount on your {{boardType}} booking', { boardType: currentBoardType })}
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-background to-background dark:from-slate-900 dark:via-background" data-testid="booking-page">
      {/* Discount Popup */}
      <Dialog open={showDiscountPopup} onOpenChange={setShowDiscountPopup}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-center font-sans font-bold text-xl">
              {isPackage ? t('booking.amazingDealTitle', 'Amazing Deal!') : t('booking.greatSavingsTitle', 'Great Savings!')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <DiscountMessage />
          </div>
          <DialogFooter>
            <Button onClick={() => setShowDiscountPopup(false)} className="w-full rounded-full h-12 font-semibold">
              {t('booking.continueBooking', 'Continue Booking')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header Section */}
      <div className="bg-white dark:bg-slate-900 border-b shadow-sm pt-20">
        <div className="max-w-[1760px] mx-auto px-4 md:px-8 py-6">
          {/* Breadcrumbs */}
          <Breadcrumbs items={[
            { label: t('search.searchTitle', 'Search'), href: '/search' },
            hotel ? { label: hotel.name, href: `/hotel/${hotelId}?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}` } : null,
            { label: t('booking.title', 'Booking') }
          ].filter(Boolean)} />
          
          {/* Premium Progress Stepper */}
          <div className="mt-6">
            <div className="flex items-center justify-center max-w-xl mx-auto">
              {/* Step 1: Select Room - Completed */}
              <div className="flex flex-col items-center flex-1">
                <div className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/30">
                  <Check className="w-6 h-6" />
                </div>
                <span className="text-xs font-medium mt-2 text-primary">{t('booking.step1', 'Room')}</span>
              </div>
              
              {/* Connector 1 */}
              <div className="flex-1 h-1 bg-primary rounded-full mx-2 -mt-4" />
              
              {/* Step 2: Guest Details - Active */}
              <div className="flex flex-col items-center flex-1">
                <div className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/30 ring-4 ring-primary/20">
                  <span className="text-lg font-bold">2</span>
                </div>
                <span className="text-xs font-medium mt-2 text-primary">{t('booking.step2', 'Details')}</span>
              </div>
              
              {/* Connector 2 */}
              <div className="flex-1 h-1 bg-border rounded-full mx-2 -mt-4" />
              
              {/* Step 3: Payment - Pending */}
              <div className="flex flex-col items-center flex-1">
                <div className="w-12 h-12 rounded-2xl bg-secondary text-muted-foreground flex items-center justify-center">
                  <span className="text-lg font-bold">3</span>
                </div>
                <span className="text-xs font-medium mt-2 text-muted-foreground">{t('booking.step3', 'Payment')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1760px] mx-auto px-4 md:px-8 py-8">
        {/* Page Title */}
        <div className="text-center mb-8 hidden lg:block">
          <h1 className="font-sans font-bold text-3xl md:text-4xl mb-2">{t('booking.title')}</h1>
          <p className="text-muted-foreground">{t('booking.subtitle', "You're almost there! Complete your details below.")}</p>
        </div>

        {/* Mobile: Summary at Top (Sticky) */}
        <div className="lg:hidden mb-6">
          <MobileBookingSummary 
            hotel={hotel}
            checkIn={checkIn}
            checkOut={checkOut}
            roomType={roomType}
            room={room}
            adults={adults}
            children={children}
            currentBoardType={currentBoardType}
            nights={nights}
            pricing={pricing}
            hasDiscount={hasDiscount}
            t={t}
            passPurchaseType={passPurchaseType}
            user={user}
            socialProof={socialProof}
            // Transfer props
            availableTransfers={availableTransfers}
            availableReturnTransfers={availableReturnTransfers}
            selectedTransfer={selectedTransfer}
            setSelectedTransfer={setSelectedTransfer}
            selectedReturnTransfer={selectedReturnTransfer}
            setSelectedReturnTransfer={setSelectedReturnTransfer}
            includeTransfer={includeTransfer}
            setIncludeTransfer={setIncludeTransfer}
            includeReturnTransfer={includeReturnTransfer}
            setIncludeReturnTransfer={setIncludeReturnTransfer}
            loadingTransfers={loadingTransfers}
            transferId={transferId}
            returnTransferId={returnTransferId}
            totalWithTransfer={totalWithTransfer}
            // Transfer selector dialog props
            setShowTransferSelector={setShowTransferSelector}
            setTransferSelectorType={setTransferSelectorType}
          />
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form Column */}
          <div className="lg:col-span-2">
            {/* Mobile Title */}
            <div className="text-center mb-6 lg:hidden">
              <h1 className="font-sans font-bold text-2xl mb-1">{t('booking.completeBooking', 'Complete your booking')}</h1>
            </div>
            
            {checkoutState ? (
              <div className="space-y-6 notranslate" translate="no">
                <div className="text-center">
                  <h1 className="font-sans font-bold text-2xl mb-1">{t('booking.completePayment', 'Complete your payment')}</h1>
                  <p className="text-muted-foreground text-sm">{t('booking.securePayment', 'Secure payment for your booking')}</p>
                </div>
                <EmbeddedCheckout
                  clientSecret={checkoutState.clientSecret}
                  amount={checkoutState.amount}
                  currency="EUR"
                  publishableKey={checkoutState.publishableKey}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handlePaymentCancel}
                  bookingId={checkoutState.bookingId}
                  bookingDetails={{
                    hotelName: hotel?.name,
                    roomType: roomType || room?.room_type,
                    dates: `${checkIn} - ${checkOut}`
                  }}
                />
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Pass Code / Pass Purchase Section - Premium Design */}
              {!validPass && (
                <Card className="p-6 md:p-8 rounded-2xl border-0 shadow-lg overflow-hidden relative">
                  {/* Decorative gradient accent */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-gold" />
                  
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center shadow-gold">
                      <Crown className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="font-semibold text-lg">{t('booking.passTitle', 'Freestays Pass - No commissions')}</h2>
                      <p className="text-sm text-muted-foreground">{t('booking.passSubtitle', 'Unlock exclusive savings')}</p>
                    </div>
                  </div>

                  {/* Existing Pass Code */}
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-[#0a2540] rounded-xl">
                    <Label className="mb-2 block text-sm font-medium">
                      <span className="hidden sm:inline">{t('booking.havePassCode', 'Have a pass code?')}</span>
                      <span className="sm:hidden flex flex-col">
                        <span>{t('booking.enterPassCode', 'Enter pass code')}</span>
                        <span className="text-xs text-muted-foreground font-normal">(e.g., GOLD-XXXXXXXX)</span>
                      </span>
                    </Label>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="GOLD-XXXXXXXX"
                        value={existingPassCode}
                        onChange={(e) => {
                          setExistingPassCode(e.target.value.toUpperCase());
                          setPassCodeValid(false);
                        }}
                        disabled={passPurchaseType !== null}
                        className="h-12 rounded-xl font-mono"
                        data-testid="input-passcode"
                      />
                      <Button 
                        type="button" 
                        variant="outline"
                        onClick={validatePassCode}
                        disabled={passCodeChecking || existingPassCode.length < 4 || passPurchaseType !== null}
                        className="h-12 rounded-xl px-6"
                      >
                        {passCodeChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : t('common.apply', 'Apply')}
                      </Button>
                    </div>
                    {passCodeValid && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-success font-medium bg-success/10 p-3 rounded-lg">
                        <div className="w-5 h-5 rounded-full bg-success flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        {t('booking.passApplied', 'Pass code applied! Your Freestays advantages are active.')}
                      </div>
                    )}
                  </div>

                  <div className="relative flex items-center gap-4 my-6">
                    <Separator className="flex-1" />
                    <span className="text-sm text-muted-foreground bg-card px-3">{t('common.or', 'or')}</span>
                    <Separator className="flex-1" />
                  </div>

                  {/* Buy Pass Options */}
                  <div>
                    <Label className="mb-4 block text-sm font-medium">{t('booking.buyPass', 'Get a Freestays Pass now:')}</Label>
                    <RadioGroup 
                      value={passPurchaseType || ''} 
                      onValueChange={(val) => {
                        setPassPurchaseType(val || null);
                        if (val) {
                          setExistingPassCode('');
                          setPassCodeValid(false);
                        }
                      }}
                      disabled={passCodeValid}
                    >
                      <div className="space-y-3">
                        {/* One-Time Pass */}
                        <label className={`group flex items-start gap-4 p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${passPurchaseType === 'one_time' ? 'border-primary bg-[#00A9E0]/5 shadow-md' : 'border-border hover:border-primary/40 hover:bg-secondary/50'}`}>
                          <RadioGroupItem value="one_time" id="one_time" className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold">{t('pass.oneTime', 'One-Time Pass')}</span>
                              <span className="font-bold text-lg">€{PASS_ONE_TIME_PRICE}</span>
                            </div>
                            <ul className="text-sm text-muted-foreground space-y-1.5">
                              <li className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-success shrink-0" />
                                {t('pass.bookWorldwide', 'Book worldwide at any hotel')}
                              </li>
                              <li className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-success shrink-0" />
                                {t('pass.singleUse', 'Single use during this booking')}
                              </li>
                              <li className="flex items-center gap-2">
                                <Gift className="w-4 h-4 text-primary shrink-0" />
                                <span className="text-primary font-medium">{t('pass.noBookingFee', 'No booking fees')}</span>
                              </li>
                            </ul>
                          </div>
                        </label>

                        {/* Annual Pass - Featured */}
                        <label className={`group relative flex items-start gap-4 p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${passPurchaseType === 'annual' ? 'border-accent bg-accent/5 shadow-lg shadow-accent/20' : 'border-accent/50 hover:border-accent hover:bg-accent/5'}`}>
                          {/* Best Value Badge */}
                          <div className="absolute -top-3 right-4">
                            <Badge className="bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 shadow-success">
                              <Sparkles className="w-3 h-3 mr-1" />
                              {t('pass.bestValue', 'Best Value')}
                            </Badge>
                          </div>
                          <RadioGroupItem value="annual" id="annual" className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold">{t('pass.annual', 'Annual Pass')}</span>
                              <span className="font-bold text-lg text-accent">€{PASS_ANNUAL_PRICE}</span>
                            </div>
                            <ul className="text-sm text-muted-foreground space-y-1.5">
                              <li className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-success shrink-0" />
                                {t('pass.bookWorldwide', 'Book worldwide at any hotel')}
                              </li>
                              <li className="flex items-center gap-2">
                                <Check className="w-4 h-4 text-success shrink-0" />
                                {t('pass.unlimitedUse', 'Unlimited use for 12 months')}
                              </li>
                              <li className="flex items-center gap-2">
                                <Gift className="w-4 h-4 text-accent shrink-0" />
                                <span className="text-accent font-medium">{t('pass.noBookingFee', 'No booking fees')}</span>
                              </li>
                            </ul>
                          </div>
                        </label>

                        {/* No Pass */}
                        <label className={`group flex items-start gap-4 p-5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${passPurchaseType === null && !passCodeValid ? 'border-muted-foreground/30 bg-gray-50 dark:bg-[#0a2540]' : 'border-border hover:border-muted-foreground/30 hover:bg-gray-50 dark:bg-[#0a2540]'}`}>
                          <RadioGroupItem value="" id="none" className="mt-1" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-semibold text-muted-foreground">{t('pass.noPass', 'No Pass')}</span>
                              <span className="font-semibold text-muted-foreground">€{BOOKING_FEE} {t('common.fee', 'fee')}</span>
                            </div>
                            <p className="text-sm text-muted-foreground">{t('pass.standardBooking', 'Standard booking with no fees.')}</p>
                          </div>
                        </label>
                      </div>
                    </RadioGroup>
                  </div>
                </Card>
              )}

              {/* Guest Details Card */}
              <Card className="p-6 md:p-8 rounded-2xl border-0 shadow-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg">{t('booking.guestDetails')}</h2>
                    <p className="text-sm text-muted-foreground">{adults} {t('booking.adults', 'adults')}{children > 0 ? `, ${children} ${t('booking.children', 'children')}` : ''}</p>
                  </div>
                </div>
                
                {/* Lead Guest (Guest 1) */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="secondary" className="text-xs">{t('booking.leadGuest', 'Lead Guest')}</Badge>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName" className="text-sm font-medium">{t('booking.firstName')}</Label>
                      <Input 
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                        required
                        className="h-12 rounded-xl mt-1.5"
                        placeholder="John"
                        data-testid="input-firstname"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="text-sm font-medium">{t('booking.lastName')}</Label>
                      <Input 
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                        required
                        className="h-12 rounded-xl mt-1.5"
                        placeholder="Doe"
                        data-testid="input-lastname"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium">{t('booking.email')}</Label>
                      <Input 
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        className="h-12 rounded-xl mt-1.5"
                        placeholder="john@example.com"
                        data-testid="input-booking-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-medium">{t('booking.phone')}</Label>
                      <Input 
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        required
                        className="h-12 rounded-xl mt-1.5"
                        placeholder="+31 6 12345678"
                        data-testid="input-phone"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="country" className="text-sm font-medium">{t('booking.nationality', 'Nationality / Country')} *</Label>
                      <select
                        id="country"
                        value={formData.country}
                        onChange={(e) => setFormData({...formData, country: e.target.value})}
                        required
                        className="w-full h-12 rounded-xl mt-1.5 px-3 border border-input bg-background text-sm"
                        data-testid="select-country"
                      >
                        <option value="">-- {t('booking.selectCountry', 'Select your country')} --</option>
                        <optgroup label="Popular">
                          {[{c:'NL',n:'Netherlands'},{c:'DE',n:'Germany'},{c:'BE',n:'Belgium'},{c:'GB',n:'United Kingdom'},{c:'FR',n:'France'},{c:'TR',n:'Turkey'},{c:'ES',n:'Spain'},{c:'IT',n:'Italy'},{c:'US',n:'United States'},{c:'PL',n:'Poland'},{c:'AT',n:'Austria'},{c:'CH',n:'Switzerland'},{c:'SE',n:'Sweden'},{c:'NO',n:'Norway'},{c:'DK',n:'Denmark'}].map(({c,n}) => (
                            <option key={c} value={c}>{n} ({c})</option>
                          ))}
                        </optgroup>
                        <optgroup label="All Countries">
                          {[{c:'AF',n:'Afghanistan'},{c:'AL',n:'Albania'},{c:'DZ',n:'Algeria'},{c:'AD',n:'Andorra'},{c:'AO',n:'Angola'},{c:'AR',n:'Argentina'},{c:'AM',n:'Armenia'},{c:'AU',n:'Australia'},{c:'AT',n:'Austria'},{c:'AZ',n:'Azerbaijan'},{c:'BH',n:'Bahrain'},{c:'BD',n:'Bangladesh'},{c:'BY',n:'Belarus'},{c:'BE',n:'Belgium'},{c:'BA',n:'Bosnia'},{c:'BR',n:'Brazil'},{c:'BG',n:'Bulgaria'},{c:'KH',n:'Cambodia'},{c:'CA',n:'Canada'},{c:'CL',n:'Chile'},{c:'CN',n:'China'},{c:'CO',n:'Colombia'},{c:'HR',n:'Croatia'},{c:'CU',n:'Cuba'},{c:'CY',n:'Cyprus'},{c:'CZ',n:'Czechia'},{c:'DK',n:'Denmark'},{c:'DO',n:'Dominican Republic'},{c:'EC',n:'Ecuador'},{c:'EG',n:'Egypt'},{c:'EE',n:'Estonia'},{c:'FI',n:'Finland'},{c:'FR',n:'France'},{c:'GE',n:'Georgia'},{c:'DE',n:'Germany'},{c:'GR',n:'Greece'},{c:'HK',n:'Hong Kong'},{c:'HU',n:'Hungary'},{c:'IS',n:'Iceland'},{c:'IN',n:'India'},{c:'ID',n:'Indonesia'},{c:'IR',n:'Iran'},{c:'IQ',n:'Iraq'},{c:'IE',n:'Ireland'},{c:'IL',n:'Israel'},{c:'IT',n:'Italy'},{c:'JP',n:'Japan'},{c:'JO',n:'Jordan'},{c:'KZ',n:'Kazakhstan'},{c:'KE',n:'Kenya'},{c:'KW',n:'Kuwait'},{c:'LV',n:'Latvia'},{c:'LB',n:'Lebanon'},{c:'LT',n:'Lithuania'},{c:'LU',n:'Luxembourg'},{c:'MY',n:'Malaysia'},{c:'MV',n:'Maldives'},{c:'MT',n:'Malta'},{c:'MX',n:'Mexico'},{c:'MD',n:'Moldova'},{c:'MC',n:'Monaco'},{c:'ME',n:'Montenegro'},{c:'MA',n:'Morocco'},{c:'NL',n:'Netherlands'},{c:'NZ',n:'New Zealand'},{c:'NG',n:'Nigeria'},{c:'MK',n:'North Macedonia'},{c:'NO',n:'Norway'},{c:'OM',n:'Oman'},{c:'PK',n:'Pakistan'},{c:'PA',n:'Panama'},{c:'PE',n:'Peru'},{c:'PH',n:'Philippines'},{c:'PL',n:'Poland'},{c:'PT',n:'Portugal'},{c:'QA',n:'Qatar'},{c:'RO',n:'Romania'},{c:'RU',n:'Russia'},{c:'SA',n:'Saudi Arabia'},{c:'RS',n:'Serbia'},{c:'SG',n:'Singapore'},{c:'SK',n:'Slovakia'},{c:'SI',n:'Slovenia'},{c:'ZA',n:'South Africa'},{c:'KR',n:'South Korea'},{c:'ES',n:'Spain'},{c:'LK',n:'Sri Lanka'},{c:'SE',n:'Sweden'},{c:'CH',n:'Switzerland'},{c:'TW',n:'Taiwan'},{c:'TH',n:'Thailand'},{c:'TN',n:'Tunisia'},{c:'TR',n:'Turkey'},{c:'UA',n:'Ukraine'},{c:'AE',n:'UAE'},{c:'GB',n:'United Kingdom'},{c:'US',n:'United States'},{c:'UY',n:'Uruguay'},{c:'UZ',n:'Uzbekistan'},{c:'VN',n:'Vietnam'}].map(({c,n}) => (
                            <option key={c} value={c}>{n} ({c})</option>
                          ))}
                        </optgroup>
                      </select>
                    </div>
                  </div>

                  {/* TC Kimlik Audit (only when CMS rule enabled + matching country + TR hotel) */}
                  {formData.country === 'TR' && (() => {
                    const hotelCountry = String(hotel?.country || '').toUpperCase();
                    const isTurkeyHotel = hotelCountry === 'TURKEY' || hotelCountry === 'TURKIJE' || hotelCountry === 'TÜRKIYE' || hotelCountry === 'TR';
                    if (!isTurkeyHotel) return null;
                    // Rule off → no TC Kimlik field (single CMS kill-switch for the entire flow)
                    if (!bookingNatGate?.enabled) return null;
                    if (bookingNatGate.trigger_country !== 'TR') return null;
                    return (
                      <div className="pt-4 border-t">
                        <Label htmlFor="tc-kimlik" className="text-sm font-medium flex items-center gap-2">
                          <ShieldAlert className="w-4 h-4 text-amber-600" />
                          {t('booking.tcKimlikLabel', 'TC Kimlik Number')} *
                        </Label>
                        <Input
                          id="tc-kimlik"
                          value={formData.tcKimlikNumber}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '').slice(0, 11);
                            setFormData({ ...formData, tcKimlikNumber: digits });
                          }}
                          placeholder="11 digits"
                          inputMode="numeric"
                          maxLength={11}
                          required
                          className="h-12 rounded-xl mt-1.5"
                          data-testid="tc-kimlik-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1.5">
                          {t('booking.tcKimlikHelp', 'Required for bookings in Turkey by Turkish nationals. Used for compliance & audit only.')}
                        </p>
                        {formData.tcKimlikNumber && formData.tcKimlikNumber.length !== 11 && (
                          <p className="text-xs text-red-600 mt-1">
                            {t('booking.tcKimlikInvalid', 'TC Kimlik number must be exactly 11 digits.')}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
                
                {/* Additional Guests */}
                {formData.additionalGuests && formData.additionalGuests.length > 0 && (
                  <div className="space-y-4 pt-4 border-t" data-testid="additional-guests-section">
                    <p className="text-sm font-medium text-muted-foreground">
                      {t('booking.additionalGuests', 'Additional Guests')}
                      <span className="text-xs text-amber-600 dark:text-amber-400 ml-2 font-normal">
                        {t('booking.adultNamesRequired', '(full name required for each adult)')}
                      </span>
                    </p>
                    {formData.additionalGuests.map((guest, index) => {
                      const guestNumber = index + 2;
                      const isChild = index >= (adults - 1);
                      return (
                        <div key={index} className="p-4 bg-gray-50 dark:bg-[#0a2540] rounded-xl" data-testid={`additional-guest-${index}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <Badge variant={isChild ? "secondary" : "outline"} className="text-xs">
                              {isChild ? <Baby className="w-3 h-3 mr-1" /> : <User className="w-3 h-3 mr-1" />}
                              {isChild 
                                ? t('booking.childGuest', 'Child {{num}}', {num: index - (adults - 2)})
                                : t('booking.guestNum', 'Guest {{num}}', {num: guestNumber})
                              }
                            </Badge>
                          </div>
                          <div className="grid md:grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs">
                                {t('booking.firstName')}
                                {!isChild && <span className="text-red-500 ml-0.5">*</span>}
                              </Label>
                              <Input 
                                value={guest.firstName}
                                onChange={(e) => updateAdditionalGuest(index, 'firstName', e.target.value)}
                                required
                                className="h-10 rounded-xl mt-1"
                                placeholder={t('booking.firstNamePlaceholder', 'First name')}
                                data-testid={`additional-guest-${index}-first-name`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">
                                {t('booking.lastName')}
                                {!isChild && <span className="text-red-500 ml-0.5">*</span>}
                              </Label>
                              <Input 
                                value={guest.lastName}
                                onChange={(e) => updateAdditionalGuest(index, 'lastName', e.target.value)}
                                required
                                className="h-10 rounded-lg mt-1"
                                placeholder={t('booking.lastNamePlaceholder', 'Last name')}
                                data-testid={`additional-guest-${index}-last-name`}
                              />
                            </div>
                            {isChild && (
                              <div>
                                <Label>{t('booking.childAge', 'Age')}</Label>
                                <div className="h-10 rounded-lg mt-1 px-3 flex items-center justify-between bg-secondary/30 border border-input">
                                  <span className="text-sm font-medium" data-testid={`child-age-readonly-${index}`}>
                                    {guest.age !== '' && guest.age != null
                                      ? (parseInt(guest.age) === 0
                                          ? t('search.underOneYear', 'Under 1 year')
                                          : parseInt(guest.age) === 1
                                            ? t('search.oneYear', '1 year')
                                            : t('search.years', '{{age}} years').replace('{{age}}', guest.age))
                                      : '—'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      // Go back to hotel detail so user re-picks ages and gets fresh price.
                                      // Hotel detail URL contains all the search params (incl. childrenAges).
                                      navigate(-1);
                                    }}
                                    className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
                                    data-testid={`child-age-edit-${index}`}
                                    title={t('booking.editAgeHint', 'Change age — price will be recalculated')}
                                  >
                                    <Edit className="w-3 h-3" />
                                    {t('booking.editAge', 'Edit')}
                                  </button>
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-1">
                                  {t('booking.ageLockedHint', 'Age set during search. Editing requires a new price quote.')}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="mt-4">
                  <Label htmlFor="specialRequests">{t('booking.specialRequests')} ({t('booking.optional')})</Label>
                  <Textarea 
                    id="specialRequests"
                    value={formData.specialRequests}
                    onChange={(e) => setFormData({...formData, specialRequests: e.target.value})}
                    placeholder={t('booking.specialRequests', 'Any special requests for the hotel...')}
                  />
                </div>
              </Card>

              {/* Secure Payment Card - Premium Design */}
              <Card className="p-6 md:p-8 rounded-2xl border-0 shadow-lg bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-slate-800 dark:to-slate-900">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-lg">{t('booking.securePayment', 'Secure Payment')}</h2>
                    <p className="text-sm text-muted-foreground">{t('booking.poweredByStripe', 'Powered by Stripe')}</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('booking.paymentNote', "You'll be redirected to Stripe for secure payment.")} <strong>{t('booking.noBookingUntilConfirmed', 'No booking is made with the hotel until payment is confirmed.')}</strong>
                </p>
                {/* Trust Badges — Row 1: Security */}
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="trust-ssl">
                    <Lock className="w-3.5 h-3.5 text-emerald-500" />
                    <span>{t('booking.encrypted', '256-bit SSL')}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="trust-pci">
                    <Shield className="w-3.5 h-3.5 text-emerald-500" />
                    <span>{t('booking.pciCompliant', 'PCI Compliant')}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="trust-checkout">
                    <CreditCard className="w-3.5 h-3.5 text-emerald-500" />
                    <span>{t('booking.secureCheckout', 'Secure Checkout')}</span>
                  </div>
                </div>
                {/* Trust Badges — Row 2: Booking guarantees */}
                <div className="flex items-center gap-4 flex-wrap mt-2 pt-2 border-t border-border/50">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="trust-no-fees">
                    <CheckCircle className="w-3.5 h-3.5 text-primary" />
                    <span>{t('booking.noHiddenFees', 'No hidden fees')}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="trust-instant">
                    <Zap className="w-3.5 h-3.5 text-primary" />
                    <span>{t('booking.instantConfirmation', 'Instant confirmation')}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="trust-cancel">
                    <RefreshCw className="w-3.5 h-3.5 text-primary" />
                    <span>{t('booking.freeCancellation', 'Free cancellation (where applicable)')}</span>
                  </div>
                </div>
              </Card>

              {/* Submit Button - Premium Design */}
              <Button 
                type="submit" 
                size="lg" 
                className="w-full h-14 rounded-2xl text-base font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all duration-300 hover:scale-[1.02]"
                disabled={loading}
                data-testid="submit-booking"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Lock className="w-5 h-5 mr-2" />
                )}
                {t('booking.payNow', 'Pay Now')} — €{pricing.finalTotal.toFixed(2)}
              </Button>
            </form>
            )}
          </div>

          {/* Summary Sidebar - Premium Design (Desktop Only) */}
          <div className="hidden lg:block">
            <Card className="p-6 md:p-7 sticky top-24 rounded-2xl border-0 shadow-xl bg-white dark:bg-slate-900">
              {/* Hotel Preview */}
              <div className="flex gap-4 mb-5">
                <div className="relative">
                  <div className="w-24 h-24 rounded-xl overflow-hidden shadow-md bg-secondary">
                    {(hotel.image_url || hotel.images?.[0]) ? (
                      <img 
                        src={hotel.image_url || hotel.images?.[0]} 
                        alt={hotel.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Hotel className="w-8 h-8 text-muted-foreground/30" />
                      </div>
                    )}
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-white dark:bg-slate-800 rounded-lg px-1.5 py-0.5 shadow-sm flex items-center gap-0.5">
                    {Array.from({ length: Math.floor(hotel.star_rating) }).map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-gold text-gold" />
                    ))}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-sans font-bold font-semibold text-lg leading-tight line-clamp-2">{hotel.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" />
                    {hotel.city}, {hotel.country}
                  </p>
                </div>
              </div>

              {/* Social Proof Counters */}
              {(socialProof?.viewers_24h || socialProof?.bookings_24h) && (
                <div className="space-y-1.5 mb-5" data-testid="social-proof">
                  {socialProof?.viewers_24h && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900/40">
                      <Flame className="w-4 h-4 text-orange-500 shrink-0" />
                      <span className="text-xs font-medium text-orange-900 dark:text-orange-200">
                        {t('booking.viewersNow', '{{n}} people viewed this hotel today').replace('{{n}}', socialProof.viewers_24h)}
                      </span>
                    </div>
                  )}
                  {socialProof?.bookings_24h && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40">
                      <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span className="text-xs font-medium text-emerald-900 dark:text-emerald-200">
                        {t('booking.bookedToday', 'Booked {{n}} times in the last 24h').replace('{{n}}', socialProof.bookings_24h)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Booking Details */}
              <div className="bg-secondary/40 rounded-xl p-4 mb-5">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('search.checkIn')}</p>
                    <p className="font-medium">{format(new Date(checkIn), 'EEE, MMM d')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('search.checkOut')}</p>
                    <p className="font-medium">{format(new Date(checkOut), 'EEE, MMM d')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('booking.room', 'Room')}</p>
                    <p className="font-medium truncate">{roomType || room?.room_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('search.guests')}</p>
                    <p className="font-medium">{adults + children} {t('common.guest', 'guest')}{adults + children > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <Separator className="my-3" />
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('booking.mealplan', 'Mealplan')}</span>
                  <span className="font-medium">{currentBoardType}</span>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm">{t('booking.priceDetails', 'Price Details')}</h4>
                {/* Selected Pass info */}
                {(passPurchaseType || (user?.pass_type && user.pass_type !== 'free')) && (
                  <div className="flex items-center justify-between text-sm bg-primary/5 -mx-2 px-3 py-2 rounded-lg border border-primary/15">
                    <span className="text-muted-foreground flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-primary" />
                      {t('booking.selectedPass', 'Selected Pass')}
                    </span>
                    <span className="font-semibold text-primary">
                      {(passPurchaseType === 'annual' || user?.pass_type === 'annual')
                        ? t('pass.annualPass', 'Annual Pass')
                        : (passPurchaseType === 'one_time' || user?.pass_type === 'one_time')
                          ? t('pass.oneTimePass', 'One-Time Pass')
                          : user?.pass_type === 'b2b'
                            ? 'B2B Pass'
                            : ''}
                    </span>
                  </div>
                )}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t('booking.roomPrice')} × {nights} {t('search.nights')}</span>
                    <span>€{pricing.priceBeforeDiscount.toFixed(2)}</span>
                  </div>
                  {hasDiscount && (
                    <div className="flex justify-between text-sm text-success">
                      <span className="flex items-center gap-1.5">
                        <Tag className="w-3.5 h-3.5" />
                        {isPackage ? t('booking.roomIsFree', 'Room is Free') : t('booking.passDiscount', 'Pass Discount')}
                      </span>
                      <span className="font-medium">-€{pricing.discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {pricing.bookingFee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('booking.bookingFee')}</span>
                      <span>€{pricing.bookingFee.toFixed(2)}</span>
                    </div>
                  )}
                  {pricing.referralDiscountApplied && (
                    <div className="flex justify-between text-sm text-success">
                      <span className="flex items-center gap-1.5">
                        <Gift className="w-3.5 h-3.5" />
                        {t('booking.referralDiscount', 'Referral Discount')}
                      </span>
                      <span className="font-medium">-€{BOOKING_FEE.toFixed(2)}</span>
                    </div>
                  )}
                  {pricing.passPrice > 0 && (
                    <div className="flex justify-between text-sm text-primary">
                      <span>{passPurchaseType === 'annual' ? t('pass.annualPass', 'Annual Pass') : t('pass.oneTimePass', 'One-Time Pass')}</span>
                      <span>€{pricing.passPrice.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                
                {/* Transfer Section */}
                {(transferId || returnTransferId) && (
                  <>
                    <Separator className="my-3" />
                    <div className="space-y-3">
                      <p className="text-sm font-medium flex items-center gap-2">
                        <Car className="w-4 h-4 text-primary" />
                        {t('transfer.airportTransfers', 'Airport Transfers')}
                      </p>
                      
                      {/* Arrival Transfer */}
                      {transferId && (
                        <div className="space-y-2 pl-3 border-l-2 border-primary/30 rounded-r-lg bg-[#00A9E0]/5 py-2 pr-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Checkbox 
                                id="include-transfer"
                                checked={includeTransfer}
                                onCheckedChange={setIncludeTransfer}
                              />
                              <Label htmlFor="include-transfer" className="text-sm cursor-pointer flex items-center gap-2">
                                <PlaneTakeoff className="w-3.5 h-3.5 text-primary" />
                                {t('transfer.arrival', 'Arrival')} ({checkIn})
                              </Label>
                            </div>
                            <span className="text-sm font-semibold">€{transferPrice.toFixed(2)}</span>
                          </div>
                          {includeTransfer && (
                            <div className="ml-6 text-xs text-muted-foreground space-y-1">
                              <p>{transferVehicle}</p>
                              {transferTime && <p>{t('transfer.pickupTime', 'Pickup')}: {transferTime}</p>}
                              {transferFlight && <p>{t('transfer.flight', 'Flight')}: {transferFlight}</p>}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Return Transfer */}
                      {returnTransferId && (
                        <div className="space-y-2 pl-3 border-l-2 border-primary/30 rounded-r-lg bg-[#00A9E0]/5 py-2 pr-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Checkbox 
                                id="include-return-transfer"
                                checked={includeReturnTransfer}
                                onCheckedChange={setIncludeReturnTransfer}
                              />
                              <Label htmlFor="include-return-transfer" className="text-sm cursor-pointer flex items-center gap-2">
                                <Plane className="w-3.5 h-3.5 text-primary" />
                                {t('transfer.return', 'Return')} ({checkOut})
                              </Label>
                            </div>
                            <span className="text-sm font-semibold">€{returnTransferPrice.toFixed(2)}</span>
                          </div>
                          {includeReturnTransfer && (
                            <div className="ml-6 text-xs text-muted-foreground space-y-1">
                              <p>{returnTransferVehicle}</p>
                              {returnTransferTime && <p>{t('transfer.departureTime', 'Departure')}: {returnTransferTime}</p>}
                              {returnTransferFlight && <p>{t('transfer.flight', 'Flight')}: {returnTransferFlight}</p>}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Transfer subtotal */}
                      {(includeTransfer || includeReturnTransfer) && (
                        <div className="flex justify-between text-sm pt-2 border-t border-dashed">
                          <span className="text-muted-foreground">{t('transfer.transferSubtotal', 'Transfer subtotal')}</span>
                          <span className="font-semibold">€{totalTransferAmount.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {/* Add Transfer Section - shows when transfers available but not yet selected from hotel page */}
                {!transferId && !returnTransferId && (availableTransfers.length > 0 || availableReturnTransfers.length > 0) && (
                  <div className="mt-4 pt-4 border-t border-dashed">
                    <p className="text-xs font-semibold text-primary mb-3 flex items-center gap-2">
                      <Car className="w-4 h-4" />
                      {t('transfer.addAirportTransfer', 'Add Airport Transfer')}
                    </p>
                    
                    {/* Arrival Transfer Selection */}
                    {availableTransfers.length > 0 && (
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center justify-between p-2 bg-[#00A9E0]/5 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              id="add-arrival-transfer"
                              checked={!!selectedTransfer}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedTransfer(availableTransfers[0]);
                                  setIncludeTransfer(true);
                                } else {
                                  setSelectedTransfer(null);
                                  setIncludeTransfer(false);
                                }
                              }}
                            />
                            <Label htmlFor="add-arrival-transfer" className="text-sm cursor-pointer flex items-center gap-2">
                              <PlaneTakeoff className="w-3.5 h-3.5 text-primary" />
                              {t('transfer.arrival', 'Arrival Transfer')} ({checkIn})
                            </Label>
                          </div>
                          {selectedTransfer ? (
                            <span className="text-sm font-semibold text-primary">€{selectedTransfer.price.toFixed(2)}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">{t('transfer.fromPrice', 'from')} €{Math.min(...availableTransfers.map(t => t.price)).toFixed(2)}</span>
                          )}
                        </div>
                        {selectedTransfer && (
                          <div className="ml-6 text-xs text-muted-foreground flex items-center justify-between">
                            <span>{selectedTransfer.airport_name} - {t(`transferVehicle.${selectedTransfer.vehicle_type}`, selectedTransfer.vehicle_type)}</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-xs h-6 px-2"
                              onClick={() => {
                                setTransferSelectorType('arrival');
                                setShowTransferSelector(true);
                              }}
                            >
                              {t('transfer.change', 'Change')}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Return Transfer Selection */}
                    {availableReturnTransfers.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-2 bg-[#00A9E0]/5 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Checkbox 
                              id="add-return-transfer"
                              checked={!!selectedReturnTransfer}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedReturnTransfer(availableReturnTransfers[0]);
                                  setIncludeReturnTransfer(true);
                                } else {
                                  setSelectedReturnTransfer(null);
                                  setIncludeReturnTransfer(false);
                                }
                              }}
                            />
                            <Label htmlFor="add-return-transfer" className="text-sm cursor-pointer flex items-center gap-2">
                              <Plane className="w-3.5 h-3.5 text-primary" />
                              {t('transfer.return', 'Return Transfer')} ({checkOut})
                            </Label>
                          </div>
                          {selectedReturnTransfer ? (
                            <span className="text-sm font-semibold text-primary">€{selectedReturnTransfer.price.toFixed(2)}</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">{t('transfer.fromPrice', 'from')} €{Math.min(...availableReturnTransfers.map(t => t.price)).toFixed(2)}</span>
                          )}
                        </div>
                        {selectedReturnTransfer && (
                          <div className="ml-6 text-xs text-muted-foreground flex items-center justify-between">
                            <span>{selectedReturnTransfer.airport_name} - {t(`transferVehicle.${selectedReturnTransfer.vehicle_type}`, selectedReturnTransfer.vehicle_type)}</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-xs h-6 px-2"
                              onClick={() => {
                                setTransferSelectorType('return');
                                setShowTransferSelector(true);
                              }}
                            >
                              {t('transfer.change', 'Change')}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Transfer Subtotal */}
                    {(selectedTransfer || selectedReturnTransfer) && (
                      <div className="flex justify-between text-sm pt-2 mt-2 border-t border-dashed">
                        <span className="text-muted-foreground">{t('transfer.transferSubtotal', 'Transfer subtotal')}</span>
                        <span className="font-semibold">€{((selectedTransfer?.price || 0) + (selectedReturnTransfer?.price || 0)).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Loading transfers indicator */}
                {loadingTransfers && (
                  <div className="mt-4 pt-4 border-t border-dashed">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('transfer.loadingTransfers', 'Loading transfer options...')}
                    </div>
                  </div>
                )}
                
                {/* Travel Credits Toggle */}
                {user && availableCredits > 0 && (
                  <div className="mt-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          id="use-credits"
                          checked={useCredits}
                          onCheckedChange={setUseCredits}
                          data-testid="use-credits-checkbox"
                        />
                        <Label htmlFor="use-credits" className="text-sm cursor-pointer flex items-center gap-2">
                          <Wallet className="w-3.5 h-3.5 text-emerald-600" />
                          {t('booking.useCredits', 'Use Travel Credits')}
                        </Label>
                      </div>
                      <span className="text-sm font-semibold text-emerald-600">
                        €{availableCredits.toFixed(2)} {t('booking.available', 'available')}
                      </span>
                    </div>
                    {useCredits && creditDiscount > 0 && (
                      <div className="mt-2 flex justify-between text-sm text-emerald-600 font-medium">
                        <span>{t('booking.creditsApplied', 'Credits Applied')}</span>
                        <span>-€{creditDiscount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Total */}
                <div className="mt-4 pt-4 border-t-2 border-[#00A9E0]/20">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-lg">{t('booking.total')}</span>
                    <span className="font-sans font-bold text-2xl text-primary">€{finalTotalAfterCredits.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Savings & Referral Banners */}
              {pricing.referralDiscountApplied && (
                <div className="mt-5 bg-gradient-to-r from-success/10 to-success/5 border border-success/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-success" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-success-dark dark:text-success">
                        {t('booking.referralApplied', 'Referral discount applied!')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('booking.referralSaved', 'Booking fee waived')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {hasDiscount && (
                <div className="mt-5 bg-gradient-to-r from-primary/10 to-primary/5 border border-[#00A9E0]/20 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-primary">
                        {isPackage ? t('booking.roomIsFree', 'Room is Free') : t('booking.passDiscount', 'Pass Discount')}: €{pricing.discountAmount.toFixed(2)}
                      </p>
                      {(passPurchaseType === 'one_time' || passPurchaseType === 'annual') && (
                        <p className="text-xs text-muted-foreground">
                          {t('booking.noBookingFee', 'No booking fees')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!hasDiscount && !pricing.referralDiscountApplied && (
                <div className="mt-5 bg-gradient-to-r from-gold/10 to-gold-light/10 border border-gold/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-gold-dark" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gold-dark">
                        {t('booking.getPass', 'Get a Freestays Pass')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('booking.saveUpTo', 'Save up to')} €{pricing.potentialSavings.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Dates — opens new DateRange picker; on selection navigates back to hotel page with new range */}
              <DateRangeSheet
                checkIn={checkIn ? new Date(checkIn) : null}
                checkOut={checkOut ? new Date(checkOut) : null}
                onChange={({ from, to }) => {
                  const newIn = format(from, 'yyyy-MM-dd');
                  const newOut = format(to, 'yyyy-MM-dd');
                  let url = `/hotel/${hotelId}?checkIn=${newIn}&checkOut=${newOut}&adults=${adults}&children=${children}`;
                  if (destinationId) url += `&destinationId=${destinationId}`;
                  if (destination) url += `&destination=${encodeURIComponent(destination)}`;
                  if (b2c === 1) url += `&b2c=1`;
                  navigate(url);
                }}
                trigger={
                  <Button
                    variant="ghost"
                    className="w-full mt-3 rounded-xl text-muted-foreground hover:text-foreground"
                    data-testid="edit-dates-btn"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {t('booking.editDates', 'Edit Dates')}
                  </Button>
                }
              />

              {/* Change Booking Button */}
              <Button 
                variant="ghost" 
                className="w-full mt-2 rounded-xl text-muted-foreground hover:text-foreground"
                onClick={() => {
                  let url = `/hotel/${hotelId}?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}`;
                  if (destinationId) url += `&destinationId=${destinationId}`;
                  if (destination) url += `&destination=${encodeURIComponent(destination)}`;
                  if (b2c === 1) url += `&b2c=1`;
                  navigate(url);
                }}
                data-testid="change-booking-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                {t('booking.changeBooking', 'Change Booking')}
              </Button>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Transfer Selector Dialog - For changing selected transfer */}
      <Dialog open={showTransferSelector} onOpenChange={setShowTransferSelector}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="w-5 h-5 text-primary" />
              {transferSelectorType === 'arrival' 
                ? t('transfer.selectArrivalTransfer', 'Select Arrival Transfer')
                : t('transfer.selectReturnTransfer', 'Select Return Transfer')}
            </DialogTitle>
            <DialogDescription>
              {transferSelectorType === 'arrival'
                ? `${t('transfer.arrivalTransferDesc', 'Airport → Hotel on')} ${checkIn}`
                : `${t('transfer.returnTransferDesc', 'Hotel → Airport on')} ${checkOut}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Transfer Options - grouped by airport if multiple */}
            {(() => {
              const transferList = transferSelectorType === 'arrival' ? availableTransfers : availableReturnTransfers;
              const currentSelection = transferSelectorType === 'arrival' ? selectedTransfer : selectedReturnTransfer;
              
              // Group transfers by airport
              const groupedByAirport = transferList.reduce((acc, transfer) => {
                const airportKey = transfer.airport_name || 'Unknown Airport';
                if (!acc[airportKey]) acc[airportKey] = [];
                acc[airportKey].push(transfer);
                return acc;
              }, {});
              
              const airportNames = Object.keys(groupedByAirport);
              
              return (
                <div className="space-y-4">
                  {airportNames.map((airportName) => (
                    <div key={airportName}>
                      {airportNames.length > 1 && (
                        <h4 className="font-semibold text-sm text-primary mb-2 flex items-center gap-2">
                          <PlaneTakeoff className="w-4 h-4" />
                          {airportName}
                        </h4>
                      )}
                      <div className="space-y-2">
                        {groupedByAirport[airportName].map((transfer) => (
                          <div 
                            key={transfer.transfer_id}
                            className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              currentSelection?.transfer_id === transfer.transfer_id 
                                ? 'border-primary bg-[#00A9E0]/5' 
                                : 'border-border hover:border-primary/50'
                            }`}
                            onClick={() => {
                              if (transferSelectorType === 'arrival') {
                                setSelectedTransfer(transfer);
                                setIncludeTransfer(true);
                              } else {
                                setSelectedReturnTransfer(transfer);
                                setIncludeReturnTransfer(true);
                              }
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {currentSelection?.transfer_id === transfer.transfer_id ? (
                                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                    <Check className="w-3 h-3 text-white" />
                                  </div>
                                ) : (
                                  <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 flex-shrink-0" />
                                )}
                                <div>
                                  <p className="font-medium text-sm">{t(`transferVehicle.${transfer.vehicle_name || transfer.vehicle_type}`, transfer.vehicle_name || transfer.vehicle_type)}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {transfer.min_passengers}-{transfer.max_passengers} {t('transfer.passengers', 'passengers')}
                                    {transfer.estimated_duration && ` • ${Math.floor(transfer.estimated_duration / 60)}h ${transfer.estimated_duration % 60}m`}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="text-lg font-semibold text-primary">€{transfer.price.toFixed(2)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  
                  {transferList.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Car className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>{t('transfer.noOptions', 'No transfer options available')}</p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowTransferSelector(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={() => setShowTransferSelector(false)}
              disabled={transferSelectorType === 'arrival' ? !selectedTransfer : !selectedReturnTransfer}
            >
              {t('transfer.confirm', 'Confirm Selection')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== BOOKING SUCCESS ====================
// Extracted to /app/frontend/src/pages/BookingSuccessPage.jsx (Fase 1)
export { BookingSuccessPage } from './pages/BookingSuccessPage';
import { BookingSuccessPage } from './pages/BookingSuccessPage';

// ==================== PARTNER OFFER PAGE ====================
// Extracted to /app/frontend/src/pages/PartnerOfferPage.jsx (Fase 1)
// Re-exported for backwards-compatible imports
export { PartnerOfferPage } from './pages/PartnerOfferPage';
import { PartnerOfferPage } from './pages/PartnerOfferPage';

// ==================== PASS PURCHASE SUCCESS ====================
// Extracted to /app/frontend/src/pages/PassSuccessPage.jsx (Fase 1)
export { PassSuccessPage } from './pages/PassSuccessPage';
import { PassSuccessPage } from './pages/PassSuccessPage';

// ==================== CHECKOUT PAGE REMOVED ====================
// Dead code removed - application uses Stripe Hosted Checkout instead of Embedded Checkout
// Original code: ~157 lines (CheckoutPage component)
// Removal date: December 2025

// ==================== PARTNER PAGES ====================

// Homepage Partners Banner Section
const PartnersBannerSection = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: settings } = useQuery({
    queryKey: ['partnerSettings'],
    queryFn: async () => {
      const res = await axios.get(`${API}/partners/settings`);
      return res.data;
    }
  });

  if (!settings?.section_enabled) return null;

  const partners = [
    {
      id: 'tours',
      icon: '🎭',
      title: t('partners.tours.title', 'Tours & Activities'),
      description: t('partners.tours.description', 'Discover local experiences, guided tours, and unforgettable adventures'),
      cta: t('partners.tours.cta', 'Explore Activities'),
      path: '/tours',
      gradient: 'from-purple-500 to-pink-500',
      enabled: settings?.viator?.enabled
    },
    {
      id: 'cars',
      icon: '🚗',
      title: t('partners.cars.title', 'Rent a Car'),
      description: t('partners.cars.description', 'Freedom to explore with convenient car rentals at the best prices'),
      cta: t('partners.cars.cta', 'Find Your Car'),
      path: '/car-rental',
      gradient: 'from-green-500 to-emerald-500',
      enabled: settings?.discovercars?.enabled
    },
    {
      id: 'flights',
      icon: '✈️',
      title: t('partners.flights.title', 'Book a Flight'),
      description: t('partners.flights.description', 'Find amazing flight deals to your dream destination'),
      cta: t('partners.flights.cta', 'Search Flights'),
      path: '/flights',
      gradient: 'from-blue-500 to-cyan-500',
      enabled: settings?.kiwi?.enabled
    }
  ].filter(p => p.enabled);

  if (partners.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-gradient-to-b from-background to-secondary/30">
      <div className="max-w-[1760px] mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary">
            <Sparkles className="w-3 h-3 mr-1" />
            {t('partners.badge', 'Complete Your Trip')}
          </Badge>
          <h2 className="font-bold text-3xl md:text-4xl mb-4">
            {settings?.section_title || t('partners.sectionTitle', 'Explore More Travel Experiences')}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {settings?.section_subtitle || t('partners.sectionSubtitle', 'Book your hotel first to unlock exclusive partner deals and complete your perfect vacation')}
          </p>
        </div>

        <div className={`grid gap-6 ${partners.length === 3 ? 'md:grid-cols-3' : partners.length === 2 ? 'md:grid-cols-2' : 'max-w-md mx-auto'}`}>
          {partners.map((partner) => (
            <Card 
              key={partner.id}
              className="group relative overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
              onClick={() => navigate(partner.path)}
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${partner.gradient} opacity-90`} />
              
              {/* Content */}
              <div className="relative p-8 text-white">
                <div className="text-5xl mb-4 transform group-hover:scale-110 transition-transform">
                  {partner.icon}
                </div>
                <h3 className="text-2xl font-bold mb-2">{partner.title}</h3>
                <p className="text-white/90 mb-6 min-h-[48px]">{partner.description}</p>
                <Button 
                  variant="secondary" 
                  className="w-full bg-white/20 hover:bg-white/30 text-white border-white/30 backdrop-blur-sm"
                >
                  {partner.cta}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
            </Card>
          ))}
        </div>

        {/* Hotel booking encouragement */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 bg-[#00A9E0]/5 border border-[#00A9E0]/20 rounded-full px-6 py-3">
            <Gift className="w-5 h-5 text-primary" />
            <span className="text-sm">
              {t('partners.hotelFirst', 'Book your hotel first to get the best partner deals!')}
            </span>
            <Button size="sm" variant="default" className="rounded-full" onClick={() => navigate('/')}>
              {t('partners.searchHotels', 'Search Hotels')}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== TC KIMLIK USER CARD (Dashboard → Personal Data) ====================
// Shown only when: nationality gate is enabled AND (user is Turkish OR already has a status).
// Users can flip their status here without seeing the popup again.
// Extracted to /app/frontend/src/pages/dashboard/TCKimlikUserCard.jsx (Fase 2)
import { TCKimlikUserCard } from './pages/dashboard/TCKimlikUserCard';




// ==================== B2B CREATE ORDER TAB (For Managers) ====================
// Extracted to /app/frontend/src/pages/dashboard/B2BCreateOrderTab.jsx (Fase 2)
import { B2BCreateOrderTab } from './pages/dashboard/B2BCreateOrderTab';

// ==================== B2B ORDERS TAB (For Managers) ====================
// Extracted to /app/frontend/src/pages/dashboard/B2BOrdersTab.jsx (Fase 2)
import { B2BOrdersTab } from './pages/dashboard/B2BOrdersTab';

// ==================== BOOKING DETAILS DIALOG ====================
// Extracted to /app/frontend/src/pages/dashboard/BookingDetailsDialog.jsx (Fase 2)
import { BookingDetailsDialog } from './pages/dashboard/BookingDetailsDialog';

// ==================== GROUP-ASSIGNED PASS CODES TAB ====================
// Extracted to /app/frontend/src/pages/dashboard/GroupPassCodesTab.jsx (Fase 2)
import { GroupPassCodesTab } from './pages/dashboard/GroupPassCodesTab';


// ==================== DASHBOARD ====================
// Extracted to /app/frontend/src/pages/Dashboard.jsx (Fase 3c)
// Uses circular import for useAuth (named, hook-only — safe pattern from Fase 3b).
import { DashboardPage } from './pages/Dashboard';

// ==================== ABOUT PAGE REMOVED ====================
// Dead code removed - using imported AboutPage from @/pages/AboutPage instead
// Original code: ~147 lines (local AboutPage component)
// Removal date: January 2026

// ==================== REFER A FRIEND PAGE REMOVED ====================
// Dead code removed - using imported ReferFriendPage from @/pages/ReferFriendPage instead
// Original code: ~218 lines (local ReferFriendPage component)
// Removal date: January 2026

// ==================== CONTACT PAGE REMOVED ====================
// Dead code removed - using imported ContactPage from @/pages/ContactPage instead
// Original code: ~207 lines (local ContactPage component)
// Removal date: December 2025

// ==================== WHO WE ARE PAGE REMOVED ====================
// Dead code removed - using imported WhoWeArePage from @/pages/WhoWeArePage instead
// Original code: ~208 lines (local WhoWeArePage component)
// Removal date: December 2025

// ==================== ADMIN PAGES ====================

// Admin Login Page
const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      
      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
        localStorage.setItem('admin_email', response.data.email);
        toast.success('Admin login successful!');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      toast.error('Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#00A9E0]/5 to-secondary/5 px-4" data-testid="admin-login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 p-2 shadow-sm border">
            <img 
              src="/assets/logo.png" 
              alt="Freestays" 
              className="h-full w-auto object-contain"
            />
          </div>
          <CardTitle className="font-sans font-bold text-2xl">Admin Login</CardTitle>
          <CardDescription>Enter your admin credentials to access the dashboard</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                placeholder="admin@freestays.eu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="admin-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="admin-login-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
              {loading ? 'Logging in...' : 'Login to Admin Panel'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== APP ====================

// Email Verification Reminder Banner
const EmailVerificationReminder = () => {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  
  // Don't show if no user, already verified, or dismissed this session
  if (!user || user.email_verified || dismissed) return null;
  
  const handleResendVerification = async () => {
    setResending(true);
    try {
      const token = localStorage.getItem('freestays_token');
      await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error('Failed to send verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };
  
  return (
    <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800" data-testid="email-verification-banner">
      <div className="max-w-[1760px] mx-auto px-4 py-2">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 text-sm">
          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
            <Mail className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs sm:text-sm">
              Please verify your email address to unlock all features.
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-800 text-xs px-3 h-7"
              onClick={handleResendVerification}
              disabled={resending}
            >
              {resending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
              Resend Email
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="w-6 h-6 text-amber-600 dark:text-[#FFAF00] hover:bg-amber-100 dark:hover:bg-amber-800"
              onClick={() => setDismissed(true)}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper for ReferFriendPage to pass user from App.js's internal useAuth
const ReferFriendPageWrapper = () => {
  const { user } = useAuth();
  return <ReferFriendPage user={user} />;
};

// CMS Protected Route - redirects to login if not authenticated
const CMSProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useCMS();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/cms/login');
    }
  }, [isAuthenticated, loading, navigate]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : null;
};

// CMS App Wrapper with its own routes
const CMSApp = () => {
  return (
    <CMSProvider>
      <Routes>
        <Route path="login" element={<CMSLogin />} />
        <Route path="/*" element={
          <CMSProtectedRoute>
            <CMSLayout />
          </CMSProtectedRoute>
        }>
          <Route path="dashboard" element={<CMSDashboard />} />
          <Route path="users" element={<CMSUsers />} />
          <Route path="users/:userId" element={<CMSUserDetail />} />
          <Route path="passes" element={<CMSPasses />} />
          <Route path="payments" element={<CMSPayments />} />
          <Route path="b2b" element={<CMSB2B />} />
          <Route path="commissions" element={<CMSCommissions />} />
          <Route path="reports" element={<CMSReports />} />
          <Route path="audit-logs" element={<CMSAuditLogs />} />
          <Route path="admins" element={<CMSAdmins />} />
          <Route path="popup" element={<CMSPopupSettings apiUrl={BACKEND_URL} />} />
          {/* Phase 2 Routes */}
          <Route path="fraud-rules" element={<CMSFraudRules />} />
          <Route path="tc-kimlik-audit" element={<CMSTCKimlikAudit />} />
          <Route path="system-health" element={<CMSSystemHealth />} />
          <Route path="alerts" element={<CMSAlerts />} />
          <Route path="analytics" element={<CMSAnalytics />} />
          <Route path="security" element={<CMSSecurity />} />
          {/* Marketing Route */}
          <Route path="marketing" element={<MarketingDashboard />} />
          <Route path="" element={<CMSDashboard />} />
        </Route>
      </Routes>
    </CMSProvider>
  );
};

function AppRouter() {
  const location = useLocation();
  
  // Google Analytics: track page views on route change
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location.pathname, location.search]);
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname, location.search]);
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/search/map" element={<SearchMapPage />} />
      <Route path="/last-minute" element={<LastMinutePage />} />
      <Route path="/tours" element={<ToursPage />} />
      <Route path="/car-rental" element={<CarRentalPage />} />
      <Route path="/flights" element={<FlightsPage />} />
      <Route path="/flights/book" element={<FlightBookingPage />} />
      <Route path="/hotel/:hotelId" element={<HotelDetailPage />} />
      <Route path="/booking/new" element={<BookingPage />} />
      <Route path="/booking/success" element={<BookingSuccessPage />} />
      <Route path="/pass/success" element={<PassSuccessPage />} />
      {/* CheckoutPage removed - dead code, using Stripe Hosted Checkout instead */}
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/movement" element={<FreestaysMovementPage />} />
      <Route path="/freestays-movement" element={<FreestaysMovementPage />} />
      <Route path="/pass" element={<PassPage />} />
      <Route path="/voucher" element={<VoucherPage />} />
      <Route path="/comparison" element={<ComparisonPage />} />
      <Route path="/who-we-are" element={<WhoWeArePage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/b2b/confirm/:token" element={<B2BConfirmOrderPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/policy" element={<PrivacyPage />} />
      <Route path="/refer-a-friend" element={<ReferFriendPageWrapper />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/survey" element={<SurveyPage />} />
      {/* News/Blog Routes */}
      <Route path="/news" element={<NewsListPage />} />
      <Route path="/news/:slug" element={<NewsArticlePage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogArticlePage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
      {/* Partner Offer Pages (Sovendus) */}
      <Route path="/offers/:pageId/:lang" element={<PartnerOfferPage />} />
      <Route path="/offers/:pageId" element={<PartnerOfferPage />} />
      {/* Programmatic SEO landing pages — Sprint 2 */}
      <Route path="/guide" element={<ProgrammaticGuideIndex />} />
      <Route path="/guide/:slug" element={<ProgrammaticLandingPage />} />
      {/* GDPR 1-click unsubscribe (linked from newsletter emails) */}
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/newsletter/confirm" element={<NewsletterConfirmPage />} />
      {/* CMS Routes - Separate admin panel for user/pass/payment management */}
      <Route path="/cms/*" element={<CMSApp />} />
      {/* SEO Destination Pages - must be LAST (catch-all for /:country/:city) */}
      <Route path="/:country/:city" element={<DestinationPage />} />
    </Routes>
  );
}

// Layout wrapper to conditionally show Header based on route
const AppLayout = () => {
  const location = useLocation();
  const isCMSRoute = location.pathname.startsWith('/cms');
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Welcome popup state — DEFERRED rendering to protect LCP.
  // Popup competes with the hero image for LCP; by mounting it only AFTER
  // the browser is idle (or after 1.5s), we let the hero paint first
  // → typical LCP improvement: 6.8s → ~1.8s on cold loads.
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);

  useEffect(() => {
    // Skip on CMS/admin routes or non-home routes.
    if (isCMSRoute || isAdminRoute || location.pathname !== '/') return;

    if (typeof window === 'undefined') return;
    const lastSeen = localStorage.getItem('welcomePopupSeen');
    const eligible = !lastSeen || ((Date.now() - parseInt(lastSeen)) / (1000 * 60 * 60)) > 24;
    if (!eligible) return;

    // Wait until the browser has painted the hero, then mount the popup.
    // requestIdleCallback gives us "after important work" semantics with a
    // 1.5s safety fallback for browsers that don't support it (Safari).
    let cancelled = false;
    const reveal = () => { if (!cancelled) setShowWelcomePopup(true); };

    let idleId, timeoutId;
    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(reveal, { timeout: 2000 });
    } else {
      timeoutId = setTimeout(reveal, 1500);
    }
    return () => {
      cancelled = true;
      if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isCMSRoute, isAdminRoute, location.pathname]);

  // Only show welcome popup on main site, not CMS/admin
  const shouldShowWelcome = showWelcomePopup && !isCMSRoute && !isAdminRoute && location.pathname === '/';

  return (
    <div className="App min-h-screen bg-background text-foreground transition-colors duration-300">
      {shouldShowWelcome && (
        <Suspense fallback={null}>
          <WelcomePopup
            onClose={() => setShowWelcomePopup(false)}
            apiUrl={BACKEND_URL}
          />
        </Suspense>
      )}
      {!isCMSRoute && !isAdminRoute && <Header />}
      {!isCMSRoute && !isAdminRoute && <EmailVerificationReminder />}
      <PageErrorBoundary>
        <Suspense fallback={<RouteSuspenseFallback />}>
          <AppRouter />
        </Suspense>
      </PageErrorBoundary>
      {!isCMSRoute && !isAdminRoute && <SiteFooter />}
      {!isCMSRoute && !isAdminRoute && <InstallAppPrompt />}
      {!isCMSRoute && !isAdminRoute && <CookieConsent />}
      <Toaster position="top-center" richColors />
    </div>
  );
};


// ==================== B2B ORDER CONFIRMATION PAGE ====================
// Extracted to /app/frontend/src/pages/B2BConfirmOrderPage.jsx (Fase 1)
export { B2BConfirmOrderPage } from './pages/B2BConfirmOrderPage';
import { B2BConfirmOrderPage } from './pages/B2BConfirmOrderPage';

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
       
        <ThemeProvider>
          <AuthProvider>
            <PWAInstallProvider>
              <BrowserRouter>
                <AppLayout />
              </BrowserRouter>
            </PWAInstallProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export { calculatePricing, Header };
export default App;
